import { useState, useEffect, useMemo, useRef, useCallback, type MutableRefObject } from "react";
import { useTranslation } from "react-i18next";
import { fmtDate, intlLocale } from "@/lib/locale";
import { ErrorBoundary } from "@/components/error-boundary";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AccessRestricted } from "@/components/access-restricted";
import { apiRequest } from "@/lib/queryClient";
import { uploadFileDirect } from "@/lib/directUpload";
import { pollFloorplanJob } from "@/lib/floorplanUpload";
import { getTimelineMonths as getTimelineMonthsLib, filterEffectiveStmts } from "@/lib/effectiveStmts";
import { type Lender, type LenderWithTiers, type FloorplanEntry } from "@shared/schema";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tooltip as UITooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LabelList,
  LineChart, Line, ReferenceLine, Cell,
  ScatterChart, Scatter, ZAxis,
} from "recharts";
import {
  Building2, Plus, Minus, Trash2, AlertTriangle, AlertCircle, Calculator,
  Upload, RefreshCw, FileText, ChevronDown, ChevronRight, Edit, CheckCircle2,
  History as HistoryIcon, Info, Tag, RotateCcw, ArrowLeftRight, Minimize2, X,
} from "lucide-react";
import {
  computeThresholds, computeTurnSensitivityModel,
  dbLenderToTerms, lenderHasTerms, defaultTerms,
  lenderFlexConfig, computeCostBreakdownWithFlex,
  dbTiersToTierConfigs, computeCostBreakdownWithTiers,
  type LenderTerms, type ScenarioInputs, type CostBreakdown, type TierConfig,
} from "@/lib/manualFloorplanModel";
import { computeFloorplanStats, filterStatementsToLenders } from "@/lib/floorplanStats";
import { type TermsFormState, emptyTermsForm, lenderToTermsForm } from "@/lib/lenderTermsForm";
import { computeRealAprForLender, computeTrueAprFromRows, dedupeReceivables, manualFeePerUnitFromLender } from "@/lib/realApr";
import { parseMoneyNumber } from "@/lib/moneyInput";
import { FormattedNumberInput } from "@/components/formatted-number-input";
import { ReceivableChat } from "@/components/ReceivableChat";
import { StatementReader } from "@/components/statement-reader";
import { useScannerContext } from "@/contexts/use-scanner-context";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt$ = (n: number | null | undefined, d = 0) =>
  n == null || isNaN(n) ? "—" : `$${n.toLocaleString("en-US", { maximumFractionDigits: d })}`;
const fmtPct = (n: number | null | undefined, d = 2) =>
  n == null || isNaN(n) ? "—" : `${Number(n).toFixed(d)}%`;
const n = (v: any, def = 0) => v != null ? parseFloat(v) : def;

// ─── Lender color palette ─────────────────────────────────────────────────────
// Each lender gets a stable color based on its position in the lenders array.
// Colors only appear when actual statement data has been uploaded for that lender.
const LENDER_PALETTE = [
  { border: "border-blue-500",   leftAccent: "border-l-blue-500",   ring: "ring-blue-500/30",   text: "text-blue-600 dark:text-blue-400" },
  { border: "border-orange-500", leftAccent: "border-l-orange-500", ring: "ring-orange-500/30", text: "text-orange-600 dark:text-orange-400" },
  { border: "border-violet-500", leftAccent: "border-l-violet-500", ring: "ring-violet-500/30", text: "text-violet-600 dark:text-violet-400" },
  { border: "border-rose-500",   leftAccent: "border-l-rose-500",   ring: "ring-rose-500/30",   text: "text-rose-600 dark:text-rose-400" },
  { border: "border-amber-500",  leftAccent: "border-l-amber-500",  ring: "ring-amber-500/30",  text: "text-amber-600 dark:text-amber-400" },
  { border: "border-cyan-500",   leftAccent: "border-l-cyan-500",   ring: "ring-cyan-500/30",   text: "text-cyan-600 dark:text-cyan-400" },
  { border: "border-pink-500",   leftAccent: "border-l-pink-500",   ring: "ring-pink-500/30",   text: "text-pink-600 dark:text-pink-400" },
  { border: "border-indigo-500", leftAccent: "border-l-indigo-500", ring: "ring-indigo-500/30", text: "text-indigo-600 dark:text-indigo-400" },
];
function getLenderColor(lenderId: string, allLenders: { id: string }[] = []) {
  const idx = Array.isArray(allLenders) ? allLenders.findIndex(l => l.id === lenderId) : -1;
  return LENDER_PALETTE[(idx >= 0 ? idx : 0) % LENDER_PALETTE.length];
}

// ─── NumInput helper ──────────────────────────────────────────────────────────

function NumInput({
  label, value, onChange, prefix, suffix, placeholder = "0", testId, hint,
}: {
  label: string; value: string | number; onChange: (v: string) => void;
  prefix?: string; suffix?: string; placeholder?: string; testId?: string; hint?: string;
}) {
  const [focused, setFocused] = useState(false);
  const raw = String(value ?? "").replace(/,/g, "");
  const num = parseFloat(raw);
  const display = !focused && Number.isFinite(num) && raw !== ""
    ? num.toLocaleString("en-US", { maximumFractionDigits: 6 })
    : String(value ?? "");
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      {hint && <p className="text-xs text-muted-foreground -mt-1">{hint}</p>}
      <div className="relative">
        {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">{prefix}</span>}
        <Input
          type="text" placeholder={placeholder}
          value={display}
          onChange={(e) => onChange(e.target.value.replace(/,/g, ""))}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          className={`${prefix ? "pl-6" : ""} ${suffix ? "pr-8" : ""} text-sm`}
          data-testid={testId}
        />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">{suffix}</span>}
      </div>
    </div>
  );
}

// ─── Terms Form ───────────────────────────────────────────────────────────────

interface TierFormState {
  id?: string;
  threshold: string;
  period1Days: string; period2Days: string; period3Days: string; period4Days: string;
  aprRate: string;
  feePeriod1: string; feePeriod2: string; feePeriod3: string; feePeriod4: string;
  curtailment1Type: "percent" | "dollar"; curtailment1Value: string;
  curtailment2Type: "percent" | "dollar"; curtailment2Value: string;
}

function emptyTierForm(): TierFormState {
  return {
    threshold: "",
    period1Days: "60", period2Days: "30", period3Days: "30", period4Days: "",
    aprRate: "",
    feePeriod1: "", feePeriod2: "", feePeriod3: "", feePeriod4: "",
    curtailment1Type: "percent", curtailment1Value: "",
    curtailment2Type: "percent", curtailment2Value: "",
  };
}

function dbTierToForm(t: any): TierFormState {
  return {
    id: t.id,
    threshold: t.threshold != null ? String(parseFloat(t.threshold)) : "",
    period1Days: t.period1Days != null ? String(t.period1Days) : "30",
    period2Days: t.period2Days != null ? String(t.period2Days) : "30",
    period3Days: t.period3Days != null ? String(t.period3Days) : "30",
    period4Days: t.period4Days != null ? String(t.period4Days) : "",
    aprRate: t.aprRate != null ? String(t.aprRate) : "",
    feePeriod1: t.feePeriod1 != null ? String(t.feePeriod1) : "",
    feePeriod2: t.feePeriod2 != null ? String(t.feePeriod2) : "",
    feePeriod3: t.feePeriod3 != null ? String(t.feePeriod3) : "",
    feePeriod4: t.feePeriod4 != null ? String(t.feePeriod4) : "",
    curtailment1Type: (t.curtailment1Type as any) ?? "percent",
    curtailment1Value: t.curtailment1Value != null ? String(t.curtailment1Value) : "",
    curtailment2Type: (t.curtailment2Type as any) ?? "percent",
    curtailment2Value: t.curtailment2Value != null ? String(t.curtailment2Value) : "",
  };
}

function formToTerms(form: TermsFormState): LenderTerms {
  return {
    period1Days: n(form.period1Days, 30),
    period2Days: n(form.period2Days, 30),
    period3Days: n(form.period3Days, 30),
    period4Days: n(form.period4Days, 0),
    aprRate: n(form.aprRate, 0),
    feePeriod1: n(form.feePeriod1, 0),
    feePeriod2: n(form.feePeriod2, 0),
    feePeriod3: n(form.feePeriod3, 0),
    feePeriod4: n(form.feePeriod4, 0),
    curtailment1Type: form.curtailment1Type,
    curtailment1Value: n(form.curtailment1Value, 0),
    curtailment2Type: form.curtailment2Type,
    curtailment2Value: n(form.curtailment2Value, 0),
  };
}

function TierCard({ tier, index, onChange, onDelete }: {
  tier: TierFormState;
  index: number;
  onChange: (t: TierFormState) => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation(["floorplan", "common"]);
  const upd = <K extends keyof TierFormState>(k: K, v: TierFormState[K]) => onChange({ ...tier, [k]: v });

  useEffect(() => {
    if (index === 0 && tier.threshold === "") {
      onChange({ ...tier, threshold: "0" });
    }
  }, []);

  const T2 = n(tier.period1Days, 30);
  const T3 = T2 + n(tier.period2Days, 30);
  const T4 = T3 + n(tier.period3Days, 30);
  const [showP4, setShowP4] = useState(!!(tier.period4Days && tier.period4Days !== ""));

  return (
    <div className="rounded-lg border border-dashed border-primary/50 p-2.5 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-primary">{t("floorplan:termsForm.tierLabel", { num: index + 1 })}</span>
        <button type="button" onClick={onDelete} className="text-muted-foreground hover:text-destructive transition-colors" data-testid={`button-delete-tier-${index}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Threshold */}
      <div className="flex items-center gap-2 flex-wrap">
        <Label className="text-xs shrink-0">{t("floorplan:termsForm.flooredAt")}</Label>
        <div className="relative w-32">
          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs pointer-events-none">$</span>
          <Input
            type="text" inputMode="numeric" placeholder={t("floorplan:termsForm.thresholdPlaceholder")}
            value={tier.threshold ? Number(tier.threshold).toLocaleString("en-US") : ""}
            onChange={(e) => upd("threshold", e.target.value.replace(/[^0-9]/g, ""))}
            className="h-7 text-xs pl-5"
            data-testid={`input-tier-threshold-${index}`}
          />
        </div>
        <Label className="text-xs shrink-0">{t("floorplan:termsForm.andOver")}</Label>
      </div>

      {/* Period table */}
      <div className="grid grid-cols-3 gap-2 text-xs text-muted-foreground font-medium px-0.5 pt-0.5">
        <span>{t("floorplan:termsForm.periodCol")}</span><span>{t("floorplan:termsForm.daysCol")}</span><span>{t("floorplan:termsForm.feePerUnitCol")}</span>
      </div>
      {([
        { num: 1, dk: "period1Days" as const, fk: "feePeriod1" as const },
        { num: 2, dk: "period2Days" as const, fk: "feePeriod2" as const },
        { num: 3, dk: "period3Days" as const, fk: "feePeriod3" as const },
      ] as const).map(({ num, dk, fk }) => (
        <div key={dk} className="grid grid-cols-3 gap-2 items-center">
          <span className="text-xs text-muted-foreground">{t("floorplan:termsForm.periodN", { num })}</span>
          <div className="relative">
            <Input type="text" inputMode="numeric" placeholder="30"
              value={tier[dk]} onChange={(e) => upd(dk, e.target.value.replace(/[^0-9]/g, ""))}
              className="h-7 text-xs pr-6" data-testid={`input-tier-${dk}`} />
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs pointer-events-none">d</span>
          </div>
          <div className="relative">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs pointer-events-none">$</span>
            <Input type="text" inputMode="numeric" placeholder="—"
              value={tier[fk]} onChange={(e) => upd(fk, e.target.value.replace(/[^0-9.]/g, ""))}
              className="h-7 text-xs pl-5" data-testid={`input-tier-${fk}`} />
          </div>
        </div>
      ))}

      {/* Period 4 (optional) */}
      {showP4 ? (
        <div className="grid gap-2 items-center" style={{ gridTemplateColumns: "1fr 1fr 1fr auto" }}>
          <span className="text-xs text-muted-foreground">{t("floorplan:termsForm.periodN", { num: 4 })}</span>
          <div className="relative">
            <Input type="text" inputMode="numeric" placeholder="30"
              value={tier.period4Days} onChange={(e) => upd("period4Days", e.target.value.replace(/[^0-9]/g, ""))}
              className="h-7 text-xs pr-6" data-testid="input-tier-period4Days" />
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs pointer-events-none">d</span>
          </div>
          <div className="relative">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs pointer-events-none">$</span>
            <Input type="text" inputMode="numeric" placeholder="—"
              value={tier.feePeriod4} onChange={(e) => upd("feePeriod4", e.target.value.replace(/[^0-9.]/g, ""))}
              className="h-7 text-xs pl-5" data-testid="input-tier-feePeriod4" />
          </div>
          <button type="button" onClick={() => { setShowP4(false); upd("period4Days", ""); upd("feePeriod4", ""); }}
            className="text-muted-foreground hover:text-destructive transition-colors" data-testid="button-remove-period4">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <Button type="button" variant="ghost" size="sm" className="h-5 text-xs text-muted-foreground px-1"
          onClick={() => setShowP4(true)} data-testid="button-add-period4">
          <Plus className="w-3 h-3 mr-0.5" />{t("floorplan:termsForm.periodN", { num: 4 })}
        </Button>
      )}

      {/* APR */}
      <div className="flex items-center gap-2 pt-0.5">
        <Label className="text-xs shrink-0">{t("floorplan:termsForm.interestRateShort")}</Label>
        <div className="relative w-24">
          <Input type="text" inputMode="decimal" placeholder="0"
            value={tier.aprRate} onChange={(e) => upd("aprRate", e.target.value.replace(/[^0-9.]/g, ""))}
            className="h-7 text-xs pr-6" data-testid="input-tier-aprRate" />
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs pointer-events-none">%</span>
        </div>
      </div>

      {/* Curtailments */}
      <div className="space-y-1.5 pt-0.5">
        <p className="text-xs text-muted-foreground font-medium">{t("floorplan:termsForm.curtailments")}</p>
        {([
          { num: 1, label: t("floorplan:termsForm.day", { day: T2 }), tk: "curtailment1Type" as const, vk: "curtailment1Value" as const },
          { num: 2, label: t("floorplan:termsForm.day", { day: T3 }), tk: "curtailment2Type" as const, vk: "curtailment2Value" as const },
        ] as const).map(({ num, label, tk, vk }) => (
          <div key={num} className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-muted-foreground w-14 shrink-0">{label}</span>
            <div className="flex items-center gap-1">
              <span className="text-xs text-muted-foreground">%</span>
              <Switch checked={tier[tk] === "dollar"} onCheckedChange={(v) => upd(tk, v ? "dollar" : "percent")} data-testid={`switch-curtailment-${num}-type`} />
              <span className="text-xs text-muted-foreground">$</span>
            </div>
            <div className="relative w-24">
              {tier[tk] === "dollar" && <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">$</span>}
              <Input type="text" placeholder="0"
                value={tier[vk]} onChange={(e) => upd(vk, e.target.value.replace(/,/g, ""))}
                className={`h-7 text-xs ${tier[tk] === "dollar" ? "pl-5" : ""}`}
                data-testid={`input-curtailment-${num}-value`} />
              {tier[tk] === "percent" && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">%</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

interface AiLenderLookupResult {
  isFloorplanLender: boolean;
  lenderName: string;
  typicalAprLow: number | null;
  typicalAprHigh: number | null;
  knownFeeStructure: string | null;
  notes: string | null;
  rejectionReason: string | null;
}

function TermsForm({ form, onChange, avgTurnDays, avgVehicleValue, onSave, saving, tiers = [], onTiersChange }: {
  form: TermsFormState;
  onChange: (k: keyof TermsFormState, v: string | boolean | null) => void;
  avgTurnDays?: number | null;
  avgVehicleValue?: number | null;
  onSave?: () => void;
  saving?: boolean;
  tiers?: TierFormState[];
  onTiersChange?: (tiers: TierFormState[]) => void;
}) {
  const { t } = useTranslation(["floorplan", "common"]);
  const terms = formToTerms(form);
  const { T2, T3 } = computeThresholds(terms);
  const [showPeriod4, setShowPeriod4] = useState(
    !!(form.period4Days && form.period4Days !== "") || !!(form.feePeriod4 && form.feePeriod4 !== "")
  );
  // Auto-expand Period 4 when prefilled values arrive after mount (e.g. statement
  // import). One-way only: never collapses, so it won't fight the explicit remove
  // button (which clears period4Days/feePeriod4 and then this stays collapsed).
  useEffect(() => {
    if ((form.period4Days && form.period4Days !== "") || (form.feePeriod4 && form.feePeriod4 !== "")) {
      setShowPeriod4(true);
    }
  }, [form.period4Days, form.feePeriod4]);
  const T4 = T3 + n(form.period3Days, 30);

  // AI lender confirm state
  const [aiConfirmLoading, setAiConfirmLoading] = useState(false);
  const [aiConfirmResult, setAiConfirmResult] = useState<AiLenderLookupResult | null>(null);
  const [aiConfirmError, setAiConfirmError] = useState<string | null>(null);

  const handleAiConfirm = async () => {
    const name = form.name.trim();
    if (!name) return;
    setAiConfirmLoading(true);
    setAiConfirmResult(null);
    setAiConfirmError(null);
    try {
      const res = await fetch("/api/ai/lender-lookup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lenderName: name }),
        credentials: "include",
      });
      if (!res.ok) throw new Error("Request failed");
      const result: AiLenderLookupResult = await res.json();
      setAiConfirmResult(result);
      if (result.isFloorplanLender) {
        // Pre-fill canonical lender name if the AI resolved it to a different/corrected spelling
        if (result.lenderName && result.lenderName.trim() && result.lenderName.toLowerCase() !== form.name.toLowerCase()) {
          onChange("name", result.lenderName);
        }
        // Pre-fill APR midpoint if confirmed and the field is currently empty
        if (result.typicalAprLow != null && result.typicalAprHigh != null && !form.aprRate) {
          const mid = ((result.typicalAprLow + result.typicalAprHigh) / 2).toFixed(1);
          onChange("aprRate", mid);
        }
        // Pre-fill AI notes (known fees + additional notes) into form state
        const noteParts: string[] = [];
        if (result.knownFeeStructure) noteParts.push(result.knownFeeStructure);
        if (result.notes) noteParts.push(result.notes);
        if (noteParts.length > 0) {
          onChange("aiNotes", noteParts.join(" | "));
        }
      }
    } catch {
      setAiConfirmError(t("floorplan:termsForm.aiLookupError"));
    } finally {
      setAiConfirmLoading(false);
    }
  };

  // Avg cost per month (requires turn days + vehicle value)
  const avgCostPerMonth = (() => {
    if (!avgTurnDays || avgTurnDays <= 0) return null;
    const vv = avgVehicleValue ?? 0;
    const p1 = n(form.period1Days, 30);
    const p2 = n(form.period2Days, 30);
    const t2 = p1;
    const t3 = p1 + p2;
    const fees =
      n(form.feePeriod1, 0) +
      (avgTurnDays >= t2 ? n(form.feePeriod2, 0) : 0) +
      (avgTurnDays >= t3 ? n(form.feePeriod3, 0) : 0) +
      (showPeriod4 && avgTurnDays >= t3 + n(form.period3Days, 30) ? n(form.feePeriod4, 0) : 0);
    const interest = vv * (n(form.aprRate, 0) / 100) * (avgTurnDays / 365);
    const total = fees + interest;
    return total / (avgTurnDays / 30);
  })();

  return (
    <div className="space-y-3">
      {/* Lender name + AI confirm */}
      <div className="space-y-1.5">
        <Label className="text-xs">{t("floorplan:termsForm.lenderName")}</Label>
        <div className="flex gap-2">
          <Input
            placeholder={t("floorplan:termsForm.lenderNamePlaceholder")}
            value={form.name}
            onChange={(e) => { onChange("name", e.target.value); setAiConfirmResult(null); setAiConfirmError(null); }}
            data-testid="input-lender-name"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAiConfirm}
            disabled={aiConfirmLoading || !form.name.trim()}
            data-testid="button-ai-confirm-lender"
            className="shrink-0 gap-1.5 text-xs"
          >
            {aiConfirmLoading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Info className="w-3.5 h-3.5" />}
            {aiConfirmLoading ? t("floorplan:termsForm.checking") : t("floorplan:termsForm.aiConfirm")}
          </Button>
        </div>
        {aiConfirmError && (
          <p className="text-xs text-destructive">{aiConfirmError}</p>
        )}
        {aiConfirmResult && (
          <div data-testid="ai-confirm-result" className={`p-2.5 rounded-lg border text-xs space-y-1 ${aiConfirmResult.isFloorplanLender ? "bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800" : "bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800"}`}>
            <p className={`font-semibold ${aiConfirmResult.isFloorplanLender ? "text-green-700 dark:text-green-300" : "text-amber-700 dark:text-amber-300"}`}>
              {aiConfirmResult.isFloorplanLender ? t("floorplan:termsForm.confirmedLender") : t("floorplan:termsForm.notLender")}
            </p>
            {aiConfirmResult.isFloorplanLender && aiConfirmResult.typicalAprLow != null && aiConfirmResult.typicalAprHigh != null && (
              <p className="text-muted-foreground">{t("floorplan:termsForm.typicalApr", { low: aiConfirmResult.typicalAprLow, high: aiConfirmResult.typicalAprHigh })} <span className="opacity-60">{t("floorplan:termsForm.prefilledNote")}</span></p>
            )}
            {aiConfirmResult.isFloorplanLender && aiConfirmResult.knownFeeStructure && (
              <p className="text-muted-foreground">{t("floorplan:termsForm.knownFees", { fees: aiConfirmResult.knownFeeStructure })}</p>
            )}
            {aiConfirmResult.isFloorplanLender && aiConfirmResult.notes && (
              <p className="text-muted-foreground">{aiConfirmResult.notes}</p>
            )}
            {!aiConfirmResult.isFloorplanLender && (
              <p className="text-muted-foreground">
                {aiConfirmResult.rejectionReason ?? t("floorplan:termsForm.rejectionDefault")}
              </p>
            )}
          </div>
        )}
        {/* Show persisted aiNotes when no live result is displayed */}
        {!aiConfirmResult && form.aiNotes && (
          <div className="p-2 rounded-lg bg-muted/40 border text-xs text-muted-foreground flex items-start gap-2">
            <div className="flex-1 min-w-0">
              <span className="font-medium text-foreground">{t("floorplan:termsForm.aiNotesLabel")}</span>{form.aiNotes}
            </div>
            <button
              type="button"
              onClick={handleAiConfirm}
              disabled={aiConfirmLoading || !form.name.trim()}
              title={t("floorplan:termsForm.rerunAiConfirm")}
              aria-label={t("floorplan:termsForm.rerunAiConfirm")}
              data-testid="button-refresh-ai-notes"
              className="shrink-0 p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {aiConfirmLoading
                ? <RefreshCw className="w-3 h-3 animate-spin" />
                : <RefreshCw className="w-3 h-3" />}
            </button>
          </div>
        )}
      </div>

      {/* Period structure + fees (combined) */}
      <div className="rounded-lg border bg-muted/20 p-3 space-y-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <p className="text-xs font-semibold">{t("floorplan:termsForm.periodDaysFees")}</p>
          <div className="flex items-center gap-1.5">
            <Button
              type="button" size="sm"
              variant={form.pricingType !== "tiered" ? "default" : "outline"}
              className={form.pricingType !== "tiered" ? "h-5 text-xs px-2 py-0" : "h-5 text-xs px-2 py-0 bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100 dark:bg-blue-950 dark:border-blue-700 dark:text-blue-300 dark:hover:bg-blue-900"}
              onClick={() => {
                onChange("pricingType", "fixed");
                onTiersChange?.([]);
              }}
              data-testid="button-fixed-term-plan-toggle"
            >
              {t("floorplan:termsForm.fixedTermPlan")}
            </Button>
            <Button
              type="button" size="sm"
              variant={form.pricingType === "tiered" ? "default" : "outline"}
              className={form.pricingType === "tiered" ? "h-5 text-xs px-2 py-0" : "h-5 text-xs px-2 py-0 bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100 dark:bg-blue-950 dark:border-blue-700 dark:text-blue-300 dark:hover:bg-blue-900"}
              onClick={() => {
                if (form.pricingType === "tiered") {
                  onChange("pricingType", "fixed");
                  onTiersChange?.([]);
                } else {
                  onChange("pricingType", "tiered");
                  if (tiers.length === 0) onTiersChange?.([emptyTierForm()]);
                }
              }}
              data-testid="button-tiered-term-plan-toggle"
            >
              {t("floorplan:termsForm.tieredTermPlan")}
            </Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          {form.pricingType === "tiered"
            ? t("floorplan:termsForm.tieredHintShort")
            : t("floorplan:termsForm.fixedHint")}
        </p>

        {/* Period rows — shown for Fixed Term Plan (and any non-tiered state) */}
        {form.pricingType !== "tiered" && (
          <>
            {/* Header row */}
            <div className="grid grid-cols-3 gap-2 text-xs text-muted-foreground font-medium px-0.5">
              <span>{t("floorplan:termsForm.periodCol")}</span><span>{t("floorplan:termsForm.daysCol")}</span><span>{t("floorplan:termsForm.feePerUnitCol")}</span>
            </div>

            {/* Period rows */}
            {[
              { period: "1", daysKey: "period1Days" as const, feeKey: "feePeriod1" as const, num: 1, testD: "input-period1", testF: "input-fee1" },
              { period: "2", daysKey: "period2Days" as const, feeKey: "feePeriod2" as const, num: 2, testD: "input-period2", testF: "input-fee2" },
              { period: "3", daysKey: "period3Days" as const, feeKey: "feePeriod3" as const, num: 3, testD: "input-period3", testF: "input-fee3" },
            ].map(({ period, daysKey, feeKey, num, testD, testF }) => (
              <div key={period} className="grid grid-cols-3 gap-2 items-center">
                <span className="text-xs text-muted-foreground">{t("floorplan:termsForm.periodN", { num })}</span>
                <div className="relative">
                  <Input
                    type="text" inputMode="numeric" placeholder="30"
                    value={form[daysKey]}
                    onChange={(e) => onChange(daysKey, e.target.value.replace(/[^0-9]/g, ""))}
                    className="h-7 text-xs pr-6"
                    data-testid={testD}
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs pointer-events-none">d</span>
                </div>
                <div className="relative">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs pointer-events-none">$</span>
                  <Input
                    type="text" inputMode="numeric" placeholder="—"
                    value={form[feeKey]}
                    onChange={(e) => onChange(feeKey, e.target.value.replace(/[^0-9.]/g, ""))}
                    className="h-7 text-xs pl-5"
                    data-testid={testF}
                  />
                </div>
              </div>
            ))}

            {/* Period 4 (optional) */}
            {showPeriod4 && (
              <div className="grid gap-2 items-center" style={{ gridTemplateColumns: "1fr 1fr 1fr auto" }}>
                <span className="text-xs text-muted-foreground">{t("floorplan:termsForm.periodN", { num: 4 })}</span>
                <div className="relative">
                  <Input
                    type="text" inputMode="numeric" placeholder="30"
                    value={form.period4Days}
                    onChange={(e) => onChange("period4Days", e.target.value.replace(/[^0-9]/g, ""))}
                    className="h-7 text-xs pr-6"
                    data-testid="input-period4"
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs pointer-events-none">d</span>
                </div>
                <div className="relative">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs pointer-events-none">$</span>
                  <Input
                    type="text" inputMode="numeric" placeholder="—"
                    value={form.feePeriod4}
                    onChange={(e) => onChange("feePeriod4", e.target.value.replace(/[^0-9.]/g, ""))}
                    className="h-7 text-xs pl-5"
                    data-testid="input-fee4"
                  />
                </div>
                <button
                  type="button"
                  className="text-muted-foreground hover:text-destructive transition-colors"
                  onClick={() => { setShowPeriod4(false); onChange("period4Days", ""); onChange("feePeriod4", ""); }}
                  data-testid="button-delete-period4"
                  title={t("floorplan:termsForm.removePeriod4")}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            )}

            {/* Add Period button */}
            {!showPeriod4 && (
              <Button
                type="button" variant="ghost" size="sm"
                className="h-6 text-xs text-muted-foreground px-2 mt-0.5"
                onClick={() => setShowPeriod4(true)}
                data-testid="button-add-period"
              >
                <Plus className="w-3 h-3 mr-1" />{t("floorplan:termsForm.addPeriod")}
              </Button>
            )}
          </>
        )}

        {/* ── Tiered Pricing cards (above interest rate) ── */}
        {form.pricingType === "tiered" && (
          <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-3 mt-1">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Badge className="text-xs">{t("floorplan:termsForm.tiersBadge")}</Badge>
                <p className="text-xs font-semibold">{t("floorplan:termsForm.tieredTermPlan")}</p>
              </div>
              <button
                type="button"
                className="text-muted-foreground hover:text-destructive transition-colors"
                onClick={() => { onChange("pricingType", "fixed"); onTiersChange?.([]); }}
                data-testid="button-delete-tiers"
                title={t("floorplan:termsForm.removeTiers")}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              {t("floorplan:termsForm.tiersHint")}
            </p>

            {tiers.map((tier, idx) => (
              <TierCard
                key={idx}
                tier={tier}
                index={idx}
                onChange={(updated) => {
                  const next = [...tiers];
                  next[idx] = updated;
                  onTiersChange?.(next);
                }}
                onDelete={() => {
                  const next = tiers.filter((_, j) => j !== idx);
                  onTiersChange?.(next);
                  if (next.length === 0) onChange("pricingType", "fixed");
                }}
              />
            ))}

            <div className="flex items-center justify-between pt-1">
              <Button
                type="button" variant="ghost" size="sm"
                className="h-6 text-xs text-muted-foreground px-2"
                onClick={() => onTiersChange?.([...tiers, emptyTierForm()])}
                data-testid="button-add-tier"
              >
                <Plus className="w-3 h-3 mr-1" />{t("floorplan:termsForm.addTier")}
              </Button>
              {onSave && (
                <Button
                  type="button" size="sm"
                  className="h-7 text-xs px-3"
                  onClick={onSave}
                  disabled={saving}
                  data-testid="button-save-tiers"
                >
                  {saving ? t("floorplan:termsForm.saving") : t("floorplan:termsForm.saveTiers")}
                </Button>
              )}
            </div>
          </div>
        )}

        <div className="border-t border-border/50 pt-2 mt-1 space-y-3">
          {/* APR */}
          <div className="space-y-1">
            <Label className="text-xs">{t("floorplan:termsForm.annualInterestRate")}</Label>
            <p className="text-xs text-muted-foreground">{t("floorplan:termsForm.annualInterestRateHint")}</p>
            <div className="relative w-28">
              <Input
                type="text" inputMode="decimal" placeholder="0"
                value={form.aprRate}
                onChange={(e) => onChange("aprRate", e.target.value.replace(/[^0-9.]/g, ""))}
                className="h-8 text-xs pr-7"
                data-testid="input-apr"
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs pointer-events-none">%</span>
            </div>
          </div>

          {/* Curtailments */}
          <div className="space-y-2">
            <p className="text-xs font-semibold">{t("floorplan:termsForm.curtailments")}</p>
            <p className="text-xs text-muted-foreground">{t("floorplan:termsForm.curtailmentsHint")}</p>
            <div className="space-y-2">
              {[
                { num: 1, label: t("floorplan:termsForm.day", { day: T2 }), typeKey: "curtailment1Type" as const, valKey: "curtailment1Value" as const },
                { num: 2, label: t("floorplan:termsForm.day", { day: T3 }), typeKey: "curtailment2Type" as const, valKey: "curtailment2Value" as const },
              ].map(({ num, label, typeKey, valKey }) => (
                <div key={num} className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-muted-foreground w-14 shrink-0">{label}</span>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-muted-foreground">%</span>
                    <Switch
                      checked={form[typeKey] === "dollar"}
                      onCheckedChange={(v) => onChange(typeKey, v ? "dollar" : "percent")}
                      data-testid={`switch-curtailment${num}-type`}
                    />
                    <span className="text-xs text-muted-foreground">$</span>
                  </div>
                  <div className="relative w-28">
                    {form[typeKey] === "dollar" && <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">$</span>}
                    <Input
                      type="text" placeholder="0"
                      value={form[valKey]}
                      onChange={(e) => onChange(valKey, e.target.value.replace(/,/g, ""))}
                      className={`h-7 text-xs ${form[typeKey] === "dollar" ? "pl-5" : ""}`}
                      data-testid={`input-curtailment${num}`}
                    />
                    {form[typeKey] === "percent" && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">%</span>}
                  </div>
                </div>
              ))}
            </div>

            {/* Westlake statement curtailment schedule (read-only, when extracted) */}
            {(form.curtailmentPct1 || form.curtailmentPct2 || form.curtailmentPct3) && (
              <div className="rounded-md bg-muted/40 px-2.5 py-2 mt-1" data-testid="display-curtailment-pcts">
                <p className="text-xs text-muted-foreground">{t("floorplan:termsForm.statementCurtailmentSchedule")}</p>
                <p className="text-sm font-semibold" data-testid="text-curtailment-pcts">
                  {[form.curtailmentPct1, form.curtailmentPct2, form.curtailmentPct3]
                    .map((p) => (p ? `${p}%` : "—"))
                    .join(" / ")}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Avg units floored per month */}
      <div className="space-y-1">
        <Label className="text-xs">{t("floorplan:termsForm.avgUnitsPerMonth")}</Label>
        <p className="text-xs text-muted-foreground">{t("floorplan:termsForm.avgUnitsPerMonthHint")}</p>
        <Input
          type="text" inputMode="numeric" placeholder="10"
          value={form.avgUnitsPerMonth}
          onChange={(e) => onChange("avgUnitsPerMonth", e.target.value.replace(/[^0-9.]/g, ""))}
          className="h-8 text-xs w-28"
          data-testid="input-units-month"
        />
      </div>

      {/* Avg cost per month (computed) */}
      {avgTurnDays != null && avgTurnDays > 0 && (
        <div className="rounded-lg border border-primary/20 bg-primary/5 p-3">
          <p className="text-xs font-semibold mb-1">{t("floorplan:termsForm.avgCostPerMonth")}</p>
          <p className="text-xs text-muted-foreground mb-2">
            {t("floorplan:termsForm.basedOnTurn", { days: avgTurnDays.toFixed(0) })}
            {avgVehicleValue ? t("floorplan:termsForm.avgVehicleValueSuffix", { value: fmt$(avgVehicleValue) }) : ""}
          </p>
          <div className="flex items-baseline gap-1.5">
            <span className="text-2xl font-black text-primary">
              {avgCostPerMonth != null ? fmt$(avgCostPerMonth) : "—"}
            </span>
            <span className="text-xs text-muted-foreground">{t("floorplan:termsForm.perMonth")}</span>
          </div>
          {avgCostPerMonth != null && avgTurnDays > 0 && (
            <p className="text-xs text-muted-foreground mt-1">
              {t("floorplan:termsForm.totalOverHold", { amount: fmt$(avgCostPerMonth * (avgTurnDays / 30)) })}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Animated Scanner Loading State ───────────────────────────────────────────

const SCANNER_VINS = [
  { vin: "3C6UR5DL6RG239111", amt: "$20,581", days: "201d" },
  { vin: "1C4RJFCT1LC218724", amt: "$23,905", days: "159d" },
  { vin: "SALGS2RKXKA539952", amt: "$16,013", days: "166d" },
  { vin: "WF0DP3TH0G4115939", amt: "$13,031", days: "161d" },
  { vin: "JTDACAAU9R3042911", amt: "$9,850",  days:  "179d" },
  { vin: "KM8J3CA45HU459541", amt: "$6,930",  days:  "83d"  },
  { vin: "1GCVKREC6JZ163613", amt: "$6,204",  days:  "81d"  },
  { vin: "5XYZT3LB0HG429755", amt: "$5,709",  days:  "81d"  },
  { vin: "1FMSK7DH5LGB41357", amt: "$12,898", days: "47d"   },
  { vin: "JF2SJGTC6HH502949", amt: "$13,476", days: "47d"   },
  { vin: "2T3RFREV6EW149739", amt: "$8,613",  days:  "47d"  },
  { vin: "WDDZF4KB9JA309639", amt: "$12,824", days: "62d"   },
];

const SCANNER_STAGE_KEYS = [
  "reading",
  "identifying",
  "extracting",
  "calculating",
  "almostDone",
];

function ScannerOverlay({ fileName, progressStep, progressPct }: { fileName: string; progressStep?: string | null; progressPct?: number | null }) {
  const { t } = useTranslation(["floorplan", "common"]);
  const SCANNER_STAGES = SCANNER_STAGE_KEYS.map((k) => t(`floorplan:scanner.stages.${k}`));
  const [stageIdx, setStageIdx] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [visibleRows, setVisibleRows] = useState<number[]>([0, 1, 2, 3, 4]);
  const [highlightRow, setHighlightRow] = useState(0);

  useEffect(() => {
    const tick = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(tick);
  }, []);

  useEffect(() => {
    const thresholds = [0, 18, 38, 58, 80];
    const next = thresholds.findIndex((t, i) => elapsed >= t && (thresholds[i + 1] == null || elapsed < thresholds[i + 1]));
    if (next >= 0 && next < SCANNER_STAGES.length) setStageIdx(next);
  }, [elapsed]);

  useEffect(() => {
    const rotateHighlight = setInterval(() => {
      setHighlightRow((h) => (h + 1) % 5);
    }, 600);
    const rotateRows = setInterval(() => {
      setVisibleRows((prev) => {
        const pool = Array.from({ length: SCANNER_VINS.length }, (_, i) => i);
        const shuffled = pool.sort(() => Math.random() - 0.5);
        return shuffled.slice(0, 5);
      });
    }, 2400);
    return () => { clearInterval(rotateHighlight); clearInterval(rotateRows); };
  }, []);

  const mins = Math.floor(elapsed / 60);
  const secs = elapsed % 60;
  const timeStr = mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;

  return (
    <div className="rounded-xl border border-primary/20 bg-black/90 overflow-hidden select-none">
      {/* Header bar */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-primary/10 bg-primary/5">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
          <span className="text-xs font-mono font-semibold text-primary tracking-wide uppercase">
            {t("floorplan:scanner.title")}
          </span>
        </div>
        <span className="text-xs font-mono text-muted-foreground">{timeStr}</span>
      </div>

      {/* File name */}
      <div className="px-4 py-1.5 border-b border-primary/10">
        <span className="text-xs text-muted-foreground font-mono truncate block">
          📄 {fileName}
        </span>
      </div>

      {/* VIN rows */}
      <div className="relative px-4 py-3 space-y-1 min-h-[140px]">
        {/* Scan beam */}
        <div
          className="absolute left-0 right-0 h-px bg-primary/60 pointer-events-none"
          style={{
            top: `${(highlightRow / 4) * 100}%`,
            boxShadow: "0 0 8px 2px hsl(var(--primary) / 0.4)",
            transition: "top 0.5s ease",
          }}
        />
        {visibleRows.map((vinIdx, rowPos) => {
          const v = SCANNER_VINS[vinIdx];
          const isActive = rowPos === highlightRow;
          return (
            <div
              key={`${vinIdx}-${rowPos}`}
              className={`flex items-center justify-between font-mono text-xs px-2 py-0.5 rounded transition-all duration-300 ${
                isActive
                  ? "bg-primary/20 text-primary"
                  : "text-primary/40"
              }`}
            >
              <span className="tracking-widest">{v.vin}</span>
              <div className="flex items-center gap-3">
                <span className={isActive ? "text-green-400" : "text-green-900"}>{v.amt}</span>
                <span className={isActive ? "text-yellow-400" : "text-yellow-900"}>{v.days}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Progress bar */}
      <div className="px-4 pb-1">
        <div className="h-0.5 w-full bg-primary/10 rounded overflow-hidden">
          <div
            className="h-full bg-primary/60 rounded transition-all duration-1000"
            style={{ width: `${progressPct != null ? Math.min(progressPct, 99) : Math.min(95, (elapsed / 90) * 100)}%` }}
          />
        </div>
      </div>

      {/* Stage text */}
      <div className="px-4 pb-3 pt-1.5 flex items-center justify-between">
        <span className="text-xs font-mono text-primary/80 animate-pulse" data-testid="scanner-progress-step">
          {progressStep ?? SCANNER_STAGES[stageIdx]}
        </span>
        {progressPct != null ? (
          <span className="text-[10px] font-mono text-muted-foreground/50 uppercase tracking-wide" data-testid="scanner-progress-pct">
            {Math.round(progressPct)}%
          </span>
        ) : (
          <span className="text-[10px] font-mono text-muted-foreground/50 uppercase tracking-wide">
            {t("floorplan:scanner.stage", { current: stageIdx + 1, total: SCANNER_STAGES.length })}
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Upload Dropzone ──────────────────────────────────────────────────────────

function UploadDropzone({ lenderId, lenderName, onComplete, onMinimize, onScanningChange, minimizeHandlerRef, originFlow, forceReparse }: {
  lenderId: string | null;
  lenderName: string;
  onComplete: (result: any) => void;
  onMinimize?: () => void;
  onScanningChange?: (scanning: boolean) => void;
  minimizeHandlerRef?: MutableRefObject<(() => void) | null>;
  originFlow?: string | null;
  forceReparse?: boolean;
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadingFileName, setUploadingFileName] = useState("");
  const [status, setStatus] = useState("");
  const [result, setResult] = useState<any>(null);
  const [wrongReportNotice, setWrongReportNotice] = useState<string | null>(null);
  const [showReceivables, setShowReceivables] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const minimizedRef = useRef(false);
  const isUploadingRef = useRef(false);
  const scanDoneRef = useRef(false);
  const jobStartedRef = useRef(false);
  const qc = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation(["floorplan", "common"]);
  const scanner = useScannerContext();
  const scannerRef = useRef(scanner);
  scannerRef.current = scanner;

  useEffect(() => {
    onScanningChange?.(isUploading);
  }, [isUploading]);

  // If this dropzone unmounts while a scan is still in flight (e.g. the user
  // navigates away and the host dialog tears down) without going through
  // Minimize or completing, hand the job off to the background ScannerContext
  // so the floating progress card + background polling take over — instead of
  // the upload card silently vanishing while the server job keeps running.
  useEffect(() => () => {
    if (isUploadingRef.current && jobStartedRef.current && !scanDoneRef.current && !minimizedRef.current) {
      minimizedRef.current = true;
      scannerRef.current.minimize();
    }
  }, []);

  useEffect(() => {
    if (!minimizeHandlerRef) return;
    minimizeHandlerRef.current = () => {
      minimizedRef.current = true;
      scanner.minimize();
      onMinimize?.();
    };
    return () => { minimizeHandlerRef.current = null; };
  }, [onMinimize, scanner]);

  const handleFile = useCallback(async (file: File) => {
    const ext = "." + file.name.split(".").pop()!.toLowerCase();
    const allowed = [".pdf"];
    if (!allowed.includes(ext)) {
      toast({ title: t("floorplan:upload.invalidFileTitle"), description: t("floorplan:upload.invalidFileDesc"), variant: "destructive" });
      return;
    }

    minimizedRef.current = false;
    isUploadingRef.current = true;
    scanDoneRef.current = false;
    jobStartedRef.current = false;
    setIsUploading(true);
    setUploadingFileName(file.name);
    setWrongReportNotice(null);
    setStatus(t("floorplan:upload.extractingPdf"));

    try {
      // Step 1: direct-to-storage upload, then finalize — returns {jobId} immediately
      const { uploadId } = await uploadFileDirect(file, "floorplan");
      const res = await fetch("/api/parse-floorplan/finalize", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ uploadId, lenderId: lenderId || undefined, forceReparse: !!forceReparse }),
        credentials: "include",
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.message); }

      const resJson = await res.json();

      // Dedup short-circuit: server found an already-processed statement and returned it directly.
      if (resJson.deduplicated && resJson.statement && !resJson.jobId) {
        const stmt = resJson.statement;
        const dedupResult: any = {
          statement: stmt,
          parsed: {
            lenderName: stmt.lenderName ?? "",
            lenderKey: stmt.lenderKey ?? "generic",
            statementPeriod: { start: stmt.statementPeriodStart, end: stmt.statementPeriodEnd },
            totals: {
              totalInterestPaid: parseFloat(stmt.totalInterestPaid || "0"),
              totalFeesPaid: parseFloat(stmt.totalFeesPaid || "0"),
              averageBalance: parseFloat(stmt.averageBalance || "0"),
              unitsSold: stmt.unitsSold ?? 0,
              periodDays: stmt.periodDays ?? 30,
              avgDaysInStock: parseFloat(stmt.avgDaysInStock || "0"),
            },
            feeBreakdown: {
              feeAdmin: parseFloat(stmt.feeAdmin || "0"),
              feeAudit: parseFloat(stmt.feeAudit || "0"),
              feeCurtailment: parseFloat(stmt.feeCurtailment || "0"),
              feeDoc: parseFloat(stmt.feeDoc || "0"),
              feeTitle: parseFloat(stmt.feeTitle || "0"),
              feeOther: parseFloat(stmt.feeOther || "0"),
            },
            receivables: JSON.parse(stmt.receivablesJson || "[]"),
            detectedFieldsConfidence: JSON.parse(stmt.confidenceJson || "{}"),
            warnings: JSON.parse(stmt.warningsJson || "[]"),
          },
          extractionMethod: stmt.extractionMethod ?? "cached",
          success: true,
        };
        setResult(dedupResult);
        qc.invalidateQueries({ queryKey: ["/api/parsed-statements"] });
        qc.invalidateQueries({ queryKey: ["/api/floorplan-inventory"] });
        qc.invalidateQueries({ queryKey: ["/api/inventory/enriched"] });
        qc.invalidateQueries({ queryKey: ["/api/lenders"] });
        qc.invalidateQueries({
          predicate: (q) => typeof q.queryKey[0] === "string" && q.queryKey[0].startsWith("/api/lenders/comparison-stats"),
        });
        qc.invalidateQueries({ queryKey: ["/api/savings/overview"] });
        scanDoneRef.current = true;
        onComplete(dedupResult);
        scanner.clear();
        return;
      }

      const { jobId } = resJson;

      // Register with background scanner context so minimize-and-continue works.
      // Pass minimizedRef.current so if the user clicked Minimize before the
      // jobId came back from the server, the context starts in minimized state
      // and background polling begins immediately (instead of resetting to false).
      scanner.startScan(jobId, window.location.pathname, originFlow, minimizedRef.current);
      jobStartedRef.current = true;

      // Poll for job completion. Returns null when the user minimizes — context takes over.
      setStatus(t("floorplan:upload.extractingStatement"));
      const data: any | null = await (async () => {
        const POLL_INTERVAL = 2000;
        const MAX_WAIT = 6 * 60 * 1000;
        const started = Date.now();
        while (Date.now() - started < MAX_WAIT) {
          if (minimizedRef.current) return null;
          await new Promise((r) => setTimeout(r, POLL_INTERVAL));
          if (minimizedRef.current) return null;
          const statusRes = await fetch(`/api/parse-floorplan/status/${jobId}`, { credentials: "include" });
          const statusJson = await statusRes.json();
          if (statusJson.status === "done") return statusJson.result;
          if (statusJson.status === "error") throw new Error(statusJson.error || t("floorplan:upload.extractionFailed"));
          if (statusJson.status === "processing") {
            if (statusJson.step != null || statusJson.pct != null) {
              scanner.setProgress(
                statusJson.step ?? null,
                statusJson.pct ?? null,
              );
            }
          }
        }
        if (minimizedRef.current) return null;
        throw new Error(t("floorplan:upload.extractionTimedOut"));
      })();

      if (data === null) return; // minimized — ScannerContext is now the sole authority

      setResult(data);
      qc.invalidateQueries({ queryKey: ["/api/parsed-statements"] });
      qc.invalidateQueries({ queryKey: ["/api/floorplan-inventory"] });
      qc.invalidateQueries({ queryKey: ["/api/inventory/enriched"] });
      qc.invalidateQueries({ queryKey: ["/api/lenders"] });
      qc.invalidateQueries({
        predicate: (q) => typeof q.queryKey[0] === "string" && q.queryKey[0].startsWith("/api/lenders/comparison-stats"),
      });
      qc.invalidateQueries({ queryKey: ["/api/savings/overview"] });

      // Show inventory sync toast from upload completion
      const ss = data?.syncSummary as { added?: number; updated?: number } | undefined;
      if (ss && (ss.added || ss.updated)) {
        const parts: string[] = [];
        if (ss.added) parts.push(t("floorplan:upload.vehiclesAdded", { count: ss.added }));
        if (ss.updated) parts.push(t("floorplan:upload.updatedFromPrevious", { count: ss.updated }));
        toast({ title: t("floorplan:upload.inventorySyncedTitle"), description: parts.join(", ") + "." });
      }

      scanDoneRef.current = true;
      onComplete(data);
      scanner.clear();
    } catch (err: any) {
      scanDoneRef.current = true;
      if (!minimizedRef.current) {
        if (/floored\s+units\s+report/i.test(err.message ?? "")) {
          // Wrong Westlake report type — show a persistent explainer banner
          // instead of a transient toast, so the dealer knows exactly which
          // report to go download. Stays until dismissed with the X.
          setWrongReportNotice(err.message);
        } else {
          toast({ title: t("floorplan:upload.uploadFailed"), description: err.message, variant: "destructive" });
        }
        scanner.clear();
      }
    } finally {
      isUploadingRef.current = false;
      if (!minimizedRef.current) {
        setIsUploading(false);
        setStatus("");
      }
    }
  }, [lenderId, toast, qc, onComplete, scanner]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault(); setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  if (result) {
    const p = result.parsed;
    const warnings = p.warnings ?? [];
    const receivables = p.receivables ?? [];
    const extractBadge = {
      "ai-pdf": { label: t("floorplan:upload.badges.aiParsed"), cls: "text-chart-3 border-chart-3/30" },
      "ai-image": { label: t("floorplan:upload.badges.aiVision"), cls: "text-chart-3 border-chart-3/30" },
      "pdf-text": { label: t("floorplan:upload.badges.pdfText"), cls: "text-chart-2 border-chart-2/30" },
      "regex-pdf": { label: t("floorplan:upload.badges.pdfText"), cls: "text-chart-2 border-chart-2/30" },
      "regex-ocr": { label: t("floorplan:upload.badges.ocr"), cls: "text-yellow-600 border-yellow-500/30" },
      "ocr": { label: t("floorplan:upload.badges.ocr"), cls: "text-yellow-600 border-yellow-500/30" },
      "manual-required": { label: t("floorplan:upload.badges.manualRequired"), cls: "text-destructive border-destructive/30" },
    }[result.extractionMethod as string] ?? { label: t("floorplan:upload.badges.unknown"), cls: "" };

    const unitsSold = p.totals.unitsSold || 0;
    const totalInterest = n(p.totals.totalInterestPaid);
    const totalFees = n(p.totals.totalFeesPaid);
    const avgBalance = n(p.totals.averageBalance);

    // Formula 1: Portfolio Effective APR (weighted by principal × daysFloored)
    // Fires when per-unit receivable rows carry principal, days, and cost data.
    const qualifyingRows = receivables.filter(
      (r: any) => (r.principal ?? 0) > 0 && (r.daysOutstanding ?? 0) > 0 && ((r.interest ?? 0) + (r.fees ?? 0)) > 0
    );

    // For per-unit averages: prefer unitsSold from statement totals.
    // Fall back to qualifying receivable row count, then full receivable count.
    const effectiveUnitCount = unitsSold > 0 ? unitsSold
      : qualifyingRows.length > 0 ? qualifyingRows.length
      : receivables.length > 0 ? receivables.length
      : 0;

    // When qualifying rows exist, per-unit stats come directly from those rows (more accurate).
    const totalCollateralFees = receivables.reduce((s: number, r: any) => s + (r.collateralFee ?? 0), 0);
    const avgInterestPerUnit = qualifyingRows.length > 0
      ? qualifyingRows.reduce((s: number, r: any) => s + (r.interest ?? 0), 0) / qualifyingRows.length
      : effectiveUnitCount > 0 ? totalInterest / effectiveUnitCount : null;
    const avgFeesPerUnit = qualifyingRows.length > 0
      ? qualifyingRows.reduce((s: number, r: any) => s + (r.fees ?? 0) - (r.collateralFee ?? 0), 0) / qualifyingRows.length
      : effectiveUnitCount > 0 ? (totalFees - totalCollateralFees) / effectiveUnitCount : null;
    const portfolioApr = qualifyingRows.length > 0
      ? (qualifyingRows.reduce((s: number, r: any) => s + (r.interest ?? 0) + (r.fees ?? 0), 0) /
         qualifyingRows.reduce((s: number, r: any) => s + (r.principal ?? 0) * (r.daysOutstanding ?? 0), 0)) * 365 * 100
      : null;

    // Formula 3: Calculated APR from statement totals.
    // Uses actual interest+fees / actual outstanding principal, annualized by avg days floored.
    // This is the "real" rate derived from what was actually charged — not the stated contract rate.
    const avgDaysFloored = n(p.totals.avgDaysInStock);
    const annualDivisor = avgDaysFloored > 0 ? avgDaysFloored : (p.totals.periodDays || 30);
    const statementApr = avgBalance > 0 && (totalInterest > 0 || totalFees > 0)
      ? ((totalInterest + totalFees) / avgBalance) * (365 / annualDivisor) * 100
      : null;

    const effectiveApr = portfolioApr ?? statementApr;
    const aprMethod = portfolioApr !== null
      ? t("floorplan:upload.aprMethod.weighted")
      : avgDaysFloored > 0
        ? t("floorplan:upload.aprMethod.interestPrincipal")
        : t("floorplan:upload.aprMethod.statementTotals");

    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <CheckCircle2 className="w-4 h-4 text-chart-3" />
          <span className="text-sm font-medium">{t("floorplan:upload.statementParsedFor", { lenderName })}</span>
          <Badge variant="outline" className={`text-xs ${extractBadge.cls}`}>{extractBadge.label}</Badge>
          <Button size="sm" variant="ghost" onClick={() => setResult(null)} className="ml-auto" data-testid="button-upload-another">
            {t("floorplan:upload.uploadAnother")}
          </Button>
        </div>

        {result.termsApplied && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-chart-3/10 border border-chart-3/20 text-xs text-chart-3 font-medium">
            <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
            {t("floorplan:upload.termStructureApplied")}
          </div>
        )}

        {/* Key takeaway metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <div className="p-3 rounded-xl bg-primary/8 border border-primary/20 text-center">
            <p className="text-xs text-muted-foreground mb-1">{t("floorplan:upload.avgInterestPerUnit")}</p>
            <p className="text-lg font-bold text-primary" data-testid="result-avg-interest">
              {avgInterestPerUnit !== null ? fmt$(avgInterestPerUnit) : "—"}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {effectiveUnitCount > 0
                ? `${t("floorplan:upload.units", { count: effectiveUnitCount })}${qualifyingRows.length > 0 ? t("floorplan:upload.fromRowsSuffix") : unitsSold > 0 ? "" : t("floorplan:upload.estSuffix")}`
                : t("floorplan:upload.noUnitCount")}
            </p>
          </div>
          <div className="p-3 rounded-xl bg-chart-2/10 border border-chart-2/20 text-center">
            <p className="text-xs text-muted-foreground mb-1">{t("floorplan:upload.avgFeesPerUnit")}</p>
            <p className="text-lg font-bold" data-testid="result-avg-fees">
              {avgFeesPerUnit !== null ? fmt$(avgFeesPerUnit) : "—"}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {effectiveUnitCount > 0 ? t("floorplan:upload.units", { count: effectiveUnitCount }) : t("floorplan:upload.noUnitCount")}
            </p>
          </div>
          <div className="p-3 rounded-xl bg-chart-3/10 border border-chart-3/20 text-center">
            <p className="text-xs text-muted-foreground mb-1">{t("floorplan:upload.effectiveIntRate")}</p>
            <p className="text-lg font-bold text-chart-3" data-testid="result-effective-apr">
              {effectiveApr !== null ? `${effectiveApr.toFixed(2)}%` : "—"}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">{aprMethod}</p>
          </div>
        </div>

        {/* Extraction summary */}
        {(() => {
          interface AccuracyCheckResult {
            status: "verified" | "warning" | "unverified" | "ai-corrected" | "needs-review";
            vehicleCount?: number;
            interestVariancePct?: number;
            feesVariancePct?: number;
            note?: string;
          }
          const ac = p.accuracyCheck as AccuracyCheckResult | undefined;
          const acStatus = ac?.status ?? "unverified";
          const acColor =
            acStatus === "verified" || acStatus === "ai-corrected"
              ? "bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-950/30 dark:border-emerald-800 dark:text-emerald-400"
              : acStatus === "warning"
              ? "bg-yellow-50 border-yellow-200 text-yellow-700 dark:bg-yellow-950/30 dark:border-yellow-800 dark:text-yellow-400"
              : acStatus === "needs-review"
              ? "bg-destructive/10 border-destructive/30 text-destructive"
              : "bg-muted/40 border-border text-muted-foreground";
          const acIcon =
            acStatus === "verified" || acStatus === "ai-corrected" ? (
              <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
            ) : acStatus === "warning" || acStatus === "needs-review" ? (
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
            ) : (
              <Info className="w-3.5 h-3.5 shrink-0" />
            );
          const acLabel =
            acStatus === "verified" ? t("floorplan:upload.accuracy.verified")
            : acStatus === "ai-corrected" ? t("floorplan:upload.accuracy.aiCorrected")
            : acStatus === "warning" ? t("floorplan:upload.accuracy.warning")
            : acStatus === "needs-review" ? t("floorplan:upload.accuracy.needsReview")
            : t("floorplan:upload.accuracy.unverified");

          return (
            <div className="rounded-lg border text-xs overflow-hidden" data-testid="extraction-summary">
              <div className="px-3 py-2 bg-muted/20 border-b border-border/50">
                <span className="uppercase tracking-wide text-[10px] text-muted-foreground font-semibold">{t("floorplan:upload.aiExtractionSummary")}</span>
              </div>
              <div className="px-3 py-2.5 space-y-2">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div className="p-2 rounded bg-muted/30 text-center" data-testid="summary-vehicle-count">
                    <p className="text-muted-foreground mb-0.5">{t("floorplan:upload.vehicles")}</p>
                    <p className="font-semibold text-sm">{ac?.vehicleCount ?? p.totals?.unitsSold ?? "—"}</p>
                  </div>
                  <div className="p-2 rounded bg-muted/30 text-center" data-testid="summary-total-interest">
                    <p className="text-muted-foreground mb-0.5">{t("floorplan:upload.totalInterest")}</p>
                    <p className="font-semibold text-sm">{totalInterest > 0 ? fmt$(totalInterest) : "—"}</p>
                  </div>
                  <div className="p-2 rounded bg-muted/30 text-center" data-testid="summary-total-fees">
                    <p className="text-muted-foreground mb-0.5">{t("floorplan:upload.totalFees")}</p>
                    <p className="font-semibold text-sm">{totalFees > 0 ? fmt$(totalFees) : "—"}</p>
                  </div>
                  <div className="p-2 rounded bg-muted/30 text-center" data-testid="summary-effective-apr">
                    <p className="text-muted-foreground mb-0.5">{t("floorplan:upload.effectiveIntRate")}</p>
                    <p className="font-semibold text-sm">{effectiveApr !== null ? `${effectiveApr.toFixed(2)}%` : "—"}</p>
                  </div>
                </div>
                <div className={`flex items-start gap-2 px-2.5 py-2 rounded border ${acColor}`} data-testid="summary-accuracy-status">
                  {acIcon}
                  <span>{acLabel}{ac?.note ? ` — ${ac.note}` : ""}</span>
                </div>
                {ac?.interestVariancePct != null && (
                  <p className="text-muted-foreground px-1">
                    {t("floorplan:upload.interestVariance", { pct: ac.interestVariancePct.toFixed(1) })}
                    {ac.feesVariancePct != null ? t("floorplan:upload.feesVarianceSuffix", { pct: ac.feesVariancePct.toFixed(1) }) : ""}
                  </p>
                )}
              </div>
            </div>
          );
        })()}

        {warnings.length > 0 && (
          <div className="space-y-1">
            {warnings.map((w: string, i: number) => (
              <div key={i} className="flex gap-2 p-2 rounded bg-yellow-500/10 border border-yellow-500/20 text-xs text-yellow-700 dark:text-yellow-400">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />{w}
              </div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
          {[
            [t("floorplan:upload.totalInterest"), fmt$(n(p.totals.totalInterestPaid))],
            [t("floorplan:upload.totalFees"), fmt$(n(p.totals.totalFeesPaid))],
            [t("floorplan:upload.avgBalance"), fmt$(n(p.totals.averageBalance))],
            [t("floorplan:upload.unitsSold"), p.totals.unitsSold ?? "—"],
            [t("floorplan:upload.periodDays"), p.totals.periodDays ?? "—"],
            [t("floorplan:upload.avgDaysInStock"), p.totals.avgDaysInStock ? t("floorplan:upload.daysValue", { value: n(p.totals.avgDaysInStock).toFixed(1) }) : "—"],
          ].map(([label, val]) => (
            <div key={label as string} className="p-2.5 rounded-lg bg-muted/40">
              <p className="text-xs text-muted-foreground">{label}</p>
              <p className="font-semibold mt-0.5">{val}</p>
            </div>
          ))}
        </div>

        {receivables.length > 0 && (
          <div>
            <button
              onClick={() => setShowReceivables(!showReceivables)}
              className="flex items-center gap-2 text-sm font-medium w-full text-left"
              data-testid="toggle-receivables"
            >
              {showReceivables ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              {t("floorplan:upload.receivableLineItems", { count: receivables.length })}
            </button>
            {showReceivables && (
              <div className="mt-2 overflow-x-auto max-h-56 overflow-y-auto">
                <table className="w-full text-xs border-collapse">
                  {(() => {
                    const hasCpp = receivables.some((r: any) => (r.collateralFee ?? 0) > 0);
                    return (
                  <thead className="sticky top-0 bg-background">
                    <tr className="border-b">
                      <th className="text-left py-1 px-2 text-muted-foreground">{t("floorplan:upload.th.vin")}</th>
                      <th className="text-left py-1 px-2 text-muted-foreground">{t("floorplan:upload.th.stock")}</th>
                      <th className="text-right py-1 px-2 text-muted-foreground">{t("floorplan:upload.th.principal")}</th>
                      <th className="text-right py-1 px-2 text-muted-foreground">{t("floorplan:upload.th.days")}</th>
                      <th className="text-right py-1 px-2 text-muted-foreground">{t("floorplan:upload.th.interest")}</th>
                      <th className="text-right py-1 px-2 text-muted-foreground">
                        {t("floorplan:upload.th.fees")}{hasCpp && <span className="block text-[9px] font-normal opacity-60">{t("floorplan:upload.th.exclCpp")}</span>}
                      </th>
                      {hasCpp && <th className="text-right py-1 px-2 text-muted-foreground">{t("floorplan:upload.th.cpp")}</th>}
                      <th className="text-right py-1 px-2 text-muted-foreground">{t("floorplan:upload.th.intRate")}</th>
                    </tr>
                  </thead>
                    );
                  })()}
                  <tbody>
                    {(() => {
                      const hasCpp = receivables.some((r: any) => (r.collateralFee ?? 0) > 0);
                      return receivables.slice(0, 50).map((r: any, i: number) => {
                        const principal = r.principal ?? 0;
                        const days = r.daysOutstanding ?? 0;
                        const interest = r.interest ?? 0;
                        const cpp = r.collateralFee ?? 0;
                        const fees = (r.fees ?? 0) - cpp;
                        const unitApr = principal > 0 && days > 0
                          ? (interest / principal) * (365 / days) * 100
                          : null;
                        return (
                        <tr key={i} className="border-b last:border-0" data-testid={`recv-row-${i}`}>
                          <td className="py-1 px-2 font-mono">{r.vin ?? "—"}</td>
                          <td className="py-1 px-2">{r.stock ?? "—"}</td>
                          <td className="py-1 px-2 text-right">{principal > 0 ? fmt$(principal) : "—"}</td>
                          <td className="py-1 px-2 text-right">{days > 0 ? days : "—"}</td>
                          <td className="py-1 px-2 text-right">{fmt$(interest ?? 0)}</td>
                          <td className="py-1 px-2 text-right">{fees > 0 ? fmt$(fees) : "—"}</td>
                          {hasCpp && <td className="py-1 px-2 text-right text-amber-600 dark:text-amber-400">{cpp > 0 ? fmt$(cpp) : "—"}</td>}
                          <td className="py-1 px-2 text-right font-medium">{unitApr !== null ? `${unitApr.toFixed(1)}%` : "—"}</td>
                        </tr>
                      );
                      });
                    })()}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <p className="text-xs text-muted-foreground">{t("floorplan:upload.statementSaved")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {wrongReportNotice && (
        <div
          className="relative rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-4 pr-10"
          data-testid="banner-wrong-report"
        >
          <button
            type="button"
            onClick={() => setWrongReportNotice(null)}
            className="absolute top-2.5 right-2.5 text-amber-700 dark:text-amber-400 hover:text-amber-900 dark:hover:text-amber-200 transition-colors"
            aria-label={t("floorplan:ui.dismiss")}
            data-testid="button-dismiss-wrong-report"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="space-y-1.5 text-sm">
              <p className="font-semibold text-amber-900 dark:text-amber-200">{t("floorplan:summary.wrongReportTitle")}</p>
              <p className="text-amber-800 dark:text-amber-300">
                {t("floorplan:summary.wrongReportBody1a")}<span className="font-semibold">{t("floorplan:summary.wrongReportQuote1")}</span>{t("floorplan:summary.wrongReportBody1b")}
              </p>
              <p className="text-amber-800 dark:text-amber-300">
                {t("floorplan:summary.wrongReportBody2a")}<span className="font-semibold">{t("floorplan:summary.wrongReportQuote2")}</span>{t("floorplan:summary.wrongReportBody2b")}
              </p>
            </div>
          </div>
        </div>
      )}
      <div
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => !isUploading && fileRef.current?.click()}
        className={`
          rounded-xl transition-all
          ${isUploading
            ? "pointer-events-none border border-primary/20"
            : `border-2 border-dashed p-10 text-center cursor-pointer ${isDragging ? "border-primary bg-primary/5" : "border-border hover:border-primary/50 hover:bg-muted/20"}`
          }
        `}
        data-testid="upload-dropzone"
      >
        <input ref={fileRef} type="file" className="hidden" accept=".pdf,application/pdf"
          onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])} />
        {isUploading ? (
          <div className="space-y-2">
            <ScannerOverlay fileName={uploadingFileName} progressStep={scanner.progressStep} progressPct={scanner.progressPct} />
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex justify-center gap-2">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <FileText className="w-5 h-5 text-primary" />
              </div>
            </div>
            <p className="text-sm font-semibold">{t("floorplan:dropzone.dropHere", { lenderName })}</p>
            <p className="text-xs text-muted-foreground">{t("floorplan:dropzone.pdfOnly")}</p>
            <div className="flex justify-center gap-1.5 flex-wrap mt-2">
              {["NextGear", "AFC", "Ally", "Manheim", "Floorplan Xpress"].map((l) => (
                <Badge key={l} variant="secondary" className="text-xs">{l}</Badge>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Real APR Section (from uploaded statements) ─────────────────────────────


// Returns ALL statements for this lender sorted most-recent-first.
// Index [0] gives the latest statement (used for lender analytics on the Floorplan page).
//
// allLenders (optional) — when provided, the fuzzy fallback also rescues statements
// whose lenderId is non-null but no longer points to any known lender (stale id from
// a delete-and-recreate cycle). Without it the fallback only considers null-lenderId rows.
function stmtsForLender(statements: any[], lender: Lender, allLenders?: Lender[]): any[] {
  const byId = statements.filter((s) => s.lenderId === lender.id);
  if (byId.length > 0)
    return [...byId].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  const nameNorm = lender.name.toLowerCase().trim();
  // Build a set of currently-known lender IDs so we can distinguish stale from valid.
  const knownIds: Set<string> | null = allLenders ? new Set(allLenders.map((l) => l.id)) : null;
  const pool = statements.filter((s) =>
    // Include rows that have no lenderId OR carry a stale lenderId no longer in the list
    (!s.lenderId || (knownIds && !knownIds.has(s.lenderId))) &&
    (
      (s.lenderName && (
        s.lenderName.toLowerCase().trim().includes(nameNorm) ||
        nameNorm.includes(s.lenderName.toLowerCase().trim())
      )) ||
      (s.fileName && (
        s.fileName.toLowerCase().includes(nameNorm) ||
        nameNorm.includes(s.fileName.toLowerCase().replace(/\.[^.]+$/, "").trim())
      ))
    )
  );
  if (pool.length === 0) return [];
  return [...pool].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
}

/**
 * Derive average fee per unit from actual receivables, bucketed by the turn-days
 * scenario value. Uses the same aging-bucket/tier logic as the comparison-stats endpoint.
 * Returns null when no qualifying receivable rows are available.
 */
function deriveReceivablesFeeForScenario(
  stmts: any[],
  lender: Lender,
  turnDays: number,
  tierValueMin?: number,
  tierValueMax?: number,
): number | null {
  const allRecvs = dedupeReceivables(stmts);
  let qualified = allRecvs.filter(
    (r) => (r.principal ?? 0) > 0 && (r.daysOutstanding ?? 0) > 0 && ((r.interest ?? 0) > 0 || (r.fees ?? 0) > 0)
  );
  // When a vehicle value range is provided (tiered-pricing lender), restrict
  // strictly to receivables whose principal falls within that tier's price band.
  // If a range was requested but no receivables match it, return null so the
  // caller falls back to stated/manual fees — never use out-of-tier data.
  if (tierValueMin !== undefined || tierValueMax !== undefined) {
    const lo = tierValueMin ?? 0;
    const hi = tierValueMax ?? Infinity;
    const rangeFiltered = qualified.filter((r) => (r.principal ?? 0) >= lo && (r.principal ?? 0) < hi);
    if (rangeFiltered.length === 0) return null;
    qualified = rangeFiltered;
  }
  if (qualified.length === 0) return null;

  const rawTierDefs = [
    { days: lender.period1Days ?? 0, fee: lender.feePeriod1 },
    { days: lender.period2Days ?? 0, fee: lender.feePeriod2 },
    { days: lender.period3Days ?? 0, fee: lender.feePeriod3 },
    { days: lender.period4Days ?? 0, fee: lender.feePeriod4 },
  ];
  const tiers: Array<{ start: number; end: number; eligible: boolean }> = [];
  let cumTotal = 0;
  for (const td of rawTierDefs) {
    if (td.days > 0) {
      const start = cumTotal === 0 ? 0 : cumTotal + 1;
      const end = cumTotal + td.days;
      tiers.push({ start, end, eligible: parseFloat(td.fee ?? "0") > 0 });
      cumTotal = end;
    }
  }
  // Use period buckets for bucketing regardless of whether a fee dollar amount is
  // configured. A 45-day scenario should only average period-1 receivables, not
  // older vehicles that have accumulated period-2/3 fees. Fall back to all
  // receivables only when no period structure is known (no period days configured).
  let feeUnits = qualified;
  if (tiers.length > 0) {
    const matchedTier = tiers.find((t) => turnDays >= t.start && turnDays <= t.end);
    if (matchedTier) {
      const tierUnits = qualified.filter(
        (r) => (r.daysOutstanding ?? 0) >= matchedTier.start && (r.daysOutstanding ?? 0) <= matchedTier.end
      );
      if (tierUnits.length > 0) feeUnits = tierUnits;
    }
  }
  return feeUnits.reduce((s: number, r: any) => s + (r.fees ?? 0), 0) / feeUnits.length;
}

// ─── LinkStatementDialog ──────────────────────────────────────────────────────

function LinkStatementDialog({
  lender, onClose,
}: {
  lender: Lender | null;
  onClose: () => void;
}) {
  const { t } = useTranslation(["floorplan", "common"]);
  const { toast } = useToast();
  const qc = useQueryClient();
  const { data: allStatements = [] } = useQuery<any[]>({ queryKey: ["/api/parsed-statements"] });
  const [linking, setLinking] = useState<string | null>(null);

  const orphaned = allStatements.filter((s) => !s.lenderId);

  if (!lender) return null;

  const handleLink = async (stmtId: string) => {
    setLinking(stmtId);
    try {
      const res = await apiRequest("PATCH", `/api/parsed-statements/${stmtId}`, { lenderId: lender.id });
      if (res.ok) {
        qc.invalidateQueries({ queryKey: ["/api/parsed-statements"] });
        toast({ title: t("floorplan:toast.statementLinked"), description: t("floorplan:toast.statementLinkedTo", { name: lender.name }) });
        onClose();
      } else {
        const errBody = await res.json().catch(() => ({}));
        toast({ title: t("floorplan:toast.linkFailed"), description: errBody.message ?? t("floorplan:toast.couldNotLink"), variant: "destructive" });
      }
    } catch (e: any) {
      toast({ title: t("floorplan:toast.linkFailed"), description: e.message ?? t("floorplan:toast.networkError"), variant: "destructive" });
    } finally {
      setLinking(null);
    }
  };

  return (
    <Dialog open={true} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("floorplan:linkDialog.title", { name: lender.name })}</DialogTitle>
          <DialogDescription>
            {t("floorplan:linkDialog.desc")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-72 overflow-y-auto">
          {orphaned.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">{t("floorplan:linkDialog.noUnlinked")}</p>
          ) : orphaned.map((s) => (
            <div
              key={s.id}
              className="flex items-center justify-between gap-2 p-2.5 rounded-lg border bg-muted/20 hover:bg-muted/40 transition-colors"
              data-testid={`link-stmt-${s.id}`}
            >
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{s.fileName ?? t("floorplan:linkDialog.statementFallback", { id: s.id.slice(0, 8) })}</p>
                <p className="text-xs text-muted-foreground">
                  {s.lenderName && s.lenderName !== "Unknown Lender" ? s.lenderName : t("floorplan:linkDialog.unknownLender")} · {new Date(s.createdAt).toLocaleDateString(intlLocale())}
                </p>
              </div>
              <Button
                size="sm" variant="outline" className="shrink-0 h-7 text-xs"
                disabled={linking === s.id}
                onClick={() => handleLink(s.id)}
                data-testid={`button-link-stmt-${s.id}`}
              >
                {linking === s.id ? "Linking…" : "Link"}
              </Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Month helpers ────────────────────────────────────────────────────────────

const MONTH_ABBR = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function getTimelineMonths(stmts: any[]): Array<{ key: string; month: number; year: number; hasStmt: boolean; stmtId: string | null }> {
  return getTimelineMonthsLib(stmts);
}

// ─── RealAprSection ───────────────────────────────────────────────────────────

function RealAprSection({
  lenders, scenario, onUpload, onEdit, onDelete, onClickAnalytics,
}: {
  lenders: Lender[];
  scenario: ScenarioInputs;
  onUpload: (l: Lender) => void;
  onEdit: (l: Lender) => void;
  onDelete: (l: Lender) => void;
  onClickAnalytics: (l: Lender) => void;
}) {
  const { t } = useTranslation(["floorplan", "common"]);
  const [linkTarget, setLinkTarget] = useState<Lender | null>(null);
  const [selectedMonths, setSelectedMonths] = useState<Record<string, Set<string>>>({});
  const { data: allStatements = [] } = useQuery<any[]>({ queryKey: ["/api/parsed-statements"] });

  // Build per-lender timeline data once from allStatements
  const lenderTimelines = useMemo(() => {
    const map: Record<string, ReturnType<typeof getTimelineMonths>> = {};
    for (const l of lenders) {
      const stmts = stmtsForLender(allStatements, l, lenders);
      map[l.id] = getTimelineMonths(stmts);
    }
    return map;
  }, [lenders, allStatements]);

  // Keep selectedMonths in sync with lenderTimelines:
  // - Initialize state for new lenders (select all available months)
  // - Auto-select newly-discovered months for existing lenders
  useEffect(() => {
    setSelectedMonths((prev) => {
      let next = prev;
      for (const l of lenders) {
        const timeline = lenderTimelines[l.id] ?? [];
        const availableKeys = timeline.filter((t) => t.hasStmt).map((t) => t.key);
        if (availableKeys.length === 0) continue;
        const existing = next[l.id];
        if (!existing) {
          // Brand-new lender in state: select all available months
          if (next === prev) next = { ...prev };
          next[l.id] = new Set(availableKeys);
        } else {
          // Existing lender: auto-select any newly discovered months
          const newKeys = availableKeys.filter((k) => !existing.has(k));
          if (newKeys.length > 0) {
            const merged = new Set(existing);
            for (const k of newKeys) merged.add(k);
            if (next === prev) next = { ...prev };
            next[l.id] = merged;
          }
        }
      }
      return next;
    });
  }, [lenders, lenderTimelines]);

  const toggleMonth = (lenderId: string, monthKey: string) => {
    setSelectedMonths((prev) => {
      const cur = prev[lenderId] ? new Set(prev[lenderId]) : new Set<string>();
      if (cur.has(monthKey)) { cur.delete(monthKey); } else { cur.add(monthKey); }
      return { ...prev, [lenderId]: cur };
    });
  };

  const getEffectiveStmts = (stmts: any[], lenderId: string) => {
    const timeline = lenderTimelines[lenderId] ?? [];
    const sel = selectedMonths[lenderId];
    return filterEffectiveStmts(stmts, timeline, sel);
  };

  const lenderData = lenders.map((l) => {
    const stmts = stmtsForLender(allStatements, l, lenders);
    const timeline = lenderTimelines[l.id] ?? [];
    const effectiveStmts = getEffectiveStmts(stmts, l.id);
    const real = computeRealAprForLender(effectiveStmts, { manualFeePerUnit: manualFeePerUnitFromLender(l) });
    // Track whether a statement exists at all (even if extraction failed)
    const hasStatement = stmts.length > 0;
    const latestStmt = stmts[0] ?? null;
    // A statement "failed extraction" only if it exists but produced zero receivables with principal
    // (APR being null alone is not a failure — Westlake has no Days column so APR is always null)
    const qualRecvsEarlyCheck = (() => {
      return dedupeReceivables(stmts).filter((r) => (r.principal ?? 0) > 0);
    })();
    const extractionFailed = hasStatement && qualRecvsEarlyCheck.length === 0 && real.apr === null;
    const latestFileName = latestStmt?.fileName ?? null;
    // Gather qualifying per-unit receivable rows from the effective (selected) statements
    const allRecvs = dedupeReceivables(effectiveStmts);
    // qualRecvs: all vehicles with a positive principal — includes newly floored (0/null days)
    // costRecvs: all vehicles with real charges (interest+fees>0) — for vehicle COUNT display
    // chargeRecvs: subset with positive days AND actual charges — for APR / cost averages
    const qualRecvs = allRecvs.filter((r) => (r.principal ?? 0) > 0);
    const costRecvs = qualRecvs.filter((r) => ((r.interest ?? 0) + (r.fees ?? 0)) > 0);
    const chargeRecvs = qualRecvs.filter(
      (r) => (r.daysOutstanding ?? 0) > 0 && ((r.interest ?? 0) + (r.fees ?? 0)) > 0
    );
    const collateralRecvs = qualRecvs.filter((r) => (r.collateralFee ?? 0) > 0);
    const avgCollateralFeePerUnit = collateralRecvs.length > 0
      ? collateralRecvs.reduce((sum: number, r: any) => sum + (r.collateralFee ?? 0), 0) / collateralRecvs.length
      : null;

    return { lender: l, ...real, qualRecvs, costRecvs, chargeRecvs, avgCollateralFeePerUnit, hasStatement, extractionFailed, latestFileName, timeline };
  });

  const hasAnyRealData = lenderData.some((d) => d.apr !== null);

  const sorted = [...lenderData].sort((a, b) => {
    if (a.apr === null && b.apr === null) return 0;
    if (a.apr === null) return 1;
    if (b.apr === null) return -1;
    return a.apr - b.apr;
  });

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <CheckCircle2 className="w-4 h-4 text-chart-3" />
        <h2 className="text-sm font-semibold">{t("floorplan:summary.title")}</h2>
        <span className="text-xs text-muted-foreground ml-1">
          {hasAnyRealData ? t("floorplan:summary.realCosts") : t("floorplan:summary.uploadToSee")}
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {sorted.map((d, i) => {
          const isCheapest = d.apr !== null && i === 0 && hasAnyRealData;
          return (
            <Card
              key={d.lender.id}
              className={`relative overflow-hidden cursor-pointer transition-all hover:shadow-md hover:border-primary/30 select-none ${isCheapest ? "ring-1 ring-chart-3/40" : ""}`}
              onClick={() => onClickAnalytics(d.lender)}
              data-testid={`real-apr-card-${d.lender.id}`}
            >
              {/* Corner action buttons */}
              <div className="absolute top-2 right-2 flex gap-0.5 z-10">
                <Button
                  size="icon" variant="ghost" className="h-6 w-6 opacity-40 hover:opacity-100 transition-opacity"
                  onClick={(e) => { e.stopPropagation(); onEdit(d.lender); }}
                  data-testid={`button-edit-real-${d.lender.id}`}
                  title={t("floorplan:lenderCard.editTerms")}
                >
                  <Edit className="w-3 h-3" />
                </Button>
                <Button
                  size="icon" variant="ghost" className="h-6 w-6 opacity-40 hover:opacity-100 hover:text-destructive transition-opacity"
                  onClick={(e) => { e.stopPropagation(); onDelete(d.lender); }}
                  data-testid={`button-delete-real-${d.lender.id}`}
                  title={t("floorplan:lenderCard.deleteLender")}
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>

              <CardContent className="p-3">
                <div className="flex items-center gap-2 pr-14 mb-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold leading-tight truncate">{d.lender.name}</p>
                    {d.latestFileName && (
                      <p className="text-xs text-muted-foreground truncate max-w-[160px]" title={d.latestFileName}>{d.latestFileName}</p>
                    )}
                  </div>
                  {isCheapest && (
                    <Badge variant="outline" className="text-xs text-chart-3 border-chart-3/30 ml-auto shrink-0">{t("floorplan:summary.lowest")}</Badge>
                  )}
                </div>

                {/* Month timeline */}
                {d.timeline.length > 0 && (
                  <div
                    className="mb-2 flex flex-wrap gap-1"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {d.timeline.map((t) => {
                      const isSelected = selectedMonths[d.lender.id]?.has(t.key) ?? true;
                      return (
                        <button
                          key={t.key}
                          title={`${MONTH_ABBR[t.month-1]} ${t.year}${!t.hasStmt ? " — missing" : ""}`}
                          data-testid={`month-btn-${d.lender.id}-${t.key}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (t.hasStmt) toggleMonth(d.lender.id, t.key);
                          }}
                          className={`flex flex-col items-center gap-0.5 px-1 py-0.5 rounded text-[9px] font-mono border transition-colors ${
                            !t.hasStmt
                              ? "border-dashed border-destructive/40 text-destructive/40 cursor-default"
                              : isSelected
                              ? "border-chart-3/60 bg-chart-3/10 text-chart-3 cursor-pointer"
                              : "border-border text-muted-foreground/50 cursor-pointer"
                          }`}
                        >
                          <span>{MONTH_ABBR[t.month-1]}</span>
                          {t.hasStmt && (
                            <span className={`w-3 h-3 rounded-sm border flex items-center justify-center ${isSelected ? "bg-chart-3 border-chart-3" : "border-muted-foreground/40"}`}>
                              {isSelected && <span className="text-white text-[7px] leading-none">✓</span>}
                            </span>
                          )}
                          {!t.hasStmt && <span className="text-[8px] text-destructive/50">—</span>}
                        </button>
                      );
                    })}
                  </div>
                )}

                {d.apr !== null ? (
                  <div className="space-y-2">
                    {/* Primary APR metrics */}
                    <div className="grid grid-cols-3 gap-1.5">
                      <div className="text-center p-1.5 rounded-lg bg-chart-3/10">
                        <p className="text-xs text-muted-foreground">
                          {d.isFallbackAllUnits
                            ? t("floorplan:summary.borrowRateAllUnits")
                            : d.isEstimate
                              ? t("floorplan:summary.borrowRateEst")
                              : t("floorplan:summary.borrowRate")}
                        </p>
                        <p className="text-base font-bold text-chart-3" data-testid={`real-apr-value-${d.lender.id}`}>{d.apr.toFixed(2)}%</p>
                        {d.isFallbackAllUnits && (
                          <p className="text-[9px] text-amber-600 dark:text-amber-400 mt-0.5">{t("floorplan:summary.estAllUnitsAvg")}</p>
                        )}
                        {!d.isFallbackAllUnits && d.isEstimate && (
                          <p className="text-[9px] text-amber-600 dark:text-amber-400 mt-0.5">{t("floorplan:summary.noOrigAmt")}</p>
                        )}
                        {!d.isEstimate && d.usedCount > 0 && (
                          <p className="text-[9px] text-muted-foreground mt-0.5">{t("floorplan:summary.uncurtailed", { count: d.usedCount })}</p>
                        )}
                      </div>
                      <div className="text-center p-1.5 rounded-lg bg-muted/40">
                        <p className="text-xs text-muted-foreground">{t("floorplan:summary.intPerUnit")}</p>
                        <p className="text-sm font-bold">{d.avgInterestPerUnit !== null ? fmt$(d.avgInterestPerUnit) : "—"}</p>
                      </div>
                      <div className="text-center p-1.5 rounded-lg bg-muted/40">
                        <p className="text-xs text-muted-foreground">{t("floorplan:summary.feesPerUnit")}</p>
                        <p className="text-sm font-bold">{d.avgFeesPerUnit !== null ? fmt$(d.avgFeesPerUnit) : "—"}</p>
                      </div>
                    </div>

                    {/* Per-unit APR breakdown when receivables exist */}
                    {d.chargeRecvs.length > 0 && (
                      <div className="rounded-lg border bg-muted/20 overflow-hidden">
                        <div className="px-2 py-1 bg-muted/40 border-b">
                          <p className="text-xs font-medium text-muted-foreground">
                            {t("floorplan:summary.intRatePerVehiclePre")}{t("floorplan:summary.unitCount", { count: d.costRecvs.length })}
                            {d.costRecvs.length > d.chargeRecvs.length ? t("floorplan:summary.missingDays", { count: d.costRecvs.length - d.chargeRecvs.length }) : ""})
                          </p>
                        </div>
                        <div className="max-h-28 overflow-y-auto">
                          <table className="w-full text-xs">
                            {(() => {
                              const hasCpp = d.chargeRecvs.some((r: any) => (r.collateralFee ?? 0) > 0);
                              return (
                              <thead className="sticky top-0 bg-muted/40">
                                <tr>
                                  <th className="text-left py-1 px-2 text-muted-foreground font-medium">{t("floorplan:upload.th.vinStock")}</th>
                                  <th className="text-right py-1 px-2 text-muted-foreground font-medium">{t("floorplan:upload.th.days")}</th>
                                  <th className="text-right py-1 px-2 text-muted-foreground font-medium">
                                    {t("floorplan:summary.fees")}{hasCpp && <span className="block text-[9px] font-normal opacity-60">{t("floorplan:upload.th.exclCpp")}</span>}
                                  </th>
                                  {hasCpp && <th className="text-right py-1 px-2 text-muted-foreground font-medium">{t("floorplan:upload.th.cpp")}</th>}
                                  <th className="text-right py-1 px-2 text-muted-foreground font-medium">{t("floorplan:summary.intRatePct")}</th>
                                </tr>
                              </thead>
                              );
                            })()}
                            <tbody>
                              {(() => {
                                const hasCpp = d.chargeRecvs.some((r: any) => (r.collateralFee ?? 0) > 0);
                                return d.chargeRecvs.map((r: any, ri: number) => {
                                  const interest = r.interest ?? 0;
                                  const cpp = r.collateralFee ?? 0;
                                  const fees = (r.fees ?? 0) - cpp;
                                  const unitApr = r.principal > 0 && r.daysOutstanding > 0
                                    ? (interest / r.principal) * (365 / r.daysOutstanding) * 100
                                    : null;
                                  return (
                                    <tr key={ri} className="border-t border-border/40 hover:bg-muted/20">
                                      <td className="py-1 px-2 font-mono truncate max-w-[90px]">{r.vin ? r.vin.slice(-8) : r.stock ?? "—"}</td>
                                      <td className="py-1 px-2 text-right">{r.daysOutstanding}d</td>
                                      <td className="py-1 px-2 text-right">{fees > 0 ? fmt$(fees) : "—"}</td>
                                      {hasCpp && <td className="py-1 px-2 text-right text-amber-600 dark:text-amber-400">{cpp > 0 ? fmt$(cpp) : "—"}</td>}
                                      <td className={`py-1 px-2 text-right font-semibold ${unitApr !== null && unitApr > 20 ? "text-destructive" : "text-chart-3"}`}>
                                        {unitApr !== null ? `${unitApr.toFixed(1)}%` : "—"}
                                      </td>
                                    </tr>
                                  );
                                });
                              })()}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    <p className="text-xs text-muted-foreground/40 text-center pt-0.5">{t("floorplan:summary.clickForFullAnalytics")}</p>
                  </div>
                ) : d.extractionFailed ? (
                  <div className="space-y-2">
                    <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-2.5">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-3.5 h-3.5 text-destructive shrink-0 mt-0.5" />
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-destructive">{t("floorplan:summary.extractionFailed")}</p>
                          <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                            {t("floorplan:summary.noDataFound1")}
                            {d.latestFileName ? ` "${d.latestFileName}"` : ` ${t("floorplan:summary.noDataLatest")}`}.
                            {" "}{t("floorplan:summary.noDataFound2")}
                          </p>
                        </div>
                      </div>
                    </div>
                    <Button
                      size="sm" variant="outline" className="w-full h-6 text-xs"
                      onClick={(e) => { e.stopPropagation(); onUpload(d.lender); }}
                      data-testid={`button-reupload-real-${d.lender.id}`}
                    >
                      <Upload className="w-3 h-3 mr-1" />{t("floorplan:summary.reuploadStatement")}
                    </Button>
                  </div>
                ) : d.hasStatement && d.qualRecvs.length > 0 ? (
                  /* Statement uploaded but no APR computable (e.g. Westlake — no Days column) */
                  <div className="space-y-2">
                    <div className="grid grid-cols-3 gap-1.5">
                      <div className="text-center p-1.5 rounded-lg bg-muted/40">
                        <p className="text-xs text-muted-foreground">{t("floorplan:summary.units")}</p>
                        <p className="text-sm font-bold">{d.qualRecvs.length}</p>
                      </div>
                      <div className="text-center p-1.5 rounded-lg bg-muted/40">
                        <p className="text-xs text-muted-foreground">{t("floorplan:summary.interest")}</p>
                        <p className="text-sm font-bold">{fmt$(d.qualRecvs.reduce((s: number, r: any) => s + (r.interest ?? 0), 0))}</p>
                      </div>
                      <div className="text-center p-1.5 rounded-lg bg-muted/40">
                        <p className="text-xs text-muted-foreground">{t("floorplan:summary.fees")}</p>
                        <p className="text-sm font-bold">{fmt$(d.qualRecvs.reduce((s: number, r: any) => s + (r.fees ?? 0), 0))}</p>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground/60 text-center leading-snug">{t("floorplan:summary.aprUnavailable")}</p>
                    <p className="text-xs text-muted-foreground/40 text-center pt-0.5">{t("floorplan:summary.clickForFullAnalytics")}</p>
                  </div>
                ) : (
                  <div className="space-y-1.5 py-0.5">
                    <p className="text-xs text-muted-foreground">{t("floorplan:summary.noStatement")}</p>
                    <div className="flex gap-1.5">
                      <Button
                        size="sm" variant="outline" className="flex-1 h-6 text-xs"
                        onClick={(e) => { e.stopPropagation(); onUpload(d.lender); }}
                        data-testid={`button-upload-real-${d.lender.id}`}
                      >
                        <Upload className="w-3 h-3 mr-1" />{t("floorplan:summary.upload")}
                      </Button>
                      <Button
                        size="sm" variant="ghost" className="flex-1 h-6 text-xs opacity-60 hover:opacity-100"
                        onClick={(e) => { e.stopPropagation(); setLinkTarget(d.lender); }}
                        data-testid={`button-link-existing-${d.lender.id}`}
                      >
                        <FileText className="w-3 h-3 mr-1" />{t("floorplan:summary.linkExisting")}
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
      {linkTarget && <LinkStatementDialog lender={linkTarget} onClose={() => setLinkTarget(null)} />}
    </div>
  );
}

// ─── Lender Card (Compare view — projected scenario only) ────────────────────

type ComparisonStat = {
  id: string;
  unitCount: number;
  realInterestRatePct: number | null;
  avgFeePerUnit: number | null;
  isEstimate: boolean;
  insufficientData: boolean;
};

function LenderCard({
  lender, scenario, rank, isFirst, isLast, onEdit, onDelete, onClickAnalytics,
  allStatements, cheapestCost, secondCheapestCost, allLenders = [], comparisonStats,
}: {
  lender: Lender;
  scenario: ScenarioInputs;
  rank: number;
  isFirst: boolean;
  isLast: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onClickAnalytics: () => void;
  allStatements: any[];
  cheapestCost: number | null;
  secondCheapestCost: number | null;
  allLenders?: Lender[];
  comparisonStats?: ComparisonStat[];
}) {
  const { t } = useTranslation(["floorplan", "common"]);
  const hasTerms = lenderHasTerms(lender);
  const storedTerms = hasTerms ? dbLenderToTerms(lender) : defaultTerms();
  const scenarioWithUnits = { ...scenario, avgUnitsPerMonth: n(lender.avgUnitsPerMonth, scenario.avgUnitsPerMonth) };

  const lenderStmts = stmtsForLender(allStatements, lender, allLenders);
  const realApr = computeRealAprForLender(lenderStmts, { manualFeePerUnit: manualFeePerUnitFromLender(lender) });
  const hasStatementData = realApr.stmtCount > 0;

  const { latestStmtParserDiscrepancy, latestStmtPipelineEvents } = (() => {
    const stmt = lenderStmts[0];
    if (!stmt?.extractionDiagnosticsJson) return { latestStmtParserDiscrepancy: null, latestStmtPipelineEvents: [] };
    try {
      const diag = JSON.parse(stmt.extractionDiagnosticsJson);
      return {
        latestStmtParserDiscrepancy: diag?.parserDiscrepancy ?? null,
        latestStmtPipelineEvents: (diag?.pipelineEvents ?? []) as { step: string; outcome: string; detail?: string; elapsedMs?: number }[],
      };
    } catch { return { latestStmtParserDiscrepancy: null, latestStmtPipelineEvents: [] }; }
  })();

  const timedEvents = latestStmtPipelineEvents.filter((e) => e.elapsedMs != null);

  // For tiered-pricing lenders, find which price tier the scenario vehicle value falls into
  // so we can scope the receivables fee average to that tier's vehicle value band.
  const lenderCardTiers = ((lender as any).tiers ?? [])
    .slice()
    .sort((a: any, b: any) => n(b.threshold) - n(a.threshold));
  const lenderCardMatchedTier = lenderCardTiers.find((t: any) => scenario.vehicleValue >= n(t.threshold)) ?? null;
  let lcTierValueMin: number | undefined;
  let lcTierValueMax: number | undefined;
  if (lenderCardMatchedTier) {
    lcTierValueMin = n(lenderCardMatchedTier.threshold);
    // sortedTiers is sorted descending; the immediate next higher tier is the
    // one with the smallest threshold that is still > matched — i.e. the last
    // element after filtering (not the first, which would be the highest).
    const greaterTiers = lenderCardTiers.filter((t: any) => n(t.threshold) > lcTierValueMin!);
    const nextHigher = greaterTiers.length > 0 ? greaterTiers[greaterTiers.length - 1] : null;
    lcTierValueMax = nextHigher ? n(nextHigher.threshold) : undefined;
  }

  // Derive fees from actual receivables by aging bucket (shared utility, same logic as comparison-stats).
  // For tiered lenders, scope to receivables whose principal falls within the matched tier's price band.
  const receivablesDerivedFee = deriveReceivablesFeeForScenario(lenderStmts, lender, scenario.daysOnFloor, lcTierValueMin, lcTierValueMax);

  // Look up this lender's server-computed stats (realInterestRatePct / avgFeePerUnit) from the
  // comparison-stats response, which uses computeTrueAprFromReceivables — curtailment-filtered,
  // outlier-removed, and turn-day-aware for fees. This is the same source as Lender Overviews.
  const compStat = comparisonStats?.find((s) => s.id === lender.id) ?? null;

  // APR sourcing hierarchy:
  //   1. comparison-stats realInterestRatePct (server-side, curtailment-filtered + outlier-removed)
  //   2. client-side realApr.interestOnlyApr (legacy client computation)
  //   3. manually entered stated APR
  const effectiveAprRate = compStat?.realInterestRatePct ?? realApr.interestOnlyApr ?? (storedTerms.aprRate > 0 ? storedTerms.aprRate : 0);
  const aprSourceLabel = compStat?.realInterestRatePct != null
    ? (compStat.isEstimate ? t("floorplan:analyticsDialog.aprEstSuffix") : t("floorplan:analyticsDialog.aprTrueSuffix"))
    : realApr.interestOnlyApr !== null
      ? (realApr.isEstimate ? t("floorplan:analyticsDialog.aprEstSuffix") : t("floorplan:analyticsDialog.aprTrueSuffix"))
      : t("floorplan:analyticsDialog.aprStatedSuffix");

  const effectiveTerms = { ...storedTerms, aprRate: effectiveAprRate };
  const rawBdResult = hasTerms ? computeCostBreakdownWithTiers(effectiveTerms, dbTiersToTierConfigs((lender as any).tiers ?? []), scenarioWithUnits) : null;
  const rawBd = rawBdResult?.breakdown ?? null;
  const isFlexTierCard = rawBdResult?.isFlexTier ?? false;

  // Fee sourcing hierarchy:
  //   1. comparison-stats avgFeePerUnit (server-side, turn-day-aware, same source as Lender Overviews)
  //      — only trusted when there is at least one real receivable row behind it (unitCount > 0),
  //        because the server always emits 0 for lenders with no uploaded statements
  //   2. receivables-derived fee (client-side aging-bucket derivation)
  //   3. manually entered fee schedule (fallback when no statement data)
  const hasCompStatFee = (compStat?.unitCount ?? 0) > 0 && compStat?.avgFeePerUnit != null;
  const hasReceivablesFeeData = hasCompStatFee || receivablesDerivedFee !== null;
  // Show manual-terms callout whenever we had to fall back to manually entered fee schedule
  // (either no statement at all, or statement present but no matching/qualifying receivables)
  const usingManualTerms = !hasReceivablesFeeData;

  let effectiveFee: number;
  if (hasCompStatFee) {
    // Primary: server-computed avg fee per unit from comparison-stats (turn-day-aware)
    effectiveFee = compStat!.avgFeePerUnit!;
  } else if (receivablesDerivedFee !== null) {
    // Secondary: fees derived from actual receivables by aging bucket
    effectiveFee = receivablesDerivedFee;
  } else if (rawBd) {
    // Fallback: fee schedule from manually entered lender terms
    effectiveFee = rawBd.feesTotal;
  } else {
    effectiveFee = 0;
  }

  // Proj. APR = (Est. Cost/Unit / Vehicle Value) × (365 / Days on Floor).
  // Using the scenario's vehicle value (not avgBalancePerUnit from the model) keeps
  // the displayed APR directly consistent with the Est. Cost/Unit shown on the card.
  const bdTrueCost = rawBd ? rawBd.interestTotal + effectiveFee : 0;
  const bdEffectiveAprPct = scenario.vehicleValue > 0 && scenario.daysOnFloor > 0
    ? (bdTrueCost / scenario.vehicleValue) * (365 / scenario.daysOnFloor) * 100
    : rawBd?.effectiveAprPct ?? 0;
  const bd = rawBd
    ? { ...rawBd, feesTotal: effectiveFee, trueCostPerUnit: bdTrueCost, effectiveAprPct: bdEffectiveAprPct }
    : null;

  // Net diff vs cheapest lender
  const netDiff = bd && cheapestCost !== null ? bd.trueCostPerUnit - cheapestCost : null;
  const isCheapest = netDiff !== null && netDiff < 0.01;

  const rankColor = isFirst
    ? "text-chart-3 bg-chart-3/10 border-chart-3/30"
    : isLast && !isFirst
      ? "text-destructive bg-destructive/10 border-destructive/30"
      : "text-muted-foreground bg-muted/40 border-border";

  return (
    <Card
      className={`relative overflow-hidden cursor-pointer transition-all hover:shadow-md hover:border-primary/30 select-none ${isFirst ? "ring-1 ring-chart-3/40" : ""}`}
      onClick={onClickAnalytics}
      data-testid={`lender-card-${lender.id}`}
    >
      {/* Corner action buttons */}
      <div className="absolute top-2 right-2 flex gap-0.5 z-10">
        <Button
          size="icon" variant="ghost" className="h-6 w-6 opacity-40 hover:opacity-100 transition-opacity"
          onClick={(e) => { e.stopPropagation(); onEdit(); }}
          data-testid={`button-edit-${lender.id}`}
          title={t("floorplan:lenderCard.editTerms")}
        >
          <Edit className="w-3 h-3" />
        </Button>
        <Button
          size="icon" variant="ghost" className="h-6 w-6 opacity-40 hover:opacity-100 hover:text-destructive transition-opacity"
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          data-testid={`button-delete-${lender.id}`}
          title={t("floorplan:lenderCard.deleteLender")}
        >
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>

      <CardContent className="p-4 space-y-3">
        {/* Rank + name */}
        <div className="flex items-center gap-2 pr-14">
          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border shrink-0 ${rankColor}`}>
            {rank}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <p className="font-semibold text-sm leading-tight truncate">{lender.name}</p>
              {Array.isArray((lender as any).tiers) && (lender as any).tiers.length > 0 && (
                <Badge className="text-xs px-1 py-0 h-4 shrink-0">{(lender as any).tiers.length} {t("floorplan:lenderCard.tier", { count: (lender as any).tiers.length })}</Badge>
              )}
              {latestStmtParserDiscrepancy && (
                <UITooltip>
                  <TooltipTrigger asChild>
                    <Badge
                      variant="outline"
                      className="text-xs px-1 py-0 h-4 shrink-0 border-amber-400 text-amber-600 bg-amber-50 dark:bg-amber-950/30 dark:text-amber-400 gap-0.5 cursor-help"
                      data-testid={`badge-statement-quality-${lender.id}`}
                    >
                      <AlertTriangle className="w-2.5 h-2.5" />
                      {t("floorplan:lenderCard.quality")}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-xs">
                    {t("floorplan:lenderCard.qualityTooltip", { aiCount: latestStmtParserDiscrepancy.aiCount, regexCount: latestStmtParserDiscrepancy.regexCount })}
                  </TooltipContent>
                </UITooltip>
              )}
            </div>
            {isFirst && hasTerms && <p className="text-xs text-chart-3">{t("floorplan:lenderCard.cheapestOption")}</p>}
          </div>
        </div>

        {!hasTerms ? (
          <div className="py-3 text-center">
            <p className="text-xs text-muted-foreground">{t("floorplan:lenderCard.noTermsYet")}</p>
            <p className="text-xs text-muted-foreground/50 mt-1">{t("floorplan:lenderCard.clickToOpen")}</p>
          </div>
        ) : bd ? (
          <>
            {/* Cost first, then APR, then net diff */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div className="text-center p-2 rounded-lg bg-primary/10">
                <p className="text-xs text-muted-foreground">{t("floorplan:lenderCard.estCostUnit")}</p>
                <p className="text-base font-bold text-primary" data-testid={`cost-unit-${lender.id}`}>{fmt$(bd.trueCostPerUnit, 2)}</p>
                <p className="text-xs text-muted-foreground/60">{scenario.daysOnFloor}d</p>
              </div>
              <div className="text-center p-2 rounded-lg bg-muted/40">
                <UITooltip>
                  <TooltipTrigger asChild>
                    <p className="text-xs text-muted-foreground cursor-help inline-flex items-center gap-0.5">{t("floorplan:lenderCard.projApr")} <Info className="w-3 h-3 opacity-60" /></p>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-xs">
                    {t("floorplan:lenderCard.projAprTooltip")}
                  </TooltipContent>
                </UITooltip>
                <p className="text-sm font-bold" data-testid={`proj-apr-${lender.id}`}>{fmtPct(bd.effectiveAprPct)}</p>
                <p className="text-xs text-muted-foreground/60">{t("floorplan:lenderCard.scenario")}</p>
              </div>
              <div className={`text-center p-2 rounded-lg ${isCheapest ? "bg-chart-3/10" : "bg-muted/40"}`}>
                <p className="text-xs text-muted-foreground">{isCheapest ? t("floorplan:lenderCard.savings") : t("floorplan:lenderCard.vsCheapest")}</p>
                {isCheapest ? (
                  secondCheapestCost !== null ? (
                    <p className="text-sm font-bold text-chart-3" data-testid={`savings-${lender.id}`}>
                      {fmt$(secondCheapestCost - (cheapestCost ?? 0), 0)}
                    </p>
                  ) : (
                    <p className="text-sm font-bold text-chart-3">—</p>
                  )
                ) : netDiff !== null ? (
                  <p className="text-sm font-bold text-destructive" data-testid={`net-diff-${lender.id}`}>+{fmt$(netDiff, 0)}</p>
                ) : (
                  <p className="text-sm font-bold">—</p>
                )}
                <p className="text-xs text-muted-foreground/60">{isCheapest ? t("floorplan:lenderCard.vsSecondCheapest") : t("floorplan:lenderCard.extra")}</p>
              </div>
            </div>

            {usingManualTerms && (
              <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/50" data-testid={`callout-manual-terms-${lender.id}`}>
                <span className="text-amber-600 dark:text-amber-400 text-xs font-medium leading-snug">
                  {hasStatementData
                    ? t("floorplan:lenderCard.manualNoReceivables")
                    : t("floorplan:lenderCard.manualNoStatement")}
                </span>
              </div>
            )}

            <p className="text-xs text-muted-foreground/40 text-center">{t("floorplan:lenderCard.clickForAnalytics")}</p>
          </>
        ) : null}

      </CardContent>
    </Card>
  );
}

// ─── Latest Statement Chat wrapper ────────────────────────────────────────────
// Isolated component so it can parse receivablesJson without an IIFE in JSX.

function LatestStmtChat({
  latestStmt, dealerName, lenderName,
}: {
  latestStmt: any | null;
  dealerName?: string;
  lenderName?: string;
}) {
  if (!latestStmt) return null;
  let recvs: any[] = [];
  try { recvs = JSON.parse(latestStmt.receivablesJson ?? "[]"); } catch {}
  const qualRecvs = recvs.filter((r: any) => (r.principal ?? 0) > 0);
  if (qualRecvs.length === 0) return null;
  return (
    <ReceivableChat
      statementId={latestStmt.id}
      dealerName={dealerName}
      lenderName={latestStmt.lenderName ?? lenderName}
    />
  );
}

// ─── Lender Analytics Dialog ──────────────────────────────────────────────────

type EditableRecvRow = {
  id: number;
  vin: string;
  stock: string;
  principal: string;
  days: string;
  interest: string;
  fees: string;
  cpp: string;
  _orig: any;
};

function LenderAnalyticsDialog({
  lender, scenario, mode, onClose, onEdit, onUpload,
}: {
  lender: Lender | null;
  scenario: ScenarioInputs;
  mode: "real" | "scenario";
  onClose: () => void;
  onEdit: () => void;
  onUpload: () => void;
}) {
  const { t } = useTranslation(["floorplan", "common"]);
  const { data: allStatements = [] } = useQuery<any[]>({ queryKey: ["/api/parsed-statements"] });
  const { data: currentUser } = useQuery<any>({ queryKey: ["/api/auth/me"] });
  const { data: compData } = useQuery<{ lenders: ComparisonStat[] }>({
    queryKey: [`/api/lenders/comparison-stats?turnDays=${scenario.daysOnFloor}`],
    enabled: !!lender,
    staleTime: 30_000,
  });
  // Track the statement the user has selected for Per-Unit Breakdown and chat.
  // `null` means "use the latest statement" — it resets when the lender changes.
  const [selectedStmtId, setSelectedStmtId] = useState<string | null>(null);
  const recvQc = useQueryClient();
  const { toast: recvToast } = useToast();
  const [editingRecv, setEditingRecv] = useState(false);
  const [savingRecv, setSavingRecv] = useState(false);
  const [recvRows, setRecvRows] = useState<EditableRecvRow[]>([]);
  const nextRecvId = useRef(0);
  if (!lender) return null;
  const compStat = compData?.lenders.find((s) => s.id === lender.id) ?? null;

  const hasTerms = lenderHasTerms(lender);
  const terms = hasTerms ? dbLenderToTerms(lender) : null;
  const scenarioWithUnits = { ...scenario, avgUnitsPerMonth: n(lender.avgUnitsPerMonth, scenario.avgUnitsPerMonth) };
  const stmts = stmtsForLender(allStatements, lender);
  const realApr = computeRealAprForLender(stmts, { manualFeePerUnit: manualFeePerUnitFromLender(lender) });

  // Most recent statement for this lender — used for the PDF link and chat default
  const latestStmt = stmts[0] ?? null;

  // Active statement: user-selected or fall back to latest
  const activeStmt = (selectedStmtId ? stmts.find((s) => s.id === selectedStmtId) : null) ?? latestStmt;

  // Gather qualifying per-VIN receivables from the active statement only
  // so the Per-Unit Breakdown and chatbot share the exact same dataset.
  const allRecvs: any[] = [];
  try { allRecvs.push(...JSON.parse(activeStmt?.receivablesJson ?? "[]")); } catch {}
  // All vehicles with a positive principal — includes newly floored (0 or null days)
  // Used for the per-unit breakdown count and table so no vehicle is ever silently dropped
  const qualRecvs = allRecvs.filter((r) => (r.principal ?? 0) > 0);
  // All vehicles with real charges regardless of days — for the vehicle count display
  const costRecvs = qualRecvs.filter((r) => ((r.interest ?? 0) + (r.fees ?? 0)) > 0);
  // Subset with positive days AND actual charges — used for APR / cost analytics
  // (APR formula divides by days, so days must be > 0; cost avgs need real charges)
  const chargeRecvs = qualRecvs.filter(
    (r) => (r.daysOutstanding ?? 0) > 0 && ((r.interest ?? 0) + (r.fees ?? 0)) > 0
  );
  const missingDaysCount = costRecvs.filter((r) => r.daysOutstanding == null).length;

  // Summary stats from receivables for the dialog subtitle.
  // Use chargeRecvs (days > 0 AND charges > 0) so the count and averages stay
  // consistent with every cost-analytics figure shown below.
  const avgDaysRecvs = chargeRecvs.length > 0
    ? Math.round(chargeRecvs.reduce((s: number, r: any) => s + (r.daysOutstanding ?? 0), 0) / chargeRecvs.length)
    : null;
  const avgPrincipalRecvs = chargeRecvs.length > 0
    ? chargeRecvs.reduce((s: number, r: any) => s + (r.principal ?? 0), 0) / chargeRecvs.length
    : null;

  // APR sourcing hierarchy (same as LenderCard):
  //   1. comparison-stats realInterestRatePct (server-side, curtailment-filtered + outlier-removed)
  //   2. client-side realApr.interestOnlyApr (legacy client computation)
  //   3. manually entered stated APR
  const effectiveAprRate = compStat?.realInterestRatePct ?? realApr.interestOnlyApr ?? (n(lender.aprRate) > 0 ? n(lender.aprRate) : 0);
  const aprSourceLabel = compStat?.realInterestRatePct != null
    ? (compStat.isEstimate ? t("floorplan:analyticsDialog.aprEstSuffix") : t("floorplan:analyticsDialog.aprTrueSuffix"))
    : realApr.interestOnlyApr !== null
      ? (realApr.isEstimate ? t("floorplan:analyticsDialog.aprEstSuffix") : t("floorplan:analyticsDialog.aprTrueSuffix"))
      : t("floorplan:analyticsDialog.aprStatedSuffix");
  const effectiveTerms = terms ? { ...terms, aprRate: effectiveAprRate } : null;
  const effectiveBdResult = effectiveTerms ? computeCostBreakdownWithTiers(effectiveTerms, dbTiersToTierConfigs((lender as any).tiers ?? []), scenarioWithUnits) : null;
  const effectiveBd = effectiveBdResult?.breakdown ?? null;
  const isFlexTierDialog = effectiveBdResult?.isFlexTier ?? false;
  const sensitivity = effectiveTerms ? computeTurnSensitivityModel(effectiveTerms, scenarioWithUnits) : [];
  const { T2, T3 } = effectiveTerms ? computeThresholds(effectiveTerms) : { T2: 0, T3: 0 };

  // Fees are "unknown" when all fee periods are null or zero — period days may be set but
  // the fee schedule was not extracted from the statement (or was never entered).
  const feesUnknown =
    (lender.feePeriod1 == null || n(lender.feePeriod1) === 0) &&
    (lender.feePeriod2 == null || n(lender.feePeriod2) === 0) &&
    (lender.feePeriod3 == null || n(lender.feePeriod3) === 0);

  // When fees are unknown, substitute avg fees/unit from comparison-stats (primary) or actual receivables (secondary)
  const fallbackFeesPerUnit = feesUnknown ? (compStat?.avgFeePerUnit ?? realApr.avgFeesPerUnit ?? null) : null;

  const hasRealData = realApr.stmtCount > 0;

  // ─── Editable per-unit receivables ────────────────────────────────────────
  // Lets the dealer correct mis-extracted numbers, add, and delete rows. On save
  // the statement's receivablesJson and totals are persisted, then every query
  // that feeds the analytics math is invalidated so all derived figures recompute.
  const enterRecvEdit = () => {
    const rows: EditableRecvRow[] = allRecvs.map((r: any) => {
      const cpp = r.collateralFee ?? 0;
      return {
        id: nextRecvId.current++,
        vin: r.vin ?? "",
        stock: r.stock ?? "",
        principal: r.principal != null ? String(r.principal) : "",
        days: r.daysOutstanding != null ? String(r.daysOutstanding) : "",
        interest: r.interest != null ? String(r.interest) : "",
        fees: r.fees != null ? String(Math.round(((r.fees ?? 0) - cpp) * 100) / 100) : "",
        cpp: cpp ? String(cpp) : "",
        _orig: r,
      };
    });
    setRecvRows(rows);
    setEditingRecv(true);
  };
  const cancelRecvEdit = () => { setEditingRecv(false); setRecvRows([]); };
  // Comma-aware money parsing (shared); display formatting handled by FormattedNumberInput.
  const parseRecvNum = parseMoneyNumber;
  const editRecvCell = (id: number, field: keyof EditableRecvRow, value: string) =>
    setRecvRows((rows) => rows.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
  const deleteRecvRow = (id: number) =>
    setRecvRows((rows) => rows.filter((r) => r.id !== id));
  const addRecvRow = () =>
    setRecvRows((rows) => [...rows, { id: nextRecvId.current++, vin: "", stock: "", principal: "", days: "", interest: "", fees: "", cpp: "", _orig: {} }]);
  const saveRecvEdit = async () => {
    if (!activeStmt?.id) return;
    setSavingRecv(true);
    try {
      const serialized = recvRows.map((row) => {
        const cpp = parseRecvNum(row.cpp) || 0;
        const feesExcl = parseRecvNum(row.fees) || 0;
        return {
          ...(row._orig ?? {}),
          vin: row.vin.trim() || null,
          stock: row.stock.trim() || null,
          principal: parseRecvNum(row.principal) || 0,
          daysOutstanding: parseInt(row.days) || 0,
          interest: parseRecvNum(row.interest) || 0,
          fees: Math.round((feesExcl + cpp) * 100) / 100,
          collateralFee: cpp,
        };
      });
      const totalInterestPaid = serialized.reduce((s, r) => s + (r.interest || 0), 0).toFixed(2);
      const totalFeesPaid = serialized.reduce((s, r) => s + (r.fees || 0), 0).toFixed(2);
      await apiRequest("PATCH", `/api/parsed-statements/${activeStmt.id}`, {
        receivablesJson: JSON.stringify(serialized),
        totalInterestPaid,
        totalFeesPaid,
      });
      await recvQc.invalidateQueries({ queryKey: ["/api/parsed-statements"] });
      recvQc.invalidateQueries({ queryKey: ["/api/floorplan-inventory"] });
      recvQc.invalidateQueries({ queryKey: ["/api/inventory/enriched"] });
      recvQc.invalidateQueries({ queryKey: ["/api/lenders"] });
      recvQc.invalidateQueries({ predicate: (q) => typeof q.queryKey[0] === "string" && q.queryKey[0].startsWith("/api/lenders/comparison-stats") });
      recvQc.invalidateQueries({ queryKey: ["/api/savings/overview"] });
      setEditingRecv(false);
      setRecvRows([]);
      recvToast({ title: t("floorplan:toast.receivablesUpdated"), description: t("floorplan:toast.receivablesUpdatedDesc") });
    } catch (e: any) {
      recvToast({ title: t("floorplan:toast.saveFailed"), description: e?.message ?? t("floorplan:toast.couldNotSave"), variant: "destructive" });
    } finally {
      setSavingRecv(false);
    }
  };

  // ── Fee Period Analysis ─────────────────────────────────────────────────────
  // For each defined period, compute:
  //   statedFee — cumulative fees a vehicle has paid by the time it reaches that period
  //   actualFee — avg actual fees from receivables for vehicles in that day range
  // Period 2 and 3 stated fees are cumulative (P1, P1+P2, P1+P2+P3) because a vehicle in
  // period 2 has already paid period 1's fee.
  const feePeriodRows = (() => {
    const p1d = n(lender.period1Days, 0);
    const p2d = n(lender.period2Days, 0);
    const p3d = n(lender.period3Days, 0);
    const p4d = n(lender.period4Days, 0);
    if (p1d === 0) return null;

    const fp1 = n(lender.feePeriod1, 0);
    const fp2 = n(lender.feePeriod2, 0);
    const fp3 = n(lender.feePeriod3, 0);
    const fp4 = n(lender.feePeriod4, 0);

    const cumFees = [fp1, fp1 + fp2, fp1 + fp2 + fp3, fp1 + fp2 + fp3 + fp4];
    const perFees = [fp1, fp2, fp3, fp4];
    const periodDaysList = [p1d, p2d, p3d, p4d];

    const numPeriods = p4d > 0 ? 4 : p3d > 0 ? 3 : p2d > 0 ? 2 : 1;

    const boundaries: number[] = [0];
    for (let i = 0; i < numPeriods; i++) boundaries.push(boundaries[i] + periodDaysList[i]);

    const buckAvg = (minD: number, maxD: number | null) => {
      const arr = chargeRecvs.filter((r: any) =>
        (r.daysOutstanding ?? 0) >= minD && (maxD === null || (r.daysOutstanding ?? 0) < maxD)
      );
      const floorFeesOnly = (r: any) => (r.fees ?? 0) - (r.collateralFee ?? 0);
      return {
        count: arr.length,
        avg: arr.length > 0 ? arr.reduce((s: number, r: any) => s + floorFeesOnly(r), 0) / arr.length : null,
        avgCollateral: arr.length > 0 ? arr.reduce((s: number, r: any) => s + (r.collateralFee ?? 0), 0) / arr.length : null,
      };
    };

    return Array.from({ length: numPeriods }, (_, i) => {
      const minD = boundaries[i];
      const maxD = i === numPeriods - 1 ? null : boundaries[i + 1];
      const { count, avg, avgCollateral } = buckAvg(minD, maxD);
      return {
        num: i + 1,
        label: t("floorplan:termsForm.periodN", { num: i + 1 }),
        range: maxD !== null ? t("floorplan:analyticsDialog.dayRangeBetween", { min: minD, max: maxD }) : t("floorplan:analyticsDialog.dayRangePlus", { min: minD }),
        statedFee: cumFees[i],
        perFee: perFees[i],
        actualFee: avg,
        avgCollateral,
        count,
      };
    });
  })();

  return (
    <Dialog open={!!lender} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <DialogTitle className="text-xl">{lender.name}</DialogTitle>
              <DialogDescription>
                {hasTerms
                  ? t("floorplan:analyticsDialog.termsLine", { p1: lender.period1Days, p2: lender.period2Days, p3: lender.period3Days, rate: effectiveAprRate.toFixed(4), source: aprSourceLabel })
                  : t("floorplan:analyticsDialog.noTermsYetClick")}
              </DialogDescription>
            </div>
            <div className="flex gap-2 shrink-0 pt-1 flex-wrap justify-end">
              {latestStmt && (
                <a
                  href={`/api/parsed-statements/${latestStmt.id}/file`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="link-view-statement-pdf"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Button size="sm" variant="ghost" className="text-muted-foreground">
                    <FileText className="w-3.5 h-3.5 mr-1.5" />
                    <span className="max-w-[120px] truncate">{latestStmt.fileName}</span>
                  </Button>
                </a>
              )}
              <Button size="sm" onClick={onEdit} data-testid="button-analytics-edit">
                <Edit className="w-3.5 h-3.5 mr-1.5" />{t("floorplan:analyticsDialog.editTerms")}
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-5 pt-1">

          {/* ── Real statement data — only shown in "real" mode ── */}
          {mode === "real" && hasRealData && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <CheckCircle2 className="w-4 h-4 text-chart-3" />
                <p className="text-sm font-semibold">{t("floorplan:analyticsDialog.realData", { count: (costRecvs.length || chargeRecvs.length || realApr.stmtCount) })}</p>
                <span className="text-xs text-muted-foreground">
                  {avgDaysRecvs != null && avgPrincipalRecvs != null
                    ? t("floorplan:analyticsDialog.avgDaysUnit", { days: avgDaysRecvs, value: fmt$(avgPrincipalRecvs) })
                    : chargeRecvs.length > 0 ? t("floorplan:analyticsDialog.calculatedFromCharges") : t("floorplan:analyticsDialog.fromStatementTotals")}
                </span>
                {missingDaysCount > 0 && (
                  <span className="text-xs text-amber-600 dark:text-amber-400">
                    {t("floorplan:analyticsDialog.missingDaysFix", { count: missingDaysCount })}
                  </span>
                )}
              </div>

              {/* Avg total per unit — prominent */}
              {(realApr.avgInterestPerUnit !== null || realApr.avgFeesPerUnit !== null) && (
                <div className="px-4 py-3 rounded-xl bg-chart-3/10 border border-chart-3/20 text-center">
                  <p className="text-xs text-muted-foreground mb-0.5">{t("floorplan:analyticsDialog.avgTotalPaidUnit")}</p>
                  <p className="text-2xl font-bold text-chart-3" data-testid="dialog-real-total-unit">
                    {fmt$((realApr.avgInterestPerUnit ?? 0) + (realApr.avgFeesPerUnit ?? 0), 2)}
                  </p>
                  <div className="flex justify-center gap-6 mt-1.5">
                    <div>
                      <p className="text-[11px] text-muted-foreground">{t("floorplan:analyticsDialog.interestUnit")}</p>
                      <p className="text-base font-semibold text-foreground">{realApr.avgInterestPerUnit !== null ? fmt$(realApr.avgInterestPerUnit, 2) : "—"}</p>
                    </div>
                    <div>
                      <p className="text-[11px] text-muted-foreground">{t("floorplan:analyticsDialog.feesUnit")}</p>
                      <p className="text-base font-semibold text-foreground">{realApr.avgFeesPerUnit !== null ? fmt$(realApr.avgFeesPerUnit, 2) : "—"}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Cost per Day per Unit */}
              {chargeRecvs.length > 0 && (() => {
                const rates = chargeRecvs.map((r: any) => {
                  const days = r.daysOutstanding as number;
                  const interest = (r.interest ?? 0) as number;
                  const fees = ((r.fees ?? 0) - (r.collateralFee ?? 0)) as number;
                  const principal = (r.principal ?? 0) as number;
                  const dailyIntRate = principal > 0 && days > 0 ? (interest / principal / days) * 100 : null;
                  return { total: (interest + fees) / days, fees: fees / days, dailyIntRate };
                });
                const avgTotal = rates.reduce((s, r) => s + r.total, 0) / rates.length;
                const avgFees  = rates.reduce((s, r) => s + r.fees, 0) / rates.length;
                const validRates = rates.filter((r) => r.dailyIntRate !== null);
                const avgDailyIntRate = validRates.length > 0
                  ? validRates.reduce((s, r) => s + r.dailyIntRate!, 0) / validRates.length
                  : null;
                return (
                  <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 text-center space-y-2">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{t("floorplan:analyticsDialog.avgCostPerDayUnit")}</p>
                    <p className="text-3xl font-black text-primary tabular-nums" data-testid="dialog-cost-per-day">
                      {fmt$(avgTotal, 2)}<span className="text-base font-normal text-muted-foreground">{t("floorplan:analyticsDialog.perDay")}</span>
                    </p>
                    <div className="flex justify-center gap-4 pt-1">
                      <div className="text-center">
                        <p className="text-[10px] text-muted-foreground">{t("floorplan:analyticsDialog.effDailyIntRate")}</p>
                        <p className="text-sm font-semibold tabular-nums">
                          {avgDailyIntRate !== null ? `${avgDailyIntRate.toFixed(4)}%` : "—"}
                        </p>
                      </div>
                      <div className="w-px bg-border" />
                      <div className="text-center">
                        <p className="text-[10px] text-muted-foreground">{t("floorplan:analyticsDialog.feesPerDay")}</p>
                        <p className="text-sm font-semibold tabular-nums">{fmt$(avgFees, 2)}</p>
                      </div>
                      <div className="w-px bg-border" />
                      <div className="text-center">
                        <p className="text-[10px] text-muted-foreground">{t("floorplan:analyticsDialog.vehicles")}</p>
                        <p className="text-sm font-semibold tabular-nums">{chargeRecvs.length}</p>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* APR metrics */}
              {(realApr.insufficientData || realApr.noInterestAccrued) && (
                <div className="px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 dark:bg-amber-950/20 dark:border-amber-800 text-xs text-amber-700 dark:text-amber-400" data-testid="apr-warning-banner">
                  {realApr.noInterestAccrued
                    ? t("floorplan:analyticsDialog.noInterestAccrued")
                    : t("floorplan:analyticsDialog.insufficientUncurtailed", { count: realApr.usedCount })}
                </div>
              )}
              {realApr.isZeroInterest && (
                <div className="px-3 py-2 rounded-lg bg-sky-50 border border-sky-200 dark:bg-sky-950/20 dark:border-sky-800 text-xs text-sky-700 dark:text-sky-400" data-testid="zero-interest-banner">
                  {t("floorplan:analyticsDialog.zeroInterest")}
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <div className="p-3 rounded-xl bg-muted/40 border text-center">
                  <p className="text-xs text-muted-foreground mb-1">
                    {realApr.isFallbackAllUnits
                      ? t("floorplan:analyticsDialog.costToBorrowAllUnits")
                      : realApr.isEstimate
                        ? t("floorplan:analyticsDialog.costToBorrowEst")
                        : realApr.isZeroInterest
                          ? t("floorplan:analyticsDialog.costToBorrowFeesOnly")
                          : t("floorplan:analyticsDialog.costToBorrowFeesInterest")}
                  </p>
                  <p className="text-lg font-bold" data-testid="dialog-real-apr">
                    {realApr.apr !== null ? `${realApr.apr.toFixed(2)}%` : "—"}
                  </p>
                  <p className="text-xs text-muted-foreground/60 mt-0.5">
                    {realApr.isFallbackAllUnits
                      ? t("floorplan:analyticsDialog.estAllUnitsInsufficient")
                      : realApr.isEstimate
                        ? t("floorplan:analyticsDialog.estimatedNoCurtailment")
                        : realApr.usedCount > 0
                          ? (realApr.excludedCount > 0
                              ? t("floorplan:analyticsDialog.uncurtailedExcluded", { used: realApr.usedCount, excluded: realApr.excludedCount })
                              : t("floorplan:analyticsDialog.uncurtailedCount", { used: realApr.usedCount }))
                          : t("floorplan:analyticsDialog.interestFeesWeighted")}
                  </p>
                </div>
                <div className="p-3 rounded-xl bg-muted/40 border text-center">
                  <p className="text-xs text-muted-foreground mb-1">
                    {realApr.isFallbackAllUnits
                      ? t("floorplan:analyticsDialog.intRateAllUnits")
                      : realApr.isEstimate
                        ? t("floorplan:analyticsDialog.intRateEst")
                        : t("floorplan:analyticsDialog.intRateOnly")}
                  </p>
                  <p className="text-lg font-bold" data-testid="dialog-real-int-rate">
                    {realApr.isZeroInterest
                      ? "0.0000%"
                      : realApr.interestOnlyApr !== null
                        ? `${realApr.interestOnlyApr.toFixed(2)}%`
                        : "—"}
                  </p>
                  <p className="text-xs text-muted-foreground/60 mt-0.5">
                    {realApr.isFallbackAllUnits
                      ? t("floorplan:analyticsDialog.avgAcrossRows")
                      : realApr.isEstimate
                        ? t("floorplan:analyticsDialog.noCurtailmentFilter")
                        : t("floorplan:analyticsDialog.outliersRemoved")}
                  </p>
                </div>
              </div>

              {/* ── Fee Period Analysis Table ── */}
              {feePeriodRows && (
                <div className="rounded-lg border overflow-hidden">
                  <div className="px-3 py-2 bg-muted/40 border-b flex items-center gap-2">
                    <p className="text-xs font-medium">{t("floorplan:analyticsDialog.feePeriodAnalysis")}</p>
                    <span className="text-[10px] text-muted-foreground">{t("floorplan:analyticsDialog.feePeriodSuffix")}</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs min-w-[360px]">
                      <thead className="bg-muted/30">
                        <tr>
                          <th className="text-left py-2 px-3 text-muted-foreground font-medium w-[110px]"></th>
                          {feePeriodRows.map((p) => (
                            <th key={p.label} className="text-right py-2 px-3 text-muted-foreground font-medium">
                              {p.label}
                            </th>
                          ))}
                        </tr>
                        <tr className="border-t border-border/30">
                          <td className="py-1.5 px-3 text-[10px] text-muted-foreground/70 font-normal">{t("floorplan:analyticsDialog.dayRange")}</td>
                          {feePeriodRows.map((p) => (
                            <td key={p.label} className="py-1.5 px-3 text-right text-[10px] text-muted-foreground/70">{p.range}</td>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {/* Stated row — cumulative fee total */}
                        <tr className="border-t border-border/40">
                          <td className="py-2 px-3 font-medium text-muted-foreground">
                            {t("floorplan:analyticsDialog.stated")}
                            <span className="block text-[10px] font-normal opacity-60">{t("floorplan:analyticsDialog.cumulative")}</span>
                          </td>
                          {feePeriodRows.map((p) => (
                            <td key={p.label} className="py-2 px-3 text-right font-semibold">
                              {p.statedFee > 0 ? fmt$(p.statedFee) : "—"}
                              {p.perFee > 0 && p.num !== 1 && (
                                <span className="block text-[10px] text-muted-foreground font-normal">
                                  {t("floorplan:analyticsDialog.thisPeriod", { fee: fmt$(p.perFee) })}
                                </span>
                              )}
                            </td>
                          ))}
                        </tr>
                        {/* Actual row — avg from receivables in each day range */}
                        <tr className="border-t border-border/40">
                          <td className="py-2 px-3 font-medium text-muted-foreground">
                            {t("floorplan:analyticsDialog.actualAvg")}
                            <span className="block text-[10px] font-normal opacity-60">{t("floorplan:analyticsDialog.floorFeesOnly")}</span>
                          </td>
                          {feePeriodRows.map((p) => {
                            const diff = p.actualFee !== null && p.statedFee > 0 ? p.actualFee - p.statedFee : null;
                            const color = diff === null ? "" : diff > p.statedFee * 0.05 ? "text-destructive" : diff < -p.statedFee * 0.05 ? "text-chart-3" : "";
                            return (
                              <td key={p.label} className={`py-2 px-3 text-right font-semibold ${color}`}>
                                {p.actualFee !== null ? fmt$(p.actualFee) : "—"}
                                <span className="block text-[10px] font-normal text-muted-foreground">
                                  {p.count > 0 ? t("floorplan:analyticsDialog.unitCount", { count: p.count }) : t("floorplan:analyticsDialog.noDataShort")}
                                </span>
                              </td>
                            );
                          })}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  {/* Collateral protection note */}
                  {(() => {
                    const withCollateral = qualRecvs.filter((r: any) => (r.collateralFee ?? 0) > 0);
                    if (withCollateral.length === 0) return null;
                    const avgColl = withCollateral.reduce((s: number, r: any) => s + (r.collateralFee ?? 0), 0) / withCollateral.length;
                    return (
                      <p className="px-3 py-2 text-xs text-muted-foreground border-t border-border/40">
                        {t("floorplan:analyticsDialog.collateralPre")}<span className="font-semibold text-foreground">{fmt$(avgColl)}</span>{t("floorplan:analyticsDialog.collateralPost", { count: withCollateral.length })}
                      </p>
                    );
                  })()}
                </div>
              )}

              {/* Per-VIN receivables table */}
              {(qualRecvs.length > 0 || editingRecv) && (
                <div className="rounded-lg border overflow-hidden">
                  <div className="px-3 py-2 bg-muted/40 border-b flex items-center justify-between gap-2">
                    <p className="text-xs font-medium text-muted-foreground">{t("floorplan:analytics.perUnitBreakdown", { count: editingRecv ? recvRows.length : qualRecvs.length })}</p>
                    <div className="flex items-center gap-2">
                      {stmts.length > 1 && !editingRecv && (
                        <select
                          className="text-xs h-6 px-1.5 rounded border border-border bg-background text-foreground"
                          value={activeStmt?.id ?? ""}
                          onChange={(e) => setSelectedStmtId(e.target.value || null)}
                          data-testid="select-active-statement"
                        >
                          {stmts.map((s: any, i: number) => (
                            <option key={s.id} value={s.id}>
                              {s.statementDate ? new Date(s.statementDate).toLocaleDateString(intlLocale()) : s.fileName ?? t("floorplan:analytics.statementN", { num: i + 1 })}
                              {i === 0 ? t("floorplan:analytics.latestSuffix") : ""}
                            </option>
                          ))}
                        </select>
                      )}
                      {!editingRecv ? (
                        <Button size="sm" variant="ghost" className="h-6 px-2 text-xs gap-1" onClick={enterRecvEdit} data-testid="button-edit-receivables" title={t("floorplan:analytics.editReceivables")}>
                          <Edit className="w-3 h-3" /> {t("floorplan:analytics.edit")}
                        </Button>
                      ) : (
                        <div className="flex items-center gap-1">
                          <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={cancelRecvEdit} disabled={savingRecv} data-testid="button-cancel-receivables">{t("common:cancel")}</Button>
                          <Button size="sm" className="h-6 px-2 text-xs gap-1" onClick={saveRecvEdit} disabled={savingRecv} data-testid="button-save-receivables">
                            <CheckCircle2 className="w-3 h-3" /> {savingRecv ? t("floorplan:analytics.saving") : t("floorplan:analytics.save")}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                  {editingRecv ? (
                  <div>
                    <div className="max-h-72 overflow-y-auto overflow-x-auto">
                      <table className="w-full text-xs min-w-[620px]">
                        <thead className="sticky top-0 bg-muted/40 z-10">
                          <tr>
                            <th className="text-left py-1.5 px-2 text-muted-foreground font-medium">{t("floorplan:upload.th.vinStock")}</th>
                            <th className="text-right py-1.5 px-2 text-muted-foreground font-medium">{t("floorplan:upload.th.principal")}</th>
                            <th className="text-right py-1.5 px-2 text-muted-foreground font-medium">{t("floorplan:upload.th.days")}</th>
                            <th className="text-right py-1.5 px-2 text-muted-foreground font-medium">{t("floorplan:upload.th.interest")}</th>
                            <th className="text-right py-1.5 px-2 text-muted-foreground font-medium">{t("floorplan:upload.th.fees")}</th>
                            <th className="text-right py-1.5 px-2 text-muted-foreground font-medium">{t("floorplan:upload.th.cpp")}</th>
                            <th className="py-1.5 px-2 w-8"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {recvRows.map((row) => {
                            const idField: "vin" | "stock" = row.vin || !row.stock ? "vin" : "stock";
                            return (
                              <tr key={row.id} className="border-t border-border/40">
                                <td className="py-1 px-2">
                                  <Input value={row[idField]} onChange={(e) => editRecvCell(row.id, idField, e.target.value)} className="h-7 text-xs font-mono" placeholder={t("floorplan:upload.th.vinStock")} data-testid={`input-recv-id-${row.id}`} />
                                </td>
                                <td className="py-1 px-2">
                                  <FormattedNumberInput value={row.principal} onValueChange={(raw) => editRecvCell(row.id, "principal", raw)} className="h-7 text-xs text-right" placeholder="0" testId={`input-recv-principal-${row.id}`} />
                                </td>
                                <td className="py-1 px-2 w-16">
                                  <Input value={row.days} onChange={(e) => editRecvCell(row.id, "days", e.target.value)} inputMode="numeric" className="h-7 text-xs text-right" placeholder="0" data-testid={`input-recv-days-${row.id}`} />
                                </td>
                                <td className="py-1 px-2">
                                  <FormattedNumberInput value={row.interest} onValueChange={(raw) => editRecvCell(row.id, "interest", raw)} className="h-7 text-xs text-right" placeholder="0" testId={`input-recv-interest-${row.id}`} />
                                </td>
                                <td className="py-1 px-2">
                                  <FormattedNumberInput value={row.fees} onValueChange={(raw) => editRecvCell(row.id, "fees", raw)} className="h-7 text-xs text-right" placeholder="0" testId={`input-recv-fees-${row.id}`} />
                                </td>
                                <td className="py-1 px-2">
                                  <FormattedNumberInput value={row.cpp} onValueChange={(raw) => editRecvCell(row.id, "cpp", raw)} className="h-7 text-xs text-right" placeholder="0" testId={`input-recv-cpp-${row.id}`} />
                                </td>
                                <td className="py-1 px-2 text-center">
                                  <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground hover:text-destructive" onClick={() => deleteRecvRow(row.id)} data-testid={`button-delete-recv-${row.id}`} title={t("floorplan:analytics.deleteRow")}>
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                </td>
                              </tr>
                            );
                          })}
                          {recvRows.length === 0 && (
                            <tr><td colSpan={7} className="py-4 text-center text-muted-foreground">{t("floorplan:analytics.noRows")}</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                    <div className="flex items-center justify-between gap-2 px-2 py-2 border-t border-border/40 bg-muted/20 flex-wrap">
                      <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={addRecvRow} data-testid="button-add-recv">
                        <Plus className="w-3 h-3" /> {t("floorplan:analytics.addRow")}
                      </Button>
                      {(() => {
                        const liveInterest = recvRows.reduce((s, r) => s + (parseRecvNum(r.interest) || 0), 0);
                        const liveFees = recvRows.reduce((s, r) => s + (parseRecvNum(r.fees) || 0) + (parseRecvNum(r.cpp) || 0), 0);
                        return (
                          <div className="text-xs text-muted-foreground flex items-center gap-3" data-testid="text-recv-live-totals">
                            <span>{t("floorplan:analytics.totalInterestLabel")} <span className="font-semibold text-foreground">{fmt$(liveInterest, 2)}</span></span>
                            <span>{t("floorplan:analytics.totalFeesLabel")} <span className="font-semibold text-foreground">{fmt$(liveFees, 2)}</span></span>
                          </div>
                        );
                      })()}
                    </div>
                    <p className="px-2 pb-2 text-[10px] text-muted-foreground/70">{t("floorplan:analytics.cppNote")}</p>
                  </div>
                  ) : (
                  <div className="max-h-48 overflow-y-auto overflow-x-auto">
                    <table className="w-full text-xs min-w-[400px]">
                      {(() => {
                        const hasCpp = qualRecvs.some((r: any) => (r.collateralFee ?? 0) > 0);
                        return (
                        <thead className="sticky top-0 bg-muted/40">
                          <tr>
                            <th className="text-left py-1.5 px-3 text-muted-foreground font-medium">{t("floorplan:upload.th.vinStock")}</th>
                            <th className="text-right py-1.5 px-3 text-muted-foreground font-medium">{t("floorplan:upload.th.principal")}</th>
                            <th className="text-right py-1.5 px-3 text-muted-foreground font-medium">{t("floorplan:upload.th.days")}</th>
                            <th className="text-right py-1.5 px-3 text-muted-foreground font-medium">{t("floorplan:upload.th.interest")}</th>
                            <th className="text-right py-1.5 px-3 text-muted-foreground font-medium">
                              {t("floorplan:upload.th.fees")}{hasCpp && <span className="block text-[9px] font-normal opacity-60">{t("floorplan:upload.th.exclCpp")}</span>}
                            </th>
                            {hasCpp && <th className="text-right py-1.5 px-3 text-muted-foreground font-medium">{t("floorplan:upload.th.cpp")}</th>}
                            <th className="text-right py-1.5 px-3 text-muted-foreground font-medium">{t("floorplan:upload.th.effIntRate")}</th>
                          </tr>
                        </thead>
                        );
                      })()}
                      <tbody>
                        {(() => {
                          const hasCpp = qualRecvs.some((r: any) => (r.collateralFee ?? 0) > 0);
                          return qualRecvs.map((r: any, i: number) => {
                            const interest = r.interest ?? 0;
                            const cpp = r.collateralFee ?? 0;
                            const fees = (r.fees ?? 0) - cpp;
                            const unitInterestRate = r.principal > 0 && r.daysOutstanding > 0
                              ? (interest / r.principal) * (365 / r.daysOutstanding) * 100
                              : null;
                            return (
                              <tr key={i} className="border-t border-border/40 hover:bg-muted/20">
                                <td className="py-1.5 px-3 font-mono">{r.vin ? r.vin.slice(-8) : r.stock ?? "—"}</td>
                                <td className="py-1.5 px-3 text-right">{fmt$(r.principal)}</td>
                                <td className="py-1.5 px-3 text-right">{r.daysOutstanding}d</td>
                                <td className="py-1.5 px-3 text-right">{fmt$(interest ?? 0)}</td>
                                <td className="py-1.5 px-3 text-right">{fees > 0 ? fmt$(fees) : "—"}</td>
                                {hasCpp && <td className="py-1.5 px-3 text-right text-amber-600 dark:text-amber-400">{cpp > 0 ? fmt$(cpp) : "—"}</td>}
                                <td className={`py-1.5 px-3 text-right font-semibold ${unitInterestRate !== null && unitInterestRate > 20 ? "text-destructive" : "text-chart-3"}`}>
                                  {unitInterestRate !== null ? `${unitInterestRate.toFixed(1)}%` : "—"}
                                </td>
                              </tr>
                            );
                          });
                        })()}
                      </tbody>
                    </table>
                  </div>
                  )}
                </div>
              )}

              {/* Receivable Chatbot — scoped to the active (selected or latest) statement */}
              <LatestStmtChat
                latestStmt={activeStmt}
                dealerName={currentUser?.companyName ?? undefined}
                lenderName={lender.name}
              />
            </div>
          )}

          {/* ── Projected cost — only shown in "scenario" mode ── */}
          {mode === "scenario" && (
            effectiveBd ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <Calculator className="w-4 h-4 text-primary" />
                  <p className="text-sm font-semibold">{t("floorplan:analytics.projectedCostTitle", { days: scenario.daysOnFloor, value: fmt$(scenario.vehicleValue) })}</p>
                  {feesUnknown && fallbackFeesPerUnit !== null && (
                    <span className="text-xs text-muted-foreground">{t("floorplan:analytics.feesEstimated")}</span>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="p-4 rounded-xl bg-primary/10 border border-primary/20 text-center">
                    <p className="text-xs text-muted-foreground mb-1">{t("floorplan:analytics.estTotalCostUnit")}</p>
                    <p className="text-2xl font-bold text-primary" data-testid="dialog-cost-unit">
                      {fmt$(effectiveBd.interestTotal + (fallbackFeesPerUnit ?? effectiveBd.feesTotal), 2)}
                    </p>
                    <p className="text-xs text-muted-foreground/60 mt-1">{t("floorplan:analytics.interestPlusFees")}</p>
                  </div>
                  <div className="p-4 rounded-xl bg-muted/50 border text-center">
                    <p className="text-xs text-muted-foreground mb-1">{t("floorplan:analytics.estInterest")}</p>
                    <p className="text-2xl font-bold" data-testid="dialog-interest">{fmt$(effectiveBd.interestTotal, 2)}</p>
                    <p className="text-xs text-muted-foreground/60 mt-1">
                      {t("floorplan:analyticsDialog.atRate", { rate: effectiveAprRate.toFixed(2), source: aprSourceLabel })}
                    </p>
                  </div>
                  <div className="p-4 rounded-xl bg-muted/50 border text-center">
                    <p className="text-xs text-muted-foreground mb-1">{t("floorplan:analytics.estFees")}</p>
                    <p className="text-2xl font-bold" data-testid="dialog-fees">
                      {fallbackFeesPerUnit !== null ? fmt$(fallbackFeesPerUnit, 2) : fmt$(effectiveBd.feesTotal, 2)}
                    </p>
                    <p className="text-xs text-muted-foreground/60 mt-1">
                      {fallbackFeesPerUnit !== null ? t("floorplan:analytics.avgFromStatement") : t("floorplan:analytics.fromFeeSchedule")}
                    </p>
                  </div>
                </div>
              </div>
            ) : hasRealData && realApr.avgInterestPerUnit !== null ? (
              // No term structure available — fall back to actuals from statement
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Calculator className="w-4 h-4 text-primary" />
                  <p className="text-sm font-semibold">{t("floorplan:analytics.actualCostTitle")}</p>
                  <span className="text-xs text-muted-foreground">{t("floorplan:analytics.termStructureUnknown")}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="p-4 rounded-xl bg-primary/10 border border-primary/20 text-center">
                    <p className="text-xs text-muted-foreground mb-1">{t("floorplan:analytics.avgTotalUnit")}</p>
                    <p className="text-2xl font-bold text-primary" data-testid="dialog-cost-unit">
                      {fmt$((realApr.avgInterestPerUnit ?? 0) + (realApr.avgFeesPerUnit ?? 0), 2)}
                    </p>
                    <p className="text-xs text-muted-foreground/60 mt-1">{t("floorplan:analytics.interestPlusFeesActual")}</p>
                  </div>
                  <div className="p-4 rounded-xl bg-muted/50 border text-center">
                    <p className="text-xs text-muted-foreground mb-1">{t("floorplan:analytics.avgInterestUnit")}</p>
                    <p className="text-2xl font-bold" data-testid="dialog-interest">{fmt$(realApr.avgInterestPerUnit, 2)}</p>
                    <p className="text-xs text-muted-foreground/60 mt-1">{t("floorplan:analytics.avgChargedActual")}</p>
                  </div>
                  <div className="p-4 rounded-xl bg-muted/50 border text-center">
                    <p className="text-xs text-muted-foreground mb-1">{t("floorplan:analytics.avgFeesUnit")}</p>
                    <p className="text-2xl font-bold" data-testid="dialog-fees">{fmt$(realApr.avgFeesPerUnit, 2)}</p>
                    <p className="text-xs text-muted-foreground/60 mt-1">{t("floorplan:analytics.avgChargedActual")}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="py-6 text-center rounded-lg border border-dashed">
                <p className="text-xs text-muted-foreground">{t("floorplan:analytics.noTermsDefined")}</p>
                <Button size="sm" variant="outline" className="mt-2 h-7 text-xs" onClick={onEdit} data-testid="button-add-terms">{t("floorplan:analytics.addTerms")}</Button>
              </div>
            )
          )}

          <Separator />

          {/* Term structure — always shown; "Unknown" placeholders when days not set */}
          <div className="space-y-2">
            <p className="text-sm font-semibold">{t("floorplan:analytics.termStructure")}</p>
            {hasTerms ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {[
                    { num: 1, days: lender.period1Days, fee: lender.feePeriod1, at: t("floorplan:analyticsDialog.dayN", { day: 0 }) },
                    { num: 2, days: lender.period2Days, fee: lender.feePeriod2, at: t("floorplan:analyticsDialog.dayN", { day: T2 }) },
                    { num: 3, days: lender.period3Days, fee: lender.feePeriod3, at: t("floorplan:analyticsDialog.dayN", { day: T3 }) },
                    ...(lender.period4Days ? [{ num: 4, days: lender.period4Days, fee: lender.feePeriod4, at: t("floorplan:analyticsDialog.dayN", { day: T3 + (lender.period3Days ?? 0) }) }] : []),
                  ].filter((p) => p.days).map((p) => (
                    <div key={p.num} className="p-2.5 rounded-lg bg-muted/40 space-y-1">
                      <p className="text-xs font-semibold text-muted-foreground">{t("floorplan:analytics.period", { num: p.num })}</p>
                      <p className="text-sm font-bold">{t("floorplan:analytics.daysCount", { count: p.days })}</p>
                      <p className="text-xs text-muted-foreground">{p.fee ? t("floorplan:analytics.feeAt", { fee: fmt$(n(p.fee)), at: p.at }) : t("floorplan:analytics.feesUnknown")}</p>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground font-mono">
                  D{lender.period1Days}/{lender.period2Days}/{lender.period3Days} &nbsp;
                  {feesUnknown
                    ? <span className="text-amber-600 dark:text-amber-400">{t("floorplan:analytics.feesUnknown")}</span>
                    : <>F{n(lender.feePeriod1)}/{n(lender.feePeriod2)}/{n(lender.feePeriod3)}</>
                  } &nbsp;
                  R{effectiveAprRate.toFixed(4)}{aprSourceLabel}
                </p>
              </>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {[1, 2, 3].map((num) => (
                  <div key={num} className="p-2.5 rounded-lg bg-muted/30 border border-dashed space-y-1">
                    <p className="text-xs font-semibold text-muted-foreground">{t("floorplan:analytics.period", { num })}</p>
                    <p className="text-sm font-bold text-muted-foreground/60">{t("floorplan:analytics.unknown")}</p>
                    <p className="text-xs text-muted-foreground/50">{t("floorplan:analytics.daysNotFound")}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Lender Comparison Table ──────────────────────────────────────────────────
// Replaces the old Effective APR bar chart with a terminal-style comparison table.
// All metrics are derived from uploaded receivable data — no hardcoded values.

function LenderComparisonTable({ lenders, scenario }: { lenders: Lender[]; scenario: ScenarioInputs }) {
  const { t } = useTranslation(["floorplan", "common"]);
  const [inputTurnDays, setInputTurnDays] = useState<string>("");
  const [debouncedTurnDays, setDebouncedTurnDays] = useState<number | null>(null);
  const didInit = useRef(false);

  // Debounce the turn-days input by 300 ms
  useEffect(() => {
    const parsed = parseFloat(inputTurnDays);
    if (!isNaN(parsed) && parsed > 0) {
      const t = setTimeout(() => setDebouncedTurnDays(parsed), 300);
      return () => clearTimeout(t);
    }
  }, [inputTurnDays]);

  const turnParam = debouncedTurnDays ?? "";
  const { data, isLoading } = useQuery<{
    lenders: Array<{
      id: string; name: string; unitCount: number; hasLimitedData: boolean;
      realInterestRatePct: number | null; avgFeePerUnit: number | null;
      avgPrincipal: number | null; dailyCost: number | null; allInPerUnit: number | null;
      isEstimate: boolean; insufficientData: boolean; noInterestAccrued: boolean; isZeroInterest: boolean;
      isFallbackAllUnits?: boolean;
      usedCount: number; excludedCount: number;
    }>;
    sharedAvgPrincipal: number | null;
    dealerAvgTurnDays: number | null;
    usedTurnDays: number;
    breakEvenDays: Record<string, Record<string, number | null>>;
  }>({
    queryKey: [`/api/lenders/comparison-stats?turnDays=${turnParam}`],
    enabled: lenders.length > 0,
    staleTime: 30_000,
  });

  // Seed turn-days input from dealer profile on first successful load
  useEffect(() => {
    if (!didInit.current && data) {
      didInit.current = true;
      if (data.dealerAvgTurnDays != null) {
        setInputTurnDays(String(Math.round(data.dealerAvgTurnDays)));
        setDebouncedTurnDays(data.dealerAvgTurnDays);
      } else {
        setInputTurnDays("45");
        setDebouncedTurnDays(45);
      }
    }
  }, [data]);

  const lenderRows = (data?.lenders.filter((l) => l.unitCount > 0) ?? [])
    .slice()
    .sort((a, b) => {
      const aVal = a.allInPerUnit ?? Infinity;
      const bVal = b.allInPerUnit ?? Infinity;
      return aVal - bVal;
    });

  // Highlight winning (lowest) value per metric row
  function bestIdx(vals: (number | null)[]): number {
    let best = Infinity, idx = -1;
    vals.forEach((v, i) => { if (v !== null && v < best) { best = v; idx = i; } });
    return idx;
  }

  const rateVals = lenderRows.map((l) => l.realInterestRatePct);
  const feeVals  = lenderRows.map((l) => l.avgFeePerUnit);
  const dailyVals = lenderRows.map((l) => l.dailyCost);
  const allinVals = lenderRows.map((l) => l.allInPerUnit);

  const bestRate  = bestIdx(rateVals);
  const bestFee   = bestIdx(feeVals);
  const bestDaily = bestIdx(dailyVals);
  const bestAllin = bestIdx(allinVals);

  if (lenders.length === 0) return null;

  const usedDays = data?.usedTurnDays ?? (debouncedTurnDays ?? 45);

  // Column accent colors — one per lender
  const colAccents = [
    { bg: "bg-primary/10 dark:bg-primary/15", text: "text-primary", border: "border-primary/30" },
    { bg: "bg-blue-500/10 dark:bg-blue-400/10", text: "text-blue-600 dark:text-blue-400", border: "border-blue-400/30" },
    { bg: "bg-violet-500/10 dark:bg-violet-400/10", text: "text-violet-600 dark:text-violet-400", border: "border-violet-400/30" },
    { bg: "bg-orange-500/10 dark:bg-orange-400/10", text: "text-orange-600 dark:text-orange-400", border: "border-orange-400/30" },
  ];

  const anyEstimate = lenderRows.some((l) => l.isEstimate);
  const anyTrueApr = lenderRows.some((l) => !l.isEstimate && !l.insufficientData && !l.noInterestAccrued);

  const metrics = [
    {
      key: "rate",
      label: anyTrueApr ? t("floorplan:comparison.rateTrue") : t("floorplan:comparison.rateEst"),
      desc: anyTrueApr
        ? t("floorplan:comparison.rateDescTrue")
        : t("floorplan:comparison.rateDescEst"),
      vals: rateVals,
      best: bestRate,
      format: (v: number) => `${v.toFixed(1)}%`,
      testPrefix: "rate",
    },
    {
      key: "fee",
      label: t("floorplan:comparison.avgFeeUnit"),
      desc: t("floorplan:comparison.avgFeeDesc"),
      vals: feeVals,
      best: bestFee,
      format: (v: number) => fmt$(v, 0),
      testPrefix: "fee",
    },
    {
      key: "daily",
      label: t("floorplan:comparison.totalDailyCost"),
      desc: `${t("floorplan:comparison.dailyDescPre", { days: usedDays })}${data?.sharedAvgPrincipal ? t("floorplan:comparison.dailyDescAvg", { value: fmt$(data.sharedAvgPrincipal, 0) }) : ""}`,
      vals: dailyVals,
      best: bestDaily,
      format: (v: number) => fmt$(v, 2),
      testPrefix: "daily",
    },
    {
      key: "allin",
      label: t("floorplan:comparison.allInAt", { days: usedDays }),
      desc: `${t("floorplan:comparison.allInDescPre")}${data?.sharedAvgPrincipal ? t("floorplan:comparison.allInDescAvg", { value: fmt$(data.sharedAvgPrincipal, 0) }) : ""}`,
      vals: allinVals,
      best: bestAllin,
      format: (v: number) => fmt$(v, 0),
      testPrefix: "allin",
    },
  ] as const;

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-4 pt-5 px-6 border-b border-border/60 bg-muted/20">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <CardTitle className="text-base font-semibold tracking-tight flex items-center gap-2">
              <ArrowLeftRight className="w-4 h-4 text-primary" />
              {t("floorplan:comparison.lenderOverviews")}
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              {t("floorplan:comparison.allMetrics")}
            </p>
          </div>
          <div className="flex items-center gap-2 bg-background rounded-lg px-3 py-2 border border-border/60 shadow-sm">
            <Label htmlFor="cmp-turn-days" className="text-xs text-muted-foreground whitespace-nowrap font-medium">{t("floorplan:comparison.avgTurn")}</Label>
            <Input
              id="cmp-turn-days"
              type="number"
              min={1}
              max={365}
              value={inputTurnDays}
              onChange={(e) => setInputTurnDays(e.target.value)}
              className="w-14 h-7 text-sm text-center font-mono border-0 bg-transparent p-0 focus-visible:ring-0 focus-visible:ring-offset-0"
              data-testid="input-comparison-turn-days"
            />
            <span className="text-xs text-muted-foreground font-medium">{t("floorplan:comparison.days")}</span>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {isLoading ? (
          <div className="p-4 space-y-2">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="flex gap-3">
                <Skeleton className="h-10 w-40 shrink-0" />
                <Skeleton className="h-10 flex-1" />
                <Skeleton className="h-10 flex-1" />
              </div>
            ))}
          </div>
        ) : lenderRows.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">
            <ArrowLeftRight className="w-7 h-7 text-muted-foreground/30 mx-auto mb-2" />
            <p>{t("floorplan:comparison.noComparisonData")}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse" style={{ minWidth: `${Math.max(400, lenderRows.length * 140 + 180)}px` }}>
              {/* Lender column headers */}
              <thead>
                <tr>
                  <th className="w-44 py-3 pl-5 pr-2 text-left align-bottom">
                    <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">{t("floorplan:comparison.metric")}</span>
                  </th>
                  {lenderRows.map((l, i) => {
                    const accent = colAccents[i % colAccents.length];
                    return (
                      <th key={l.id} className="py-3 px-3 text-center align-bottom border-l border-border/40" data-testid={`col-lender-${l.id}`}>
                        <div className={`inline-flex flex-col items-center gap-1 rounded-lg px-3 py-2 border ${accent.bg} ${accent.border}`}>
                          <span className={`text-xs font-bold ${accent.text} leading-tight`}>{l.name}</span>
                          <span className="text-[10px] text-muted-foreground font-normal">{t("floorplan:comparison.units", { count: l.unitCount })}</span>
                          {l.hasLimitedData && (
                            <Badge variant="outline" className="text-[9px] px-1 py-0 font-medium text-amber-600 border-amber-300 dark:text-amber-400 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/20">
                              {t("floorplan:comparison.limited")}
                            </Badge>
                          )}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>

              {/* Metric rows */}
              <tbody>
                {metrics.map(({ key, label, desc, vals, best, format, testPrefix }, rowIdx) => (
                  <tr key={key} className={rowIdx % 2 === 0 ? "bg-muted/20" : "bg-background"}>
                    <td className="py-3 pl-5 pr-2 border-t border-border/40">
                      <p className="text-xs font-semibold text-foreground leading-tight">{label}</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5 leading-tight">{desc}</p>
                    </td>
                    {lenderRows.map((l, i) => {
                      const v = vals[i];
                      const isBest = i === best && v !== null && lenderRows.length > 1;
                      return (
                        <td
                          key={l.id}
                          className={`py-3 px-3 text-center border-t border-l border-border/40 ${isBest ? "bg-green-50/60 dark:bg-green-950/20" : ""}`}
                          data-testid={`${testPrefix}-${l.id}`}
                        >
                          <span className={`text-base font-bold tabular-nums tracking-tight ${
                            isBest ? "text-green-600 dark:text-green-400" : "text-foreground"
                          }`}>
                            {v != null ? format(v) : <span className="text-muted-foreground text-sm">—</span>}
                          </span>
                          {key === "rate" && v != null && l.isFallbackAllUnits && (
                            <p className="text-[9px] text-amber-600 dark:text-amber-400 mt-0.5">{t("floorplan:comparison.estAllUnits")}</p>
                          )}
                          {key === "rate" && v != null && l.isEstimate && !l.isFallbackAllUnits && (
                            <p className="text-[9px] text-amber-600 dark:text-amber-400 mt-0.5">{t("floorplan:comparison.est")}</p>
                          )}
                          {key === "rate" && l.noInterestAccrued && (
                            <p className="text-[9px] text-muted-foreground mt-0.5">{t("floorplan:comparison.noInterest")}</p>
                          )}
                          {key === "rate" && l.isZeroInterest && (
                            <p className="text-[9px] text-sky-600 dark:text-sky-400 mt-0.5">{t("floorplan:comparison.zeroInterestShort")}</p>
                          )}
                          {key === "rate" && v != null && !l.isEstimate && !l.noInterestAccrued && !l.isZeroInterest && (
                            <p className="text-[9px] text-sky-600 dark:text-sky-400 mt-0.5">{t("floorplan:comparison.true")}</p>
                          )}
                          {key === "allin" && data?.sharedAvgPrincipal != null && (
                            <p className="text-[9px] text-muted-foreground mt-0.5">{t("floorplan:comparison.onAvg", { value: fmt$(data.sharedAvgPrincipal, 0) })}</p>
                          )}
                          {isBest && lenderRows.length > 1 && key !== "allin" && (
                            <p className="text-[9px] text-green-600 dark:text-green-400 font-medium mt-0.5">{t("floorplan:comparison.lowest")}</p>
                          )}
                          {isBest && lenderRows.length > 1 && key === "allin" && (
                            <p className="text-[9px] text-green-600 dark:text-green-400 font-medium">{t("floorplan:comparison.lowest")}</p>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}

              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Head-to-Head Cost Comparison ────────────────────────────────────────────

function LenderCostComparison({ lenders, allStatements, onAddLender }: { lenders: LenderWithTiers[]; allStatements: any[]; onAddLender?: () => void }) {
  const { t } = useTranslation(["floorplan", "common"]);
  const [lenderAId, setLenderAId] = useState<string>("");
  const [lenderBId, setLenderBId] = useState<string>("");
  const [extraLenderIds, setExtraLenderIds] = useState<string[]>([]);
  const [turnDays, setTurnDays] = useState(45);
  const [principal, setPrincipal] = useState(10000);
  const [principalStr, setPrincipalStr] = useState("10,000");
  const [chartMode, setChartMode] = useState<"statements" | "stated">("statements");

  const qualified = lenders.filter(lenderHasTerms);

  const addExtraLender = () => {
    const taken = new Set([lenderAId, lenderBId, ...extraLenderIds].filter(Boolean));
    const next = qualified.find((l) => !taken.has(l.id));
    if (next) setExtraLenderIds((prev) => [...prev, next.id]);
  };

  const removeExtraLender = (idx: number) => {
    setExtraLenderIds((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateExtraLender = (idx: number, id: string) => {
    setExtraLenderIds((prev) => prev.map((v, i) => (i === idx ? id : v)));
  };

  const allSelectedIds = [lenderAId, lenderBId, ...extraLenderIds];

  const compResult = useMemo(() => {
    const lA = lenders.find((l) => l.id === lenderAId) ?? null;
    const lB = lenders.find((l) => l.id === lenderBId) ?? null;
    if (!lA || !lB || !lenderHasTerms(lA) || !lenderHasTerms(lB) || lenderAId === lenderBId) {
      return null;
    }

    // ─── Stated-terms cost ───────────────────────────────────────────────────
    // "Stated terms" = the dealer manually entered a fee schedule (feePeriod1…4).
    // aprRate is intentionally excluded: the server auto-writes the statement-derived
    // effective APR back to lender.aprRate, so a non-zero aprRate does NOT mean the
    // user ever entered a stated fee schedule.
    const hasMeaningfulStatedTerms = (lender: LenderWithTiers): boolean =>
      n(lender.feePeriod1) > 0 || n(lender.feePeriod2) > 0 ||
      n(lender.feePeriod3) > 0 || n(lender.feePeriod4) > 0;

    const computeStatedCost = (lender: LenderWithTiers, day: number): number => {
      const terms = dbLenderToTerms(lender);
      const tiers = dbTiersToTierConfigs(Array.isArray(lender.tiers) ? lender.tiers : []);
      return computeCostBreakdownWithTiers(terms, tiers, {
        vehicleValue: principal,
        daysOnFloor: day,
        avgUnitsPerMonth: 1,
      }).breakdown.trueCostPerUnit;
    };

    // Returns null when the lender has no meaningful configured terms.
    const buildStatedCurve = (lender: LenderWithTiers): number[] | null => {
      if (!hasMeaningfulStatedTerms(lender)) return null;
      return Array.from({ length: 121 }, (_, day) =>
        parseFloat(computeStatedCost(lender, day).toFixed(2))
      );
    };

    // ─── Receivables-based curve ─────────────────────────────────────────────
    // Returns null when lender has no linked statement data.
    // Interest uses the actual effective APR derived from statement charges.
    // Fees are the average floor fees (fees minus collateralFee) observed in
    // receivables for vehicles whose age falls within each period bucket.
    // Buckets are defined by the lender's own period-day boundaries so the
    // comparison aligns directly with the stated-terms structure.
    const buildActualCurve = (lender: LenderWithTiers): number[] | null => {
      const stmts = stmtsForLender(allStatements, lender, lenders);
      const realApr = computeRealAprForLender(stmts, { manualFeePerUnit: manualFeePerUnitFromLender(lender) });
      if (realApr.interestOnlyApr === null) return null;

      // Gather qualifying receivables across all statements.
      // Require per-unit rows with actual charges — if none exist we return null even
      // if the APR fallback produced a value from statement-level totals, because the
      // bucket fee average would be meaningless (all zeros).
      const allRecvs = dedupeReceivables(stmts);
      const qualRecvs = allRecvs.filter((r) =>
        (r.principal ?? 0) > 0 &&
        (r.daysOutstanding ?? 0) > 0 &&
        ((r.interest ?? 0) + (r.fees ?? 0)) > 0
      );
      if (qualRecvs.length === 0) return null;

      // Build period-day boundary list from lender terms (always starts at 0)
      const p1d = n(lender.period1Days, 0);
      const p2d = n(lender.period2Days, 0);
      const p3d = n(lender.period3Days, 0);
      const p4d = n(lender.period4Days, 0);
      const rawBoundaries = [0, p1d, p1d + p2d, p1d + p2d + p3d, p1d + p2d + p3d + p4d];
      const periodFlags = [true, p1d > 0, p2d > 0, p3d > 0, p4d > 0];
      const boundaries = rawBoundaries.filter((_, i) => periodFlags[i]);

      // Avg floor fees per period bucket — each bucket covers [boundaries[i], boundaries[i+1])
      // The last bucket is open-ended. CPP (collateralFee) is excluded from the floor fee average
      // so the lender comparison curve reflects only the contractual floor fee, not insurance charges.
      const bucketFees: number[] = boundaries.map((startDay, i) => {
        const endDay = boundaries[i + 1] ?? null;
        const bucket = qualRecvs.filter((r) =>
          (r.daysOutstanding ?? 0) >= startDay &&
          (endDay === null || (r.daysOutstanding ?? 0) < endDay)
        );
        if (bucket.length === 0) return 0;
        return bucket.reduce((s: number, r: any) => s + (r.fees ?? 0) - (r.collateralFee ?? 0), 0) / bucket.length;
      });

      // Cumulative 0-120 day curve:
      //   interest = principal × realApr% × D/365
      //   fees     = sum of bucket fees for every period boundary already crossed by day D
      const aprDecimal = realApr.interestOnlyApr / 100;
      return Array.from({ length: 121 }, (_, day) => {
        const interest = principal * aprDecimal * (day / 365);
        let fees = 0;
        for (let i = 0; i < boundaries.length; i++) {
          if (day >= boundaries[i]) fees += bucketFees[i];
          else break;
        }
        return parseFloat((interest + fees).toFixed(2));
      });
    };

    // ─── Build both curve datasets ───────────────────────────────────────────
    // Extra lenders (beyond A and B)
    const extraLenders = extraLenderIds
      .map((id) => lenders.find((l) => l.id === id) ?? null)
      .filter((l): l is LenderWithTiers => l !== null && lenderHasTerms(l));

    const statedA = buildStatedCurve(lA);
    const statedB = buildStatedCurve(lB);
    const statedExtras = extraLenders.map(buildStatedCurve);
    const hasStatedData = statedA !== null || statedB !== null || statedExtras.some((c) => c !== null);
    const statedCurveData = Array.from({ length: 121 }, (_, day) => {
      const point: Record<string, number | null> = {
        day,
        a: statedA?.[day] ?? null,
        b: statedB?.[day] ?? null,
      };
      extraLenders.forEach((l, i) => { point[`extra_${i}`] = statedExtras[i]?.[day] ?? null; });
      return point;
    });

    const actualA = buildActualCurve(lA);
    const actualB = buildActualCurve(lB);
    const actualExtras = extraLenders.map(buildActualCurve);
    const hasActualData = actualA !== null || actualB !== null || actualExtras.some((c) => c !== null);
    const actualCurveData = Array.from({ length: 121 }, (_, day) => {
      const point: Record<string, number | null> = {
        day,
        a: actualA?.[day] ?? null,
        b: actualB?.[day] ?? null,
      };
      extraLenders.forEach((l, i) => { point[`extra_${i}`] = actualExtras[i]?.[day] ?? null; });
      return point;
    });

    // Per-mode at-turn costs: null = that mode has no data for this lender.
    // Stated: from the configured fee schedule (only non-null when feePeriods are set).
    // Actual: from the receivables-derived curve at the chosen turn-day index.
    const atTurnStatedA = statedA?.[Math.min(turnDays, 120)] ?? null;
    const atTurnStatedB = statedB?.[Math.min(turnDays, 120)] ?? null;
    const atTurnActualA = actualA?.[Math.min(turnDays, 120)] ?? null;
    const atTurnActualB = actualB?.[Math.min(turnDays, 120)] ?? null;
    const atTurnStatedExtras = statedExtras.map((c) => c?.[Math.min(turnDays, 120)] ?? null);
    const atTurnActualExtras = actualExtras.map((c) => c?.[Math.min(turnDays, 120)] ?? null);

    return {
      statedCurveData,
      hasStatedData,
      statedA,
      statedB,
      statedExtras,
      actualCurveData,
      hasActualData,
      actualA,
      actualB,
      actualExtras,
      atTurnStatedA,
      atTurnStatedB,
      atTurnActualA,
      atTurnActualB,
      atTurnStatedExtras,
      atTurnActualExtras,
      lA,
      lB,
      extraLenders,
    };
  }, [lenderAId, lenderBId, extraLenderIds, principal, turnDays, lenders, allStatements]);

  // Auto-revert to "statements" whenever the selected lenders have no meaningful stated terms.
  useEffect(() => {
    if (!compResult?.hasStatedData && chartMode === "stated") {
      setChartMode("statements");
    }
  }, [compResult?.hasStatedData, chartMode]);

  if (qualified.length < 2) {
    return (
      <div className="py-8 text-center">
        <ArrowLeftRight className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
        <p className="text-sm text-muted-foreground">{t("floorplan:headToHead.addAtLeast2")}</p>
      </div>
    );
  }

  // Chart color palette: chart-1 = A, chart-2 = B, then extra lenders use chart-3..5 and fallback colors
  const EXTRA_COLORS = [
    "hsl(var(--chart-3))",
    "hsl(var(--chart-4))",
    "hsl(var(--chart-5))",
    "hsl(221,83%,53%)",
    "hsl(142,71%,45%)",
  ];

  const COST_EPS = 0.01; // cents — two lenders within $0.01 are treated as tied
  // Pick the correct at-turn cost based on which chart mode is active.
  const atA = compResult
    ? (chartMode === "stated" ? compResult.atTurnStatedA : compResult.atTurnActualA)
    : null;
  const atB = compResult
    ? (chartMode === "stated" ? compResult.atTurnStatedB : compResult.atTurnActualB)
    : null;
  const atExtras = compResult
    ? (chartMode === "stated" ? compResult.atTurnStatedExtras : compResult.atTurnActualExtras)
    : [];

  // Build array of all [lender, atCost] for multi-lender win detection
  const allAtCosts: (number | null)[] = [atA, atB, ...atExtras];
  const minCost = allAtCosts.filter((v): v is number => v !== null).reduce((m, v) => Math.min(m, v), Infinity);

  const lenderNameForKey = (key: string): string => {
    if (!compResult) return key;
    if (key === "a") return compResult.lA.name;
    if (key === "b") return compResult.lB.name;
    const idx = parseInt(key.replace("extra_", ""), 10);
    return compResult.extraLenders[idx]?.name ?? key;
  };

  const canAddMore = qualified.length > allSelectedIds.filter(Boolean).length;

  return (
    <div className="space-y-4">
      {/* Lender selectors */}
      <div className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">{t("floorplan:headToHead.lenderA")}</Label>
            <Select value={lenderAId} onValueChange={setLenderAId}>
              <SelectTrigger data-testid="select-lender-a">
                <SelectValue placeholder={t("floorplan:headToHead.selectLender")} />
              </SelectTrigger>
              <SelectContent>
                {qualified.map((l) => (
                  <SelectItem key={l.id} value={l.id} disabled={allSelectedIds.filter((id) => id !== lenderAId).includes(l.id)} data-testid={`option-a-${l.id}`}>
                    {l.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">{t("floorplan:headToHead.lenderB")}</Label>
            <Select value={lenderBId} onValueChange={setLenderBId}>
              <SelectTrigger data-testid="select-lender-b">
                <SelectValue placeholder={t("floorplan:headToHead.selectLender")} />
              </SelectTrigger>
              <SelectContent>
                {qualified.map((l) => (
                  <SelectItem key={l.id} value={l.id} disabled={allSelectedIds.filter((id) => id !== lenderBId).includes(l.id)} data-testid={`option-b-${l.id}`}>
                    {l.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Extra lender selectors */}
        {extraLenderIds.map((id, idx) => (
          <div key={idx} className="flex items-end gap-2">
            <div className="flex-1 space-y-1.5">
              <Label className="text-xs text-muted-foreground">{t("floorplan:headToHead.lenderLetter", { letter: String.fromCharCode(67 + idx) })}</Label>
              <Select value={id} onValueChange={(v) => updateExtraLender(idx, v)}>
                <SelectTrigger data-testid={`select-lender-extra-${idx}`}>
                  <SelectValue placeholder={t("floorplan:headToHead.selectLender")} />
                </SelectTrigger>
                <SelectContent>
                  {qualified.map((l) => (
                    <SelectItem key={l.id} value={l.id} disabled={allSelectedIds.filter((sid) => sid !== id).includes(l.id)} data-testid={`option-extra-${idx}-${l.id}`}>
                      {l.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-9 w-9 p-0 shrink-0 text-muted-foreground hover:text-destructive"
              onClick={() => removeExtraLender(idx)}
              data-testid={`button-remove-extra-lender-${idx}`}
            >
              <Minus className="w-4 h-4" />
            </Button>
          </div>
        ))}

        {/* Add Lender button */}
        <div className="flex items-center gap-2">
          {canAddMore ? (
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 text-xs"
              onClick={addExtraLender}
              data-testid="button-add-compare-lender"
            >
              <Plus className="w-3.5 h-3.5" />
              {t("floorplan:headToHead.addLender")}
            </Button>
          ) : qualified.length < 3 ? (
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 text-xs"
              onClick={onAddLender}
              data-testid="button-add-new-lender-for-compare"
            >
              <Plus className="w-3.5 h-3.5" />
              {t("floorplan:headToHead.addLender")}
            </Button>
          ) : null}
        </div>
      </div>

      {/* Scenario controls */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 bg-muted/40 rounded-lg p-4 border">
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">
            {t("floorplan:headToHead.targetTurnTime")} <span className="font-semibold text-foreground">{t("floorplan:headToHead.turnDaysValue", { days: turnDays })}</span>
          </Label>
          <Slider
            min={1}
            max={120}
            step={1}
            value={[turnDays]}
            onValueChange={([v]) => setTurnDays(v)}
            data-testid="slider-turn-days"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">{t("floorplan:headToHead.avgUnitPrice")}</Label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs pointer-events-none">$</span>
            <Input
              type="text"
              inputMode="numeric"
              className="pl-6 h-9 text-sm"
              value={principalStr}
              onChange={(e) => {
                const raw = e.target.value.replace(/[^0-9]/g, "");
                setPrincipalStr(raw === "" ? "" : parseInt(raw, 10).toLocaleString("en-US"));
                const num = raw === "" ? 0 : parseInt(raw, 10);
                if (!isNaN(num) && num > 0) setPrincipal(num);
              }}
              onBlur={() => {
                if (principalStr === "" || principalStr === "0") { setPrincipalStr("10,000"); setPrincipal(10000); }
              }}
              data-testid="input-compare-principal"
            />
          </div>
        </div>
      </div>

      {/* Placeholder when nothing selected */}
      {(!lenderAId || !lenderBId) && (
        <div className="py-6 text-center text-sm text-muted-foreground">
          {t("floorplan:headToHead.selectTwo")}
        </div>
      )}

      {/* Same lender selected */}
      {lenderAId && lenderBId && lenderAId === lenderBId && (
        <div className="py-4 text-center text-sm text-amber-600 dark:text-amber-400">
          {t("floorplan:headToHead.selectTwoDifferent")}
        </div>
      )}

      {/* Results */}
      {compResult && (
        <div className="space-y-4">
          {/* Summary cards — all lenders */}
          {(() => {
            const noDataLabel = chartMode === "stated" ? t("floorplan:headToHead.noStatedTerms") : t("floorplan:headToHead.noStatementData");
            const modeLabel = chartMode === "stated" ? t("floorplan:headToHead.estStatedTerms") : t("floorplan:headToHead.estFromStatements");

            const allEntries: Array<{ lender: LenderWithTiers; atCost: number | null; testId: string }> = [
              { lender: compResult.lA, atCost: atA, testId: "compare-cost-a" },
              { lender: compResult.lB, atCost: atB, testId: "compare-cost-b" },
              ...compResult.extraLenders.map((l, i) => ({
                lender: l,
                atCost: atExtras[i] ?? null,
                testId: `compare-cost-extra-${i}`,
              })),
            ];

            const colCount = Math.min(allEntries.length, 4);
            const gridColsClass = colCount <= 2 ? "grid-cols-2" : colCount === 3 ? "grid-cols-3" : "grid-cols-4";

            return (
              <div className={`grid gap-3 ${gridColsClass}`}>
                {allEntries.map(({ lender, atCost, testId }) => {
                  const isWinner = atCost !== null && atCost <= minCost + COST_EPS && allAtCosts.filter((v) => v !== null).length > 1;
                  const hasData = atCost !== null;
                  return hasData ? (
                    <Card key={lender.id} className={isWinner ? "border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-950/20" : "border-red-200 dark:border-red-800 bg-red-50/30 dark:bg-red-950/10"}>
                      <CardContent className="pt-3 pb-3 px-4">
                        <p className="text-xs text-muted-foreground mb-1 truncate" title={lender.name}>{lender.name}</p>
                        <p className={`text-xl font-bold tabular-nums ${isWinner ? "text-green-700 dark:text-green-400" : "text-red-600 dark:text-red-400"}`} data-testid={testId}>
                          {fmt$(atCost, 0)}
                        </p>
                        <p className="text-[11px] text-muted-foreground mt-0.5">
                          {t("floorplan:headToHead.atTurnSuffix", { state: isWinner ? t("floorplan:headToHead.cheapest") : t("floorplan:headToHead.moreExpensive"), days: turnDays, mode: modeLabel })}
                        </p>
                      </CardContent>
                    </Card>
                  ) : (
                    <Card key={lender.id}>
                      <CardContent className="pt-3 pb-3 px-4">
                        <p className="text-xs text-muted-foreground mb-1 truncate" title={lender.name}>{lender.name}</p>
                        <p className="text-xl font-bold tabular-nums text-muted-foreground" data-testid={testId}>—</p>
                        <p className="text-[11px] text-muted-foreground mt-0.5">{noDataLabel}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            );
          })()}

          {/* Single chart card with dropdown to switch view */}
          <Card>
            <CardHeader className="pb-2 pt-4 px-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <CardTitle className="text-sm font-medium">
                    {chartMode === "statements" ? t("floorplan:headToHead.basedOnStatements") : t("floorplan:headToHead.basedOnStatedTerms")}
                  </CardTitle>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {chartMode === "statements"
                      ? t("floorplan:headToHead.realEffApr")
                      : t("floorplan:headToHead.configuredApr", { value: fmt$(principal, 0) })}
                  </p>
                </div>
                <Select value={chartMode} onValueChange={(v) => setChartMode(v as "statements" | "stated")}>
                  <SelectTrigger className="w-44 h-8 text-xs" data-testid="select-chart-mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="statements">{t("floorplan:headToHead.basedOnStatements")}</SelectItem>
                    <SelectItem value="stated" disabled={!compResult.hasStatedData}>
                      {!compResult.hasStatedData ? t("floorplan:headToHead.basedOnStatedTermsNoTerms") : t("floorplan:headToHead.basedOnStatedTerms")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              {(() => {
                const curveData = chartMode === "statements" ? compResult.actualCurveData : compResult.statedCurveData;
                const hasData = chartMode === "statements" ? compResult.hasActualData : compResult.hasStatedData;
                const noDataMsg = chartMode === "statements"
                  ? t("floorplan:headToHead.noStatementLinked")
                  : t("floorplan:headToHead.noStatedConfigured");

                const curveA = chartMode === "statements" ? compResult.actualA : compResult.statedA;
                const curveB = chartMode === "statements" ? compResult.actualB : compResult.statedB;
                const curveExtras = chartMode === "statements" ? compResult.actualExtras : compResult.statedExtras;

                return !hasData ? (
                  <div className="py-8 text-center text-sm text-muted-foreground">{noDataMsg}</div>
                ) : (
                  <>
                    <ResponsiveContainer width="100%" height={240}>
                      <LineChart data={curveData} margin={{ top: 4, right: 8, bottom: 20, left: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-border/50" />
                        <XAxis dataKey="day" tick={{ fontSize: 11 }} label={{ value: t("floorplan:headToHead.daysFloored"), position: "insideBottom", offset: -4, fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} width={56} tickFormatter={(v) => `$${Number(v).toLocaleString()}`} />
                        <Tooltip
                          content={(props) => {
                            if (!props.active || !props.payload?.length) return null;
                            const sorted = [...props.payload].sort((a, b) => (Number(b.value) || 0) - (Number(a.value) || 0));
                            return (
                              <div className="bg-background border border-border rounded shadow-md px-3 py-2 text-sm">
                                <p className="mb-1.5 text-muted-foreground font-medium">{t("floorplan:headToHead.dayLabel", { day: props.label })}</p>
                                {sorted.map((entry, i) => (
                                  <div key={i} className="flex items-center gap-2 py-0.5">
                                    <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: entry.color }} />
                                    <span className="text-foreground">{lenderNameForKey(String(entry.name))}</span>
                                    <span className="ml-auto pl-4 font-mono text-foreground">{fmt$(Number(entry.value), 2)}</span>
                                  </div>
                                ))}
                              </div>
                            );
                          }}
                        />
                        <ReferenceLine
                          x={turnDays}
                          stroke="hsl(var(--muted-foreground))"
                          strokeDasharray="4 2"
                          label={{ value: `${turnDays}d`, position: "insideTopRight", fontSize: 10 }}
                        />
                        {curveA !== null && (
                          <Line type="monotone" dataKey="a" stroke="hsl(var(--chart-1))" strokeWidth={2} dot={false} name="a" connectNulls={false} />
                        )}
                        {curveB !== null && (
                          <Line type="monotone" dataKey="b" stroke="hsl(var(--chart-2))" strokeWidth={2} dot={false} name="b" connectNulls={false} />
                        )}
                        {compResult.extraLenders.map((l, i) => curveExtras[i] !== null && (
                          <Line key={l.id} type="monotone" dataKey={`extra_${i}`} stroke={EXTRA_COLORS[i % EXTRA_COLORS.length]} strokeWidth={2} dot={false} name={`extra_${i}`} connectNulls={false} />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                    {(() => {
                      const legendItems = [
                        { name: compResult.lA.name, color: "hsl(var(--chart-1))", cost: atA, hasData: curveA !== null },
                        { name: compResult.lB.name, color: "hsl(var(--chart-2))", cost: atB, hasData: curveB !== null },
                        ...compResult.extraLenders.map((l, i) => ({
                          name: l.name,
                          color: EXTRA_COLORS[i % EXTRA_COLORS.length],
                          cost: atExtras[i] ?? null,
                          hasData: curveExtras[i] !== null,
                        })),
                      ].sort((a, b) => {
                        if (a.cost === null && b.cost === null) return 0;
                        if (a.cost === null) return 1;
                        if (b.cost === null) return -1;
                        return b.cost - a.cost;
                      });
                      return (
                        <div className="flex items-center justify-center gap-6 mt-1 flex-wrap">
                          {legendItems.map((item) =>
                            item.hasData ? (
                              <div key={item.name} className="flex items-center gap-1.5">
                                <div className="w-5 h-0.5 rounded" style={{ background: item.color }} />
                                <span className="text-xs text-muted-foreground">{item.name}</span>
                              </div>
                            ) : (
                              <span key={item.name} className="text-xs text-muted-foreground italic">{t("floorplan:headToHead.noDataItalic", { name: item.name })}</span>
                            )
                          )}
                        </div>
                      );
                    })()}
                  </>
                );
              })()}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

// ─── Statement History per lender ─────────────────────────────────────────────

function LenderHistory({ lenders }: { lenders: Lender[] }) {
  const { t } = useTranslation(["floorplan", "common"]);
  const [selectedId, setSelectedId] = useState<string | null>(lenders[0]?.id ?? null);
  const qc = useQueryClient();
  const { toast } = useToast();

  const { data: statements = [], isLoading } = useQuery<any[]>({
    queryKey: ["/api/parsed-statements"],
  });

  const lenderStmts = statements.filter((s) =>
    selectedId ? s.lenderId === selectedId : true
  );

  const [deletingStmtId, setDeletingStmtId] = useState<string | null>(null);
  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/parsed-statements/${id}`),
    onMutate: (id) => setDeletingStmtId(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["/api/parsed-statements"] }); qc.invalidateQueries({ queryKey: ["/api/savings/overview"] }); toast({ title: t("floorplan:toast.deleted") }); setDeletingStmtId(null); },
    onError: (err: any) => { toast({ title: t("floorplan:toast.failedDeleteStatement"), description: err.message, variant: "destructive" }); setDeletingStmtId(null); },
  });

  const [reextractingId, setReextractingId] = useState<string | null>(null);
  const reextractMutation = useMutation({
    mutationFn: (id: string) => apiRequest("POST", `/api/parsed-statements/${id}/reextract`),
    onMutate: (id) => setReextractingId(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/parsed-statements"] });
      toast({ title: t("floorplan:toast.reextracted"), description: t("floorplan:toast.reextractedDesc") });
      setReextractingId(null);
    },
    onError: (err: any) => {
      toast({ title: t("floorplan:toast.reextractionFailed"), description: err.message, variant: "destructive" });
      setReextractingId(null);
    },
  });

  // APR history chart for selected lender
  const aprHistory = lenderStmts
    .filter((s) => s.totalInterestPaid && s.averageBalance && s.periodDays)
    .map((s) => {
      const interest = n(s.totalInterestPaid);
      const fees = n(s.totalFeesPaid);
      const balance = n(s.averageBalance);
      const days = s.periodDays ?? 30;
      const trueCost = interest + fees;
      const apr = balance > 0 ? (trueCost / balance) * (365 / days) * 100 : 0;
      return {
        label: s.statementPeriodStart ?? s.createdAt?.slice(0, 10) ?? "—",
        apr: parseFloat(apr.toFixed(2)),
        cost: trueCost,
      };
    })
    .reverse();

  return (
    <div className="space-y-4">
      {/* Lender selector */}
      {lenders.length > 1 && (
        <div className="flex gap-2 flex-wrap">
          {lenders.map((l) => (
            <Button
              key={l.id}
              size="sm"
              variant={selectedId === l.id ? "default" : "outline"}
              onClick={() => setSelectedId(l.id)}
              data-testid={`history-lender-${l.id}`}
            >
              {l.name}
            </Button>
          ))}
        </div>
      )}

      {/* APR trend chart */}
      {aprHistory.length >= 2 && (
        <Card>
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-sm font-medium">{t("floorplan:history.effIntRateHistory")}</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <ResponsiveContainer width="100%" height={140}>
              <LineChart data={aprHistory}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border/50" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 11 }} unit="%" width={40} />
                <Tooltip formatter={(v: number) => [`${v.toFixed(2)}%`, t("floorplan:history.intRate")]} />
                <Line dataKey="apr" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Statement list */}
      {isLoading ? (
        <div className="space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-14 w-full rounded-lg" />)}</div>
      ) : lenderStmts.length === 0 ? (
        <div className="py-10 text-center">
          <HistoryIcon className="w-8 h-8 text-muted-foreground/40 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">{t("floorplan:history.noStatements")}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {lenderStmts.map((stmt) => {
            const interest = n(stmt.totalInterestPaid);
            const fees = n(stmt.totalFeesPaid);
            const balance = n(stmt.averageBalance);
            const days = stmt.periodDays ?? 30;
            const trueCost = interest + fees;
            const apr = balance > 0 ? ((trueCost / balance) * (365 / days) * 100).toFixed(2) : null;

            return (
              <div key={stmt.id} className="flex items-center justify-between p-3 rounded-lg border bg-card gap-3" data-testid={`stmt-${stmt.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{stmt.fileName}</p>
                  <p className="text-xs text-muted-foreground">
                    {stmt.statementPeriodStart ?? stmt.createdAt?.slice(0, 10)}
                    {stmt.unitsSold ? t("floorplan:history.unitsSuffix", { count: stmt.unitsSold }) : ""}
                    {stmt.periodDays ? t("floorplan:history.daysSuffix", { count: stmt.periodDays }) : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {interest > 0 && <Badge variant="secondary" className="text-xs">{t("floorplan:history.totalCost", { value: `$${(interest + fees).toLocaleString(intlLocale(), { maximumFractionDigits: 0 })}` })}</Badge>}
                  {apr && <Badge variant="secondary" className="text-xs">{t("floorplan:history.aprBadge", { value: apr })}</Badge>}
                  <Button
                    size="icon" variant="ghost" className="h-7 w-7"
                    onClick={() => reextractMutation.mutate(stmt.id)}
                    disabled={reextractingId === stmt.id}
                    title={t("floorplan:history.reextract")}
                    data-testid={`reextract-stmt-${stmt.id}`}
                  >
                    {reextractingId === stmt.id
                      ? <RefreshCw className="w-3.5 h-3.5 animate-spin text-primary" />
                      : <RefreshCw className="w-3.5 h-3.5 text-muted-foreground" />}
                  </Button>
                  <Button
                    size="sm" variant="ghost" className="h-7 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                    onClick={() => deleteMutation.mutate(stmt.id)}
                    disabled={deletingStmtId === stmt.id}
                    data-testid={`delete-stmt-${stmt.id}`}
                  >
                    {deletingStmtId === stmt.id
                      ? t("floorplan:history.deleting")
                      : <Trash2 className="w-3.5 h-3.5" />}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── New/Edit Lender Dialog ───────────────────────────────────────────────────

function LenderDialog({
  open, onOpenChange, editLender, prefilledTerms, pendingStatementId, allStatements, onStatementLinked, defaultTab,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  editLender: Lender | null;
  prefilledTerms?: Partial<TermsFormState>;
  pendingStatementId?: string | null;
  allStatements?: any[];
  onStatementLinked?: () => void;
  defaultTab?: "terms" | "upload";
}) {
  const { t } = useTranslation(["floorplan", "common"]);
  const [innerTab, setInnerTab] = useState<"terms" | "upload">(defaultTab ?? "terms");
  const [form, setForm] = useState<TermsFormState>(editLender ? lenderToTermsForm(editLender) : emptyTermsForm());
  const [tiers, setTiers] = useState<TierFormState[]>(() =>
    Array.isArray((editLender as any)?.tiers) ? (editLender as any).tiers.map(dbTierToForm) : []
  );
  const [saving, setSaving] = useState(false);
  const [savedLenderId, setSavedLenderId] = useState<string | null>(editLender?.id ?? null);
  const [savedLenderName, setSavedLenderName] = useState<string>(editLender?.name ?? "");
  const [isScanning, setIsScanning] = useState(false);
  const minimizeHandlerRef = useRef<(() => void) | null>(null);
  const qc = useQueryClient();
  const { toast } = useToast();

  const recentStmt = editLender
    ? stmtsForLender(allStatements ?? [], editLender)[0] ?? null
    : null;

  const lenderStmts = editLender ? stmtsForLender(allStatements ?? [], editLender) : [];

  const [deletingDialogStmtId, setDeletingDialogStmtId] = useState<string | null>(null);
  const [linkingStmtId, setLinkingStmtId] = useState<string | null>(null);
  const [rescanningStmtId, setRescanningStmtId] = useState<string | null>(null);
  const rescanFileRef = useRef<HTMLInputElement>(null);
  const rescanTargetLenderIdRef = useRef<string | null>(null);
  const rescanTargetStmtIdRef = useRef<string | null>(null);

  const handleRescanClick = (lenderId: string | null, stmtId: string) => {
    rescanTargetLenderIdRef.current = lenderId;
    rescanTargetStmtIdRef.current = stmtId;
    rescanFileRef.current?.click();
  };

  const handleRescanFileSelected = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!rescanFileRef.current) return;
    rescanFileRef.current.value = "";
    if (!file) {
      rescanTargetStmtIdRef.current = null;
      return;
    }
    setRescanningStmtId(rescanTargetStmtIdRef.current);
    try {
      const { uploadId } = await uploadFileDirect(file, "floorplan");
      const res = await fetch("/api/parse-floorplan/finalize", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ uploadId, forceReparse: true, lenderId: rescanTargetLenderIdRef.current || undefined }),
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        toast({ title: t("floorplan:toast.rescanFailed"), description: body.message ?? t("floorplan:toast.uploadError"), variant: "destructive" });
        setRescanningStmtId(null);
        return;
      }
      const data = await res.json();
      if (data.jobId) {
        toast({ title: t("floorplan:toast.rescanning"), description: t("floorplan:toast.rescanningDesc") });
        pollFloorplanJob(data.jobId, () => {
          qc.invalidateQueries({ queryKey: ["/api/parsed-statements"] });
          qc.invalidateQueries({ queryKey: ["/api/floorplan-inventory"] });
          qc.invalidateQueries({ queryKey: ["/api/lenders"] });
        });
      } else {
        qc.invalidateQueries({ queryKey: ["/api/parsed-statements"] });
        qc.invalidateQueries({ queryKey: ["/api/floorplan-inventory"] });
        qc.invalidateQueries({ queryKey: ["/api/lenders"] });
        toast({ title: t("floorplan:toast.rescanComplete") });
      }
    } catch (err: any) {
      toast({ title: t("floorplan:toast.rescanFailed"), description: err.message, variant: "destructive" });
    } finally {
      setRescanningStmtId(null);
    }
  }, [qc, toast]);
  const stmtDeleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/parsed-statements/${id}`),
    onMutate: (id) => setDeletingDialogStmtId(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/parsed-statements"] });
      qc.invalidateQueries({ queryKey: ["/api/savings/overview"] });
      toast({ title: t("floorplan:toast.statementRemoved") });
      setDeletingDialogStmtId(null);
    },
    onError: () => { toast({ title: t("floorplan:toast.failedRemoveStatement"), variant: "destructive" }); setDeletingDialogStmtId(null); },
  });
  const linkStatementMutation = useMutation({
    mutationFn: async (stmtId: string) => {
      const res = await apiRequest("PATCH", `/api/parsed-statements/${stmtId}`, { lenderId: savedLenderId });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message ?? "Link failed");
      return res.json();
    },
    onMutate: (stmtId) => setLinkingStmtId(stmtId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/parsed-statements"] });
      qc.invalidateQueries({ queryKey: ["/api/lenders"] });
      qc.invalidateQueries({
        predicate: (q) => typeof q.queryKey[0] === "string" && q.queryKey[0].startsWith("/api/lenders/comparison-stats"),
      });
      toast({ title: t("floorplan:toast.statementLinked") });
      setLinkingStmtId(null);
    },
    onError: (e: any) => {
      toast({ title: t("floorplan:toast.linkFailed"), description: e.message, variant: "destructive" });
      setLinkingStmtId(null);
    },
  });
  // Statements that parsed with a matching lenderName but no lenderId yet — can be linked with one click.
  const unlinkedStmts = useMemo(() => {
    if (!savedLenderId || !savedLenderName) return [];
    const myName = savedLenderName.toLowerCase().trim();
    const linkedIds = new Set((allStatements ?? []).filter((s: any) => s.lenderId === savedLenderId).map((s: any) => s.id));
    return (allStatements ?? []).filter((s: any) => {
      if (linkedIds.has(s.id)) return false;
      if (s.lenderId) return false;
      const sName = (s.lenderName ?? "").toLowerCase().trim();
      if (!sName) return false;
      return sName.includes(myName) || myName.includes(sName);
    });
  }, [savedLenderId, savedLenderName, allStatements]);
  const stmtAvgTurnDays = lenderStmts.length > 0
    ? lenderStmts.reduce((s: number, st: any) => s + (parseFloat(st.avgDaysInStock) || 0), 0) / lenderStmts.length
    : null;
  const stmtAvgVehicleValue = (() => {
    const recvs = lenderStmts.flatMap((st: any) => {
      try { return JSON.parse(st.receivablesJson || "[]"); } catch { return []; }
    }).filter((r: any) => (r.principal ?? 0) > 0);
    if (!recvs.length) return null;
    return recvs.reduce((s: number, r: any) => s + r.principal, 0) / recvs.length;
  })();

  useEffect(() => {
    if (open) {
      if (editLender) {
        setForm(lenderToTermsForm(editLender));
        setTiers(Array.isArray((editLender as any)?.tiers) ? (editLender as any).tiers.map(dbTierToForm) : []);
      } else if (prefilledTerms && Object.keys(prefilledTerms).length > 0) {
        setForm({ ...emptyTermsForm(), ...prefilledTerms });
        setTiers([]);
      } else {
        setForm(emptyTermsForm());
        setTiers([]);
      }
      setSavedLenderId(editLender?.id ?? null);
      setSavedLenderName(editLender?.name ?? "");
      setInnerTab(defaultTab ?? "terms");
    }
  }, [open, editLender, defaultTab]);

  const handleChange = (k: keyof TermsFormState, v: string | boolean | null) =>
    setForm((f) => ({ ...f, [k]: v }));

  const handleSaveTerms = async () => {
    if (!form.name.trim()) {
      toast({ title: t("floorplan:toast.nameRequired"), variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        period1Days: form.period1Days ? parseInt(form.period1Days) : null,
        period2Days: form.period2Days ? parseInt(form.period2Days) : null,
        period3Days: form.period3Days ? parseInt(form.period3Days) : null,
        period4Days: form.period4Days ? parseInt(form.period4Days) : null,
        aprRate: form.aprRate || null,
        feePeriod1: form.feePeriod1 || null,
        feePeriod2: form.feePeriod2 || null,
        feePeriod3: form.feePeriod3 || null,
        feePeriod4: form.feePeriod4 || null,
        curtailment1Type: form.curtailment1Type,
        curtailment1Value: form.curtailment1Value || null,
        curtailment2Type: form.curtailment2Type,
        curtailment2Value: form.curtailment2Value || null,
        curtailmentPct1: form.curtailmentPct1 || null,
        curtailmentPct2: form.curtailmentPct2 || null,
        curtailmentPct3: form.curtailmentPct3 || null,
        avgUnitsPerMonth: form.avgUnitsPerMonth || null,
        pricingType: form.pricingType,
        fixedFeePerUnit: form.fixedFeePerUnit || null,
      };

      let result: any;
      if (editLender) {
        const res = await apiRequest("PATCH", `/api/lenders/${editLender.id}`, payload);
        result = await res.json();
      } else {
        const res = await apiRequest("POST", "/api/lenders", payload);
        result = await res.json();
      }

      const newLenderId = result.id ?? editLender?.id ?? null;

      // Sync tiers if lender has tiered pricing
      if (newLenderId && tiers.length > 0) {
        const tiersPayload = tiers.map((t) => ({
          id: t.id,
          threshold: t.threshold ? parseFloat(t.threshold) : 0,
          period1Days: t.period1Days ? parseInt(t.period1Days) : null,
          period2Days: t.period2Days ? parseInt(t.period2Days) : null,
          period3Days: t.period3Days ? parseInt(t.period3Days) : null,
          period4Days: t.period4Days ? parseInt(t.period4Days) : null,
          aprRate: t.aprRate || null,
          feePeriod1: t.feePeriod1 || null,
          feePeriod2: t.feePeriod2 || null,
          feePeriod3: t.feePeriod3 || null,
          feePeriod4: t.feePeriod4 || null,
          curtailment1Type: t.curtailment1Type,
          curtailment1Value: t.curtailment1Value || null,
          curtailment2Type: t.curtailment2Type,
          curtailment2Value: t.curtailment2Value || null,
        }));
        try {
          await apiRequest("POST", `/api/lenders/${newLenderId}/tiers/sync`, { tiers: tiersPayload });
        } catch (e) {
          console.error("Tiers sync failed:", e);
        }
      } else if (newLenderId && tiers.length === 0) {
        // Clear tiers if tiered pricing was disabled
        try {
          await apiRequest("POST", `/api/lenders/${newLenderId}/tiers/sync`, { tiers: [] });
        } catch (e) {
          console.error("Tiers clear failed:", e);
        }
      }

      qc.invalidateQueries({ queryKey: ["/api/lenders"] });
      qc.invalidateQueries({
        predicate: (q) => typeof q.queryKey[0] === "string" && q.queryKey[0].startsWith("/api/lenders/comparison-stats"),
      });
      setSavedLenderId(newLenderId);
      setSavedLenderName(result.name ?? form.name ?? "");

      // If there's a pending uploaded statement (from the "Analyze a Statement" flow), link it now.
      // Signal save-completed to the parent immediately (before the link attempt) so that the
      // dialog close handler will never delete the uploaded statement — even if the PATCH fails,
      // the statement can be recovered via "Link existing" on the new lender card.
      if (!editLender && newLenderId && pendingStatementId) {
        onStatementLinked?.();
        try {
          const linkRes = await apiRequest("PATCH", `/api/parsed-statements/${pendingStatementId}`, { lenderId: newLenderId });
          if (linkRes.ok) {
            qc.invalidateQueries({ queryKey: ["/api/parsed-statements"] });
            // Re-invalidate lenders after the link completes so the card re-renders
            // with hasStatement=true (the first invalidation above happened before the link).
            qc.invalidateQueries({ queryKey: ["/api/lenders"] });
            qc.invalidateQueries({
              predicate: (q) => typeof q.queryKey[0] === "string" && q.queryKey[0].startsWith("/api/lenders/comparison-stats"),
            });
          } else {
            const errBody = await linkRes.json().catch(() => ({}));
            console.error("[statement-link] PATCH returned non-OK:", errBody);
            toast({ title: t("floorplan:toast.statementNotLinked"), description: errBody.message ?? t("floorplan:toast.couldNotLinkToLender"), variant: "destructive" });
          }
        } catch (e: any) {
          console.error("[statement-link] PATCH threw:", e);
          toast({ title: t("floorplan:toast.statementNotLinked"), description: t("floorplan:toast.networkErrorSaved"), variant: "destructive" });
        }
      }

      toast({ title: editLender ? t("floorplan:toast.termsSaved") : t("floorplan:toast.lenderAdded"), description: t("floorplan:toast.termsSavedFor", { name: result.name || form.name || t("floorplan:toast.lenderFallback") }) });
      onOpenChange(false);
    } catch (err: any) {
      toast({ title: t("floorplan:toast.error"), description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={isScanning ? () => {} : onOpenChange}>
      {/* Hidden file input used by the per-statement Rescan button */}
      <input
        ref={rescanFileRef}
        type="file"
        accept=".pdf"
        className="hidden"
        data-testid="input-rescan-file"
        onChange={handleRescanFileSelected}
      />
      <DialogContent
        className={`max-w-5xl max-h-[90vh] overflow-y-auto${isScanning ? " [&>button]:hidden" : ""}`}
        onInteractOutside={(e) => { if (isScanning) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (isScanning) e.preventDefault(); }}
      >
        {isScanning && (
          <div
            role="button"
            tabIndex={0}
            className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100 transition-opacity inline-flex items-center gap-1.5 text-xs font-medium text-foreground cursor-pointer"
            data-testid="button-minimize-scanner"
            onClick={() => minimizeHandlerRef.current?.()}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") minimizeHandlerRef.current?.(); }}
          >
            <Minimize2 className="w-4 h-4" />
            {t("floorplan:lenderDialog.minimize")}
          </div>
        )}
        <DialogHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <DialogTitle>{editLender ? t("floorplan:lenderDialog.editTitle", { name: editLender.name }) : t("floorplan:lenderDialog.newTitle")}</DialogTitle>
              <DialogDescription className="mt-1">
                {t("floorplan:lenderDialog.desc")}
              </DialogDescription>
            </div>
            {recentStmt && (
              <a
                href={`/api/parsed-statements/${recentStmt.id}/file`}
                target="_blank"
                rel="noopener noreferrer"
                className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-muted/40 hover:bg-muted/70 transition-colors text-xs font-medium text-foreground group"
                data-testid="link-view-statement"
              >
                <FileText className="w-3.5 h-3.5 text-primary shrink-0" />
                <span className="max-w-[140px] truncate" title={recentStmt.fileName}>{recentStmt.fileName}</span>
              </a>
            )}
          </div>
        </DialogHeader>

        <Tabs value={innerTab} onValueChange={(v) => setInnerTab(v as any)}>
          <TabsList>
            <TabsTrigger value="terms" data-testid="tab-define-terms">
              <Calculator className="w-3.5 h-3.5 mr-1.5" />{t("floorplan:lenderDialog.defineTerms")}
            </TabsTrigger>
            <TabsTrigger value="upload" data-testid="tab-upload-statement">
              <Upload className="w-3.5 h-3.5 mr-1.5" />{t("floorplan:lenderDialog.statements")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="terms" className="mt-4">
            <div>
              <TermsForm form={form} onChange={handleChange} avgTurnDays={stmtAvgTurnDays} avgVehicleValue={stmtAvgVehicleValue} onSave={handleSaveTerms} saving={saving} tiers={tiers} onTiersChange={setTiers} />
            </div>
            <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
              <Button variant="outline" onClick={() => onOpenChange(false)}>{t("floorplan:lenderDialog.cancel")}</Button>
              <Button onClick={handleSaveTerms} disabled={saving} data-testid="button-save-terms">
                {saving ? t("floorplan:lenderDialog.saving") : t("floorplan:lenderDialog.save")}
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="upload" className="mt-4">
            {savedLenderId ? (
              <div className="space-y-4">
                {/* Existing statements list */}
                {lenderStmts.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      {t("floorplan:lenderDialog.uploadedStatements", { count: lenderStmts.length })}
                    </p>
                    {lenderStmts.map((s: any) => {
                      const interest = parseFloat(s.totalInterestPaid ?? 0);
                      const fees = parseFloat(s.totalFeesPaid ?? 0);
                      const total = interest + fees;
                      let vehicleCount = -1;
                      try { vehicleCount = JSON.parse(s.receivablesJson ?? "[]").length; } catch {}
                      const hasZeroVehicles = vehicleCount === 0;
                      return (
                        <div key={s.id} className={`flex items-start justify-between p-2.5 rounded-lg border bg-card gap-2${hasZeroVehicles ? " border-amber-300 dark:border-amber-700" : ""}`}>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium truncate">{s.fileName}</p>
                            <p className="text-xs text-muted-foreground">
                              {s.statementPeriodStart && s.statementPeriodEnd
                                ? `${s.statementPeriodStart} – ${s.statementPeriodEnd}`
                                : s.createdAt?.slice(0, 10) ?? "—"}
                            </p>
                            <div className="flex items-center flex-wrap gap-1.5 mt-1">
                              {vehicleCount >= 0 && (
                                <span
                                  className={`text-xs px-1.5 py-0.5 rounded font-medium ${hasZeroVehicles ? "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400" : "bg-muted text-muted-foreground"}`}
                                  data-testid={`text-vehicle-count-${s.id}`}
                                >
                                  {t("floorplan:lenderDialog.vehicles", { count: vehicleCount })}
                                </span>
                              )}
                              {interest > 0 && (
                                <span className="text-xs text-muted-foreground">{t("floorplan:lenderDialog.intAmount", { value: interest.toLocaleString(intlLocale(), { maximumFractionDigits: 0 }) })}</span>
                              )}
                              {fees > 0 && (
                                <span className="text-xs text-muted-foreground">{t("floorplan:lenderDialog.feesAmount", { value: fees.toLocaleString(intlLocale(), { maximumFractionDigits: 0 }) })}</span>
                              )}
                              {total > 0 && (
                                <span className="text-xs font-medium">{t("floorplan:lenderDialog.totalAmount", { value: total.toLocaleString(intlLocale(), { maximumFractionDigits: 0 }) })}</span>
                              )}
                              {s.status && (
                                <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground capitalize">{s.status.replace(/_/g, " ")}</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            {s.filePath && (
                              <a
                                href={`/api/parsed-statements/${s.id}/file`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-1.5 rounded hover:bg-muted/60 transition-colors"
                                title={t("floorplan:lenderDialog.viewPdf")}
                              >
                                <FileText className="w-3.5 h-3.5 text-primary" />
                              </a>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              className={`h-7 px-2 text-xs ${hasZeroVehicles ? "text-amber-600 hover:text-amber-700 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-900/30" : "text-muted-foreground hover:text-foreground"}`}
                              onClick={() => handleRescanClick(savedLenderId, s.id)}
                              disabled={rescanningStmtId === s.id}
                              title={t("floorplan:lenderDialog.reupload")}
                              data-testid={`button-rescan-stmt-${s.id}`}
                            >
                              {rescanningStmtId === s.id ? (
                                t("floorplan:lenderDialog.uploading")
                              ) : (
                                <><RefreshCw className="w-3 h-3 mr-1" />{t("floorplan:lenderDialog.rescan")}</>
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                              onClick={() => stmtDeleteMutation.mutate(s.id)}
                              disabled={deletingDialogStmtId === s.id}
                              data-testid={`dialog-remove-stmt-${s.id}`}
                            >
                              {deletingDialogStmtId === s.id ? (
                                t("floorplan:lenderDialog.deleting")
                              ) : (
                                <><Trash2 className="w-3 h-3 mr-1" />{t("floorplan:lenderDialog.remove")}</>
                              )}
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Unlinked statements that match this lender by name */}
                {unlinkedStmts.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      {t("floorplan:lenderDialog.unlinkedStatements", { count: unlinkedStmts.length })}
                    </p>
                    <p className="text-xs text-muted-foreground -mt-1">
                      {t("floorplan:lenderDialog.unlinkedDesc")}
                    </p>
                    {unlinkedStmts.map((s: any) => {
                      const interest = parseFloat(s.totalInterestPaid ?? 0);
                      const fees = parseFloat(s.totalFeesPaid ?? 0);
                      const total = interest + fees;
                      return (
                        <div key={s.id} className="flex items-start justify-between p-2.5 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30 gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium truncate">{s.fileName}</p>
                            <p className="text-xs text-muted-foreground">
                              {s.lenderName && <span className="mr-1.5">{s.lenderName}</span>}
                              {s.statementPeriodStart && s.statementPeriodEnd
                                ? `${s.statementPeriodStart} – ${s.statementPeriodEnd}`
                                : s.createdAt?.slice(0, 10) ?? "—"}
                            </p>
                            <div className="flex items-center flex-wrap gap-1.5 mt-1">
                              {interest > 0 && (
                                <span className="text-xs text-muted-foreground">{t("floorplan:lenderDialog.intAmount", { value: interest.toLocaleString(intlLocale(), { maximumFractionDigits: 0 }) })}</span>
                              )}
                              {fees > 0 && (
                                <span className="text-xs text-muted-foreground">{t("floorplan:lenderDialog.feesAmount", { value: fees.toLocaleString(intlLocale(), { maximumFractionDigits: 0 }) })}</span>
                              )}
                              {total > 0 && (
                                <span className="text-xs font-medium">{t("floorplan:lenderDialog.totalAmount", { value: total.toLocaleString(intlLocale(), { maximumFractionDigits: 0 }) })}</span>
                              )}
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-2 shrink-0 text-xs"
                            onClick={() => linkStatementMutation.mutate(s.id)}
                            disabled={linkingStmtId === s.id}
                            data-testid={`button-link-stmt-${s.id}`}
                          >
                            {linkingStmtId === s.id ? t("floorplan:lenderDialog.linking") : t("floorplan:lenderDialog.linkToThisLender")}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Add new statement */}
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                    {t("floorplan:lenderDialog.addStatement")}
                  </p>
                  <ErrorBoundary label="Upload">
                  <UploadDropzone
                    lenderId={savedLenderId}
                    lenderName={savedLenderName}
                    onMinimize={() => onOpenChange(false)}
                    onScanningChange={setIsScanning}
                    minimizeHandlerRef={minimizeHandlerRef}
                    forceReparse={true}
                    onComplete={(data: any) => {
                      qc.invalidateQueries({ queryKey: ["/api/parsed-statements"] });
                      qc.invalidateQueries({ queryKey: ["/api/floorplan-inventory"] });
                      qc.invalidateQueries({ queryKey: ["/api/lenders"] });
                      qc.invalidateQueries({
                        predicate: (q) => typeof q.queryKey[0] === "string" && q.queryKey[0].startsWith("/api/lenders/comparison-stats"),
                      });
                      if (data?.termsApplied && data?.lenderTerms) {
                        const lt = data.lenderTerms;
                        const rawRecvs2: any[] = data?.parsed?.receivables ?? [];
                        const trueAprAnnotation2 = computeTrueAprFromRows(rawRecvs2);
                        const interestOnlyApr2 = (!trueAprAnnotation2.isEstimate && !trueAprAnnotation2.insufficientData && trueAprAnnotation2.apr != null)
                          ? trueAprAnnotation2.apr
                          : lt.statedAprRate;
                        setForm((f) => ({
                          ...f,
                          ...(lt.period1Days != null ? { period1Days: String(lt.period1Days) } : {}),
                          ...(lt.period2Days != null ? { period2Days: String(lt.period2Days) } : {}),
                          ...(lt.period3Days != null ? { period3Days: String(lt.period3Days) } : {}),
                          ...(lt.period4Days != null ? { period4Days: String(lt.period4Days) } : {}),
                          ...(lt.feePeriod1 != null ? { feePeriod1: String(lt.feePeriod1) } : {}),
                          ...(lt.feePeriod2 != null ? { feePeriod2: String(lt.feePeriod2) } : {}),
                          ...(lt.feePeriod3 != null ? { feePeriod3: String(lt.feePeriod3) } : {}),
                          ...(lt.feePeriod4 != null ? { feePeriod4: String(lt.feePeriod4) } : {}),
                          ...(interestOnlyApr2 != null ? { aprRate: String(interestOnlyApr2) } : {}),
                        }));
                        toast({ title: t("floorplan:toast.termsApplied"), description: t("floorplan:toast.termsAppliedDesc") });
                      }
                    }}
                  />
                  </ErrorBoundary>
                </div>
              </div>
            ) : (
              <div className="py-10 text-center">
                <AlertTriangle className="w-8 h-8 text-muted-foreground/40 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">{t("floorplan:lenderDialog.saveTermsFirst")}</p>
                <Button size="sm" className="mt-3" onClick={() => setInnerTab("terms")} data-testid="button-go-to-terms">{t("floorplan:lenderDialog.goToDefineTerms")}</Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

// ─── Add Lender Terms Preview ─────────────────────────────────────────────────
// Extracted from AddLenderChoiceDialog to avoid an IIFE-in-JSX-ternary which
// is technically invalid TSX even though Vite once tolerated it.

function AddLenderTermsPreview({ uploadResult }: { uploadResult: any }) {
  const { t } = useTranslation(["floorplan", "common"]);
  const rawRecvs: any[] = uploadResult?.parsed?.receivables ?? [];
  const trueApr = computeTrueAprFromRows(rawRecvs);
  const lt = uploadResult.lenderTerms;

  const hasValue = !trueApr.insufficientData && !trueApr.noInterestAccrued && trueApr.apr != null;
  const trueAprDisplay = hasValue ? `${trueApr.apr!.toFixed(2)}%` : "—";
  const trueAprLabel = trueApr.isEstimate && hasValue ? t("floorplan:addLenderPreview.interestRateEstimate") : t("floorplan:addLenderPreview.interestRate");

  let footerNote: string;
  if (trueApr.insufficientData) {
    footerNote = t("floorplan:addLenderPreview.insufficientNote", { count: trueApr.usedCount });
  } else if (trueApr.noInterestAccrued) {
    footerNote = t("floorplan:addLenderPreview.noInterestNote");
  } else if (trueApr.isZeroInterest) {
    footerNote = t("floorplan:addLenderPreview.zeroInterestNote", { count: trueApr.usedCount });
  } else if (trueApr.isEstimate) {
    footerNote = t("floorplan:addLenderPreview.estimateNote");
  } else {
    footerNote = t("floorplan:addLenderPreview.computedNote", { used: trueApr.usedCount, excluded: trueApr.excludedCount });
  }

  const hasCurtailmentPcts = lt?.curtailmentPct1 != null || lt?.curtailmentPct2 != null || lt?.curtailmentPct3 != null;
  const curtailmentPctDisplay = hasCurtailmentPcts
    ? [lt?.curtailmentPct1, lt?.curtailmentPct2, lt?.curtailmentPct3]
        .map((p) => (p != null ? `${p}%` : "—"))
        .join(" / ")
    : null;

  const termRows: { label: string; val: string | number | null; prefix?: string; suffix?: string }[] = [
    { label: t("floorplan:addLenderPreview.period1Days"), val: lt?.period1Days ?? null },
    { label: t("floorplan:addLenderPreview.period2Days"), val: lt?.period2Days ?? null },
    { label: t("floorplan:addLenderPreview.period3Days"), val: lt?.period3Days ?? null },
    { label: t("floorplan:addLenderPreview.period4Days"), val: lt?.period4Days ?? null },
    { label: t("floorplan:addLenderPreview.feeT1"), val: lt?.feePeriod1 ?? null, prefix: "$" },
    { label: t("floorplan:addLenderPreview.feeT2"), val: lt?.feePeriod2 ?? null, prefix: "$" },
    { label: t("floorplan:addLenderPreview.feeT3"), val: lt?.feePeriod3 ?? null, prefix: "$" },
    { label: t("floorplan:addLenderPreview.feeT4"), val: lt?.feePeriod4 ?? null, prefix: "$" },
    { label: t("floorplan:addLenderPreview.statedRate"), val: lt?.statedAprRate ?? null, suffix: "%" },
    { label: t("floorplan:addLenderPreview.curtailments"), val: curtailmentPctDisplay },
  ];

  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t("floorplan:addLenderPreview.autoDetectedTerms")}</p>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        {termRows.filter((r) => r.val != null).map(({ label, val, prefix, suffix }) => (
          <div key={label} className="p-2 rounded-lg bg-muted/40">
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="text-sm font-semibold">{prefix ?? ""}{val}{suffix ?? ""}</p>
          </div>
        ))}
        <div className="p-2 rounded-lg bg-primary/10 border border-primary/20">
          <p className="text-xs text-primary font-medium">{trueAprLabel}</p>
          <p className="text-sm font-semibold">{trueAprDisplay}</p>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">{footerNote}</p>
    </div>
  );
}

// ─── Add Lender Choice Dialog ─────────────────────────────────────────────────

function AddLenderChoiceDialog({
  open, onOpenChange, onChooseManual, onAnalyzeAndSave, initialResult, initialError,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onChooseManual: () => void;
  onAnalyzeAndSave: (prefilled: Partial<TermsFormState>, lenderName: string, pendingStatementId: string | null) => void;
  initialResult?: any;
  initialError?: string | null;
}) {
  const { t } = useTranslation(["floorplan", "common"]);
  const [mode, setMode] = useState<"choice" | "upload" | "review">("choice");
  const [uploadResult, setUploadResult] = useState<any>(null);
  const [editedLenderName, setEditedLenderName] = useState<string>("");
  const [isScanning, setIsScanning] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);
  const minimizeHandlerRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (open && initialResult) {
      setUploadResult(initialResult);
      const detected = initialResult?.parsed?.lenderName ?? "";
      setEditedLenderName(detected === "Unknown Lender" ? "" : detected);
      setScanError(null);
      setMode("review");
    } else if (open && initialError) {
      setScanError(initialError);
      setMode("upload");
    } else if (!open) {
      setMode("choice");
      setUploadResult(null);
      setEditedLenderName("");
      setIsScanning(false);
      setScanError(null);
    }
  }, [open, initialResult, initialError]);

  const handleUploadComplete = (data: any) => {
    setScanError(null);
    setUploadResult(data);
    const detected = data?.parsed?.lenderName ?? "";
    setEditedLenderName(detected === "Unknown Lender" ? "" : detected);
    setMode("review");
  };

  const handleCreateFromStatement = () => {
    const lt = uploadResult?.lenderTerms;
    const lenderName = editedLenderName.trim() || uploadResult?.parsed?.lenderName || "";
    // Compute True APR from uncurtailed vehicles (curtailment-filtered)
    const rawRecvs: any[] = uploadResult?.parsed?.receivables ?? [];
    const trueAprAnnotation = computeTrueAprFromRows(rawRecvs);
    const interestOnlyApr = (!trueAprAnnotation.isEstimate && !trueAprAnnotation.insufficientData && trueAprAnnotation.apr != null)
      ? trueAprAnnotation.apr
      : (lt?.statedAprRate ?? null);
    const prefilled: Partial<TermsFormState> = {
      ...(lenderName && lenderName !== "Unknown Lender" ? { name: lenderName } : {}),
      ...(lt?.period1Days ? { period1Days: String(lt.period1Days) } : {}),
      ...(lt?.period2Days ? { period2Days: String(lt.period2Days) } : {}),
      ...(lt?.period3Days ? { period3Days: String(lt.period3Days) } : {}),
      ...(lt?.period4Days ? { period4Days: String(lt.period4Days) } : {}),
      ...(lt?.feePeriod1 ? { feePeriod1: String(lt.feePeriod1) } : {}),
      ...(lt?.feePeriod2 ? { feePeriod2: String(lt.feePeriod2) } : {}),
      ...(lt?.feePeriod3 ? { feePeriod3: String(lt.feePeriod3) } : {}),
      ...(lt?.feePeriod4 ? { feePeriod4: String(lt.feePeriod4) } : {}),
      ...(interestOnlyApr != null ? { aprRate: String(interestOnlyApr) } : {}),
      ...(rawRecvs.length > 0 ? { avgUnitsPerMonth: String(rawRecvs.length) } : {}),
      ...(lt?.curtailmentPct1 != null ? { curtailmentPct1: String(lt.curtailmentPct1) } : {}),
      ...(lt?.curtailmentPct2 != null ? { curtailmentPct2: String(lt.curtailmentPct2) } : {}),
      ...(lt?.curtailmentPct3 != null ? { curtailmentPct3: String(lt.curtailmentPct3) } : {}),
    };
    onOpenChange(false);
    onAnalyzeAndSave(prefilled, lenderName !== "Unknown Lender" ? lenderName : "", uploadResult?.statement?.id ?? null);
  };

  const hasTermsData = uploadResult?.lenderTerms && Object.values(uploadResult.lenderTerms).some((v) => v != null);

  return (
    <Dialog open={open} onOpenChange={isScanning ? () => {} : onOpenChange}>
      <DialogContent
        className={`max-w-2xl max-h-[90vh] overflow-y-auto${isScanning ? " [&>button]:hidden" : ""}`}
        onInteractOutside={(e) => { if (isScanning) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (isScanning) e.preventDefault(); }}
      >
        {isScanning && (
          <div
            role="button"
            tabIndex={0}
            className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100 transition-opacity inline-flex items-center gap-1.5 text-xs font-medium text-foreground cursor-pointer"
            data-testid="button-minimize-scanner"
            onClick={() => minimizeHandlerRef.current?.()}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") minimizeHandlerRef.current?.(); }}
          >
            <Minimize2 className="w-4 h-4" />
            {t("floorplan:addLenderChoice.minimize")}
          </div>
        )}
        <DialogHeader>
          <DialogTitle>{t("floorplan:addLenderChoice.title")}</DialogTitle>
          <DialogDescription>
            {t("floorplan:addLenderChoice.desc")}
          </DialogDescription>
        </DialogHeader>

        {mode === "choice" && (
          <div className="grid grid-cols-2 gap-4 pt-2">
            <button
              onClick={() => setMode("upload")}
              className="flex flex-col items-center gap-3 p-6 rounded-xl border-2 border-dashed border-border hover:border-primary/50 hover:bg-muted/20 transition-all group text-center"
              data-testid="button-choice-analyze"
            >
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                <FileText className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="font-semibold text-sm">{t("floorplan:addLenderChoice.analyzeStatement")}</p>
                <p className="text-xs text-muted-foreground mt-1">{t("floorplan:addLenderChoice.analyzeDesc")}</p>
              </div>
            </button>
            <button
              onClick={() => { onOpenChange(false); onChooseManual(); }}
              className="flex flex-col items-center gap-3 p-6 rounded-xl border-2 border-dashed border-border hover:border-primary/50 hover:bg-muted/20 transition-all group text-center"
              data-testid="button-choice-manual"
            >
              <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center group-hover:bg-muted/80 transition-colors">
                <Calculator className="w-6 h-6 text-muted-foreground group-hover:text-foreground" />
              </div>
              <div>
                <p className="font-semibold text-sm">{t("floorplan:addLenderChoice.enterManually")}</p>
                <p className="text-xs text-muted-foreground mt-1">{t("floorplan:addLenderChoice.enterManuallyDesc")}</p>
              </div>
            </button>
          </div>
        )}

        {mode === "upload" && (
          <div className="space-y-3">
            <button
              onClick={() => setMode("choice")}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
              data-testid="button-back-to-choice"
            >
              <ChevronRight className="w-3.5 h-3.5 rotate-180" />{t("floorplan:addLenderChoice.back")}
            </button>
            {scanError && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm" data-testid="banner-scan-error">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{t("floorplan:addLenderChoice.extractionFailed")}</span>
              </div>
            )}
            <ErrorBoundary label="Upload">
              <UploadDropzone
                lenderId={null}
                lenderName=""
                onComplete={handleUploadComplete}
                onMinimize={() => onOpenChange(false)}
                onScanningChange={setIsScanning}
                minimizeHandlerRef={minimizeHandlerRef}
                originFlow="addLender"
                forceReparse={true}
              />
            </ErrorBoundary>
          </div>
        )}

        {mode === "review" && uploadResult && (
          <div className="space-y-4 pt-1">
            <button
              onClick={() => { setUploadResult(null); setMode("upload"); }}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
              data-testid="button-upload-different-file"
            >
              <ChevronRight className="w-3.5 h-3.5 rotate-180" />{t("floorplan:addLenderChoice.uploadDifferent")}
            </button>

            <div className="p-3 rounded-lg bg-chart-3/10 border border-chart-3/20 space-y-2">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-chart-3 shrink-0" />
                <p className="text-sm font-semibold text-chart-3">{t("floorplan:addLenderChoice.parsedSuccess")}</p>
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground font-medium">{t("floorplan:addLenderChoice.lenderNameEdit")}</label>
                <Input
                  value={editedLenderName}
                  onChange={(e) => setEditedLenderName(e.target.value)}
                  placeholder={t("floorplan:addLenderChoice.lenderNamePlaceholder")}
                  className="h-9 text-sm font-semibold bg-background"
                  data-testid="input-new-lender-name"
                />
                {editedLenderName !== (uploadResult.parsed?.lenderName ?? "") && (
                  <p className="text-xs text-muted-foreground">
                    {t("floorplan:addLenderChoice.originallyDetected")}<span className="italic">{uploadResult.parsed?.lenderName || t("floorplan:addLenderChoice.unknownLender")}</span>
                  </p>
                )}
              </div>
            </div>

            {hasTermsData ? (
              <AddLenderTermsPreview uploadResult={uploadResult} />
            ) : (
              <div className="p-3 rounded-lg bg-muted/40">
                <p className="text-sm text-muted-foreground">{t("floorplan:addLenderChoice.noTermStructure")}</p>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={() => onOpenChange(false)}>{t("floorplan:addLenderChoice.cancel")}</Button>
              <Button onClick={handleCreateFromStatement} data-testid="button-create-from-statement">
                <Plus className="w-4 h-4 mr-2" />{t("floorplan:addLenderChoice.saveAsLender")}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Delete Lender Dialog ─────────────────────────────────────────────────────

function DeleteLenderDialog({
  lender, onClose, onConfirm, isPending,
}: { lender: Lender | null; onClose: () => void; onConfirm: () => void; isPending?: boolean }) {
  const { t } = useTranslation(["floorplan", "common"]);
  return (
    <Dialog open={!!lender} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("floorplan:deleteDialog.title", { name: lender?.name })}</DialogTitle>
          <DialogDescription>
            {t("floorplan:deleteDialog.desc")}
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={isPending} data-testid="button-cancel-delete-lender">{t("floorplan:deleteDialog.cancel")}</Button>
          <Button variant="destructive" onClick={onConfirm} disabled={isPending} data-testid="button-confirm-delete-lender">
            {isPending ? t("floorplan:deleteDialog.deleting") : t("floorplan:deleteDialog.delete")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

// ─── Aged Inventory Section ──────────────────────────────────────────────────
// Moved here from Operational Analysis page so all floorplan-related cost
// analytics live in one place. Reads from stored op analytics + live statements.

// ─── What-if Pricing Section ──────────────────────────────────────────────────

const RATE_STEP = 0.25;  // percent per click
const FEES_STEP = 5;     // dollars per click

function WhatIfPricingSection({ lenders, allStatements, scenario }: {
  lenders: Lender[];
  allStatements: any[];
  scenario: ScenarioInputs;
}) {
  const { t } = useTranslation(["floorplan", "common"]);
  // Steps: integer count of clicks from zero (negative = reduced, positive = increased)
  const [deltas, setDeltas] = useState<Record<string, { rateSteps: number; feesSteps: number }>>({});
  const [localVehicleValue, setLocalVehicleValue] = useState("");
  const [localDaysOnFloor, setLocalDaysOnFloor] = useState("");

  function adjustDelta(lenderId: string, field: "rateSteps" | "feesSteps", dir: 1 | -1) {
    setDeltas(prev => {
      const cur = prev[lenderId] ?? { rateSteps: 0, feesSteps: 0 };
      return { ...prev, [lenderId]: { ...cur, [field]: cur[field] + dir } };
    });
  }

  function resetDeltas(lenderId: string) {
    setDeltas(prev => ({ ...prev, [lenderId]: { rateSteps: 0, feesSteps: 0 } }));
  }

  const realStats = useMemo(() => computeFloorplanStats(allStatements), [allStatements]);
  // Scenario values take priority over raw AI stats; real stats serve as fallback behind the scenario
  const scenarioVV = scenario.vehicleValue > 0 ? scenario.vehicleValue : (realStats.avgVehicleValue ?? 15000);
  const scenarioDOF = scenario.daysOnFloor > 0 ? scenario.daysOnFloor : (realStats.avgTurnDays ?? 45);

  // Local override > scenario (which already carries any AI-auto-filled values) > hard fallback
  const effectiveVV = localVehicleValue !== "" ? (parseFloat(localVehicleValue) || 0) : scenarioVV;
  const effectiveDOF = localDaysOnFloor !== "" ? (parseFloat(localDaysOnFloor) || 0) : scenarioDOF;

  // Debounce effectiveDOF so comparison-stats isn't fetched on every keystroke
  const [debouncedEffectiveDOF, setDebouncedEffectiveDOF] = useState(effectiveDOF);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedEffectiveDOF(effectiveDOF), 300);
    return () => clearTimeout(t);
  }, [effectiveDOF]);

  // Fetch server-computed comparison-stats (curtailment-filtered, outlier-removed, Westlake-aware)
  // so What-If uses the same rate source as Lender Overviews and Scenario Comparison.
  const { data: wiCompData } = useQuery<{ lenders: ComparisonStat[] }>({
    queryKey: [`/api/lenders/comparison-stats?turnDays=${debouncedEffectiveDOF}`],
    enabled: lenders.length > 0,
    staleTime: 30_000,
  });
  const wiCompStats = wiCompData?.lenders ?? [];

  // Period-aware fee sum: only charge a period's fee if DOF has reached that period's threshold.
  // Period 1 always applies; Period 2 starts after period1Days; Period 3 after p1+p2; etc.
  function periodAwareFees(
    dof: number,
    p1Days: number | null | undefined, p2Days: number | null | undefined,
    p3Days: number | null | undefined, p4Days: number | null | undefined,
    f1: any, f2: any, f3: any, f4: any,
  ): number {
    const c1 = n(p1Days);
    const c2 = c1 + n(p2Days);
    const c3 = c2 + n(p3Days);
    let total = n(f1); // Period 1 always applies (vehicle is floored at all)
    if (c1 > 0 && dof > c1) total += n(f2); // Past Period 1 threshold → Period 2 fee kicks in
    if (c2 > 0 && dof > c2) total += n(f3); // Past Period 2 threshold → Period 3 fee kicks in
    if (c3 > 0 && dof > c3) total += n(f4); // Past Period 3 threshold → Period 4 fee kicks in
    return total;
  }

  const rows = useMemo(() => {
    return lenders.map(lender => {
      const stmts = stmtsForLender(allStatements, lender, lenders);
      const real = computeRealAprForLender(stmts, { manualFeePerUnit: manualFeePerUnitFromLender(lender) });
      const compStat = wiCompStats.find((s) => s.id === lender.id) ?? null;

      // Tier matching: sort tiers descending by threshold, pick highest that effectiveVV qualifies for
      const sortedTiers = ((lender as any).tiers ?? [])
        .slice()
        .sort((a: any, b: any) => n(b.threshold) - n(a.threshold));
      const matchedTier = sortedTiers.find((t: any) => effectiveVV >= n(t.threshold)) ?? null;

      let currentRate: number;
      let manualFees: number;
      if (matchedTier) {
        // Use the matched tier's terms; only apply period fees the DOF has actually reached
        currentRate = n(matchedTier.aprRate);
        manualFees = periodAwareFees(
          effectiveDOF,
          matchedTier.period1Days, matchedTier.period2Days, matchedTier.period3Days, matchedTier.period4Days,
          matchedTier.feePeriod1, matchedTier.feePeriod2, matchedTier.feePeriod3, matchedTier.feePeriod4,
        );
      } else {
        // Rate sourcing cascade (matches Lender Overviews and Scenario Comparison):
        //   1. comparison-stats realInterestRatePct (server-side, curtailment-filtered + outlier-removed)
        //   2. client-side interest-only APR from actual receivables
        //   3. manually entered stated APR
        currentRate = compStat?.realInterestRatePct ?? real.interestOnlyApr ?? (n(lender.aprRate) > 0 ? n(lender.aprRate) : 0);
        manualFees = periodAwareFees(
          effectiveDOF,
          lender.period1Days, lender.period2Days, lender.period3Days, lender.period4Days,
          lender.feePeriod1, lender.feePeriod2, lender.feePeriod3, lender.feePeriod4,
        );
      }
      // Fee priority: prefer avg fees from actual receivables (tier-scoped for tiered lenders),
      // fall back to stated/manual fees only when no usable receivables data exists.
      let wiTierValueMin: number | undefined;
      let wiTierValueMax: number | undefined;
      if (matchedTier) {
        wiTierValueMin = n(matchedTier.threshold);
        // sortedTiers is sorted descending; use the last element among those
        // with threshold > min to get the immediate next higher (not the highest).
        const wiGreaterTiers = sortedTiers.filter((t: any) => n(t.threshold) > wiTierValueMin!);
        const wiNextHigher = wiGreaterTiers.length > 0 ? wiGreaterTiers[wiGreaterTiers.length - 1] : null;
        wiTierValueMax = wiNextHigher ? n(wiNextHigher.threshold) : undefined;
      }
      const receivablesAvgFee = deriveReceivablesFeeForScenario(stmts, lender, effectiveDOF, wiTierValueMin, wiTierValueMax);
      const currentFees = receivablesAvgFee !== null
        ? receivablesAvgFee
        : (manualFees > 0 ? manualFees : 0);

      // Per-lender receivable stats for annual savings projection
      const lenderRecvs: any[] = [];
      for (const s of stmts) {
        try { lenderRecvs.push(...JSON.parse(s.receivablesJson ?? "[]")); } catch {}
      }
      // Unit count: any vehicle with positive principal (including 0 days = floored today)
      const qualRecvs = lenderRecvs.filter((r: any) => (r.principal ?? 0) > 0);
      // For averages (VV and DOF), only use vehicles with recorded days > 0
      const avgRecvs = qualRecvs.filter((r: any) => (r.daysOutstanding ?? 0) > 0);
      const lenderAvgVV = avgRecvs.length > 0
        ? avgRecvs.reduce((s: number, r: any) => s + (r.principal ?? 0), 0) / avgRecvs.length
        : effectiveVV;

      // For tiered-pricing lenders, derive the vehicle value used for interest calculation
      // from receivables whose principal falls within the matched tier's price range —
      // but only when the user hasn't entered a local vehicle value override.
      let calcVV = effectiveVV;
      if (matchedTier && localVehicleValue === "") {
        const tierLower = n(matchedTier.threshold);
        const nextHigherTier = sortedTiers.find((t: any) => n(t.threshold) > tierLower) ?? null;
        const tierUpper = nextHigherTier ? n(nextHigherTier.threshold) : Infinity;
        const tierScopedRecvs = avgRecvs.filter(
          (r: any) => (r.principal ?? 0) >= tierLower && (r.principal ?? 0) < tierUpper
        );
        if (tierScopedRecvs.length > 0) {
          calcVV = tierScopedRecvs.reduce((s: number, r: any) => s + (r.principal ?? 0), 0) / tierScopedRecvs.length;
        } else if (lenderAvgVV > 0) {
          calcVV = lenderAvgVV;
        }
      }

      const currentInterest = calcVV * (currentRate / 100) * (effectiveDOF / 365);
      const currentTotal = currentInterest + currentFees;
      const d = deltas[lender.id] ?? { rateSteps: 0, feesSteps: 0 };
      const rateDelta = d.rateSteps * RATE_STEP;
      const feesDelta = d.feesSteps * FEES_STEP;
      const whatIfRate = currentRate + rateDelta;
      const whatIfFees = Math.max(0, currentFees + feesDelta);
      const whatIfInterest = calcVV * (Math.max(0, whatIfRate) / 100) * (effectiveDOF / 365);
      const whatIfTotal = whatIfInterest + whatIfFees;
      const delta = whatIfTotal - currentTotal;
      const lenderAvgDOF = avgRecvs.length > 0
        ? avgRecvs.reduce((s: number, r: any) => s + (r.daysOutstanding ?? 0), 0) / avgRecvs.length
        : effectiveDOF;
      const lenderUnits = qualRecvs.length;

      // Annual savings uses scenario inputs (calcVV, effectiveDOF) so the projection
      // stays in sync when the user changes vehicle value or days on floor.
      // Annual interest savings per slot = calcVV × (rateDelta / 100)          [APR × principal, annually]
      // Annual fee savings per slot      = effectiveFeeDelta × (365 / effectiveDOF) [fee per turn × turns/yr]
      const effectiveFeeDelta = whatIfFees - currentFees;
      const annualInterestDeltaPerSlot = calcVV > 0 ? calcVV * (rateDelta / 100) : 0;
      const annualFeeDeltaPerSlot = effectiveDOF > 0 ? effectiveFeeDelta * (365 / effectiveDOF) : 0;
      const annualDeltaPerSlot = annualInterestDeltaPerSlot + annualFeeDeltaPerSlot;
      const totalAnnualDelta = annualDeltaPerSlot * lenderUnits;

      return {
        lender, currentRate, currentFees, currentTotal, whatIfTotal, delta,
        hasData: real.stmtCount > 0, rateDelta, feesDelta, d,
        whatIfFees,
        lenderAvgVV, lenderAvgDOF, lenderUnits,
        annualDeltaPerSlot, totalAnnualDelta,
        matchedTier,
      };
    });
  }, [lenders, allStatements, deltas, effectiveVV, effectiveDOF, localVehicleValue, wiCompStats]);

  function buildNarrative(delta: number, rateDelta: number, feesDelta: number, improved: boolean, worse: boolean): string | null {
    if (!improved && !worse) return null;
    const parts: string[] = [];
    if (rateDelta !== 0) parts.push(`${rateDelta < 0 ? "decreasing" : "increasing"} rate by ${Math.abs(rateDelta).toFixed(2)}%`);
    if (feesDelta !== 0) parts.push(`${feesDelta < 0 ? "decreasing" : "increasing"} fees by ${fmt$(Math.abs(feesDelta), 0)}`);
    if (parts.length === 0) return null;
    const action = improved ? "save" : "cost";
    const amount = fmt$(Math.abs(delta), 2);
    const sentence = `${parts.join(" and ")} would ${action} you an extra ${amount}/unit`;
    return sentence.charAt(0).toUpperCase() + sentence.slice(1);
  }

  if (lenders.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Tag className="w-4 h-4 text-primary" />
          <CardTitle className="text-base">{t("floorplan:whatIf.title")}</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">
          {t("floorplan:whatIf.desc")}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Scenario inputs */}
        <div className="grid grid-cols-2 gap-3 p-3 rounded-xl bg-muted/40 border border-border">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">
              {t("floorplan:whatIf.vehicleValue")}
              {localVehicleValue === "" && (
                <span className="ml-1.5 text-muted-foreground font-normal">{t("floorplan:whatIf.fromScenarioValue", { value: fmt$(scenarioVV, 0) })}</span>
              )}
            </Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm pointer-events-none">$</span>
              <Input
                type="text"
                inputMode="numeric"
                className="pl-7"
                placeholder={Math.round(scenarioVV).toLocaleString("en-US")}
                value={localVehicleValue !== "" ? Number(localVehicleValue).toLocaleString("en-US") : ""}
                onChange={(e) => {
                  const raw = e.target.value.replace(/,/g, "").replace(/[^0-9.]/g, "");
                  setLocalVehicleValue(raw);
                }}
                data-testid="input-whatif-vehicle-value"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">
              {t("floorplan:whatIf.daysOnFloor")}
              {localDaysOnFloor === "" && (
                <span className="ml-1.5 text-muted-foreground font-normal">{t("floorplan:whatIf.fromScenarioDays", { days: Math.round(scenarioDOF) })}</span>
              )}
            </Label>
            <Input
              type="number"
              placeholder={String(Math.round(scenarioDOF))}
              value={localDaysOnFloor}
              onChange={(e) => setLocalDaysOnFloor(e.target.value)}
              data-testid="input-whatif-days-on-floor"
            />
          </div>
        </div>

        {/* Per-lender cards — 2-column grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {rows.map(({ lender, currentRate, currentFees, currentTotal, whatIfTotal, delta, hasData, rateDelta, feesDelta, d, whatIfFees, lenderAvgVV, lenderAvgDOF, lenderUnits, annualDeltaPerSlot, totalAnnualDelta, matchedTier }) => {
            const improved = delta < -0.01;
            const worse = delta > 0.01;
            const annualImproved = totalAnnualDelta < -0.01;
            const annualWorse = totalAnnualDelta > 0.01;
            const hasAnnualData = (annualImproved || annualWorse) && lenderUnits > 0;
            const lcolor = getLenderColor(lender.id, lenders);
            const hasDelta = d.rateSteps !== 0 || d.feesSteps !== 0;
            const narrative = buildNarrative(delta, rateDelta, feesDelta, improved, worse);

            return (
              <div key={lender.id} className={`border-2 border-border border-l-4 ${lcolor.leftAccent} rounded-xl p-3 space-y-2.5 flex flex-col`}>
                {/* Header */}
                <div className="flex items-center justify-between flex-wrap gap-1.5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-sm">{lender.name}</p>
                    {matchedTier && (
                      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/20">
                        {t("floorplan:whatIf.tierThreshold", { value: fmt$(n(matchedTier.threshold), 0) })}
                      </span>
                    )}
                    {hasData && (currentRate > 0 || currentFees > 0) && (
                      <span className="text-xs text-muted-foreground">
                        {currentRate > 0 && <>{currentRate.toFixed(2)}%</>}
                        {currentRate > 0 && currentFees > 0 && " · "}
                        {currentFees > 0 && <>{fmt$(currentFees, 0)}{t("floorplan:whatIf.perUnitSuffix")}</>}
                      </span>
                    )}
                    {hasDelta && (
                      <button
                        onClick={() => resetDeltas(lender.id)}
                        className="flex items-center gap-0.5 text-[10px] text-muted-foreground hover:text-foreground transition-colors"
                        data-testid={`button-reset-${lender.id}`}
                      >
                        <RotateCcw className="w-2.5 h-2.5" />{t("floorplan:whatIf.reset")}
                      </button>
                    )}
                  </div>
                  {!hasData && <span className="text-xs text-muted-foreground italic">{t("floorplan:whatIf.noDataYet")}</span>}
                  {(improved || worse) && (
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${improved
                      ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30"
                      : "bg-red-500/10 text-destructive border-destructive/30"}`}>
                      {improved ? t("floorplan:whatIf.saves") : t("floorplan:whatIf.costs")}
                      {Math.abs(delta).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 })}{t("floorplan:whatIf.perUnit")}
                    </span>
                  )}
                </div>

                {/* Steppers */}
                <div className="space-y-2 flex-1">
                  {/* Rate stepper */}
                  <div>
                    <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide mb-1">{t("floorplan:whatIf.rateStepper")}</p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline" size="icon"
                        className="h-8 w-8 shrink-0"
                        onClick={() => adjustDelta(lender.id, "rateSteps", -1)}
                        data-testid={`button-rate-down-${lender.id}`}
                      >
                        <Minus className="w-3.5 h-3.5" />
                      </Button>
                      <div className="flex-1 flex items-center justify-center gap-1.5 h-8 rounded-md bg-muted/50 border border-border px-3">
                        <span className="font-mono text-sm font-semibold tabular-nums">
                          {currentRate > 0 ? `${(currentRate + rateDelta).toFixed(2)}%` : "—"}
                        </span>
                        {d.rateSteps !== 0 && (
                          <span className={`text-[11px] font-semibold ${rateDelta < 0 ? "text-emerald-600 dark:text-emerald-400" : "text-destructive"}`}>
                            ({rateDelta > 0 ? "+" : ""}{rateDelta.toFixed(2)}%)
                          </span>
                        )}
                      </div>
                      <Button
                        variant="outline" size="icon"
                        className="h-8 w-8 shrink-0"
                        onClick={() => adjustDelta(lender.id, "rateSteps", 1)}
                        data-testid={`button-rate-up-${lender.id}`}
                      >
                        <Plus className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>

                  {/* Fees stepper */}
                  <div>
                    <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide mb-1">{t("floorplan:whatIf.feesStepper")}</p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline" size="icon"
                        className="h-8 w-8 shrink-0"
                        onClick={() => adjustDelta(lender.id, "feesSteps", -1)}
                        disabled={whatIfFees <= 0}
                        data-testid={`button-fees-down-${lender.id}`}
                      >
                        <Minus className="w-3.5 h-3.5" />
                      </Button>
                      <div className="flex-1 flex items-center justify-center gap-1.5 h-8 rounded-md bg-muted/50 border border-border px-3">
                        <span className="font-mono text-sm font-semibold tabular-nums">
                          {currentFees > 0 ? fmt$(whatIfFees, 2) : "—"}
                        </span>
                        {d.feesSteps !== 0 && whatIfFees !== currentFees && (
                          <span className={`text-[11px] font-semibold ${whatIfFees < currentFees ? "text-emerald-600 dark:text-emerald-400" : "text-destructive"}`}>
                            ({whatIfFees > currentFees ? "+" : ""}{fmt$(whatIfFees - currentFees, 0)})
                          </span>
                        )}
                      </div>
                      <Button
                        variant="outline" size="icon"
                        className="h-8 w-8 shrink-0"
                        onClick={() => adjustDelta(lender.id, "feesSteps", 1)}
                        data-testid={`button-fees-up-${lender.id}`}
                      >
                        <Plus className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Narrative sentence */}
                {narrative && (
                  <p className={`text-xs leading-snug ${improved ? "text-emerald-600 dark:text-emerald-400" : "text-destructive"}`}>
                    {narrative}
                  </p>
                )}

                {/* Cost comparison */}
                <div className="grid grid-cols-2 gap-1.5 pt-2 border-t border-border">
                  <div className="bg-muted/40 rounded-lg px-2.5 py-2 text-center">
                    <p className="text-[10px] text-muted-foreground mb-0.5">{t("floorplan:whatIf.current")}</p>
                    <p className="text-lg font-bold tabular-nums leading-tight">
                      {currentTotal > 0 ? fmt$(currentTotal, 2) : "—"}
                    </p>
                    <p className="text-[10px] text-muted-foreground">{t("floorplan:whatIf.perUnitFloor")}</p>
                  </div>
                  <div className={`rounded-lg px-2.5 py-2 text-center ${improved
                    ? "bg-emerald-500/5 border border-emerald-500/20"
                    : worse ? "bg-red-500/5 border border-destructive/20"
                    : "bg-muted/40"}`}>
                    <p className="text-[10px] text-muted-foreground mb-0.5">{t("floorplan:whatIf.whatIfLabel")}</p>
                    <p className={`text-lg font-bold tabular-nums leading-tight ${improved
                      ? "text-emerald-600 dark:text-emerald-400"
                      : worse ? "text-destructive" : ""}`}>
                      {whatIfTotal > 0 ? fmt$(whatIfTotal, 2) : "—"}
                    </p>
                    <p className="text-[10px] text-muted-foreground">{t("floorplan:whatIf.perUnitFloor")}</p>
                  </div>
                </div>

                {/* Annual savings projection — shown only when deltas are set and receivable data exists */}
                {hasAnnualData && (
                  <div className={`rounded-xl border p-3 space-y-2 ${annualImproved
                    ? "bg-emerald-500/5 border-emerald-500/25"
                    : "bg-red-500/5 border-destructive/25"}`}>
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {t("floorplan:whatIf.annualImpact")}
                    </p>
                    {/* Basis line */}
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-muted-foreground">
                      <span>{t("floorplan:whatIf.unitsOnFloor", { units: lenderUnits })}</span>
                      <span>{t("floorplan:whatIf.avgTurn", { days: Math.round(lenderAvgDOF) })}</span>
                      <span>{t("floorplan:whatIf.avgPerUnit", { value: fmt$(lenderAvgVV, 0) })}</span>
                    </div>
                    {/* Per-slot and total */}
                    <div className="grid grid-cols-2 gap-1.5">
                      <div className="bg-background/60 rounded-lg px-2.5 py-2 text-center">
                        <p className="text-[10px] text-muted-foreground mb-0.5">{t("floorplan:whatIf.savingsPerMo")}</p>
                        <p className={`text-sm font-bold tabular-nums leading-tight ${annualImproved ? "text-emerald-600 dark:text-emerald-400" : "text-destructive"}`}>
                          {fmt$(Math.abs(totalAnnualDelta / 12), 0)}
                        </p>
                      </div>
                      <div className={`rounded-lg px-2.5 py-2 text-center ${annualImproved
                        ? "bg-emerald-500/10 border border-emerald-500/30"
                        : "bg-red-500/10 border border-destructive/30"}`}>
                        <p className="text-[10px] text-muted-foreground mb-0.5">
                          {annualImproved ? t("floorplan:whatIf.totalAnnualSavings") : t("floorplan:whatIf.totalAnnualExtraCost")}
                        </p>
                        <p className={`text-base font-bold tabular-nums leading-tight ${annualImproved ? "text-emerald-600 dark:text-emerald-400" : "text-destructive"}`}>
                          {fmt$(Math.abs(totalAnnualDelta), 0)}{t("floorplan:whatIf.perYear")}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

export default function FloorplanPage() {
  const { t } = useTranslation(["floorplan", "common"]);
  const { data: authUser } = useQuery<any>({ queryKey: ["/api/auth/me"], retry: false });
  const [showAddChoiceDialog, setShowAddChoiceDialog] = useState(false);
  const [addLenderInitialResult, setAddLenderInitialResult] = useState<any>(undefined);
  const [addLenderScanError, setAddLenderScanError] = useState<string | null>(null);
  const [showLenderDialog, setShowLenderDialog] = useState(false);
  const [lenderDialogInitTab, setLenderDialogInitTab] = useState<"terms" | "upload">("terms");
  const [editLender, setEditLender] = useState<Lender | null>(null);
  const [deleteLender, setDeleteLender] = useState<Lender | null>(null);
  const [analyticsLender, setAnalyticsLender] = useState<Lender | null>(null);
  const [analyticsMode, setAnalyticsMode] = useState<"real" | "scenario">("real");
  const [prefilledTerms, setPrefilledTerms] = useState<Partial<TermsFormState> | undefined>(undefined);
  const [pendingStatementId, setPendingStatementId] = useState<string | null>(null);
  // Ref (not state) so it updates synchronously before onOpenChange's closure reads it.
  // Set to true when handleSaveTerms successfully links the statement; reset on dialog open.
  const pendingStatementLinkedRef = useRef(false);
  const [scenario, setScenario] = useState<ScenarioInputs>({ vehicleValue: 10000, daysOnFloor: 45, avgUnitsPerMonth: 10 });
  const [vehicleValueStr, setVehicleValueStr] = useState("10,000");
  const [daysOnFloorStr, setDaysOnFloorStr] = useState("45");
  const [scannerOpen, setScannerOpen] = useState(false);
  const { toast } = useToast();
  const qc = useQueryClient();
  const scanner = useScannerContext();

  // Auto-open the correct dialog when a background scan requests re-open on this page.
  // If the scan originated from the Add Lender flow and has completed, open the Add Lender
  // dialog in review mode with the parsed result pre-filled rather than the Document Reader.
  // If the scan failed from the Add Lender flow, return to the upload step with an error banner.
  useEffect(() => {
    if (!scanner.reopenRequested) return;
    if (scanner.status === "completed" && scanner.originFlow === "addLender" && scanner.result) {
      setAddLenderInitialResult(scanner.result);
      setAddLenderScanError(null);
      setShowAddChoiceDialog(true);
      scanner.clearReopenRequest();
    } else if (scanner.status === "failed" && scanner.originFlow === "addLender") {
      setAddLenderInitialResult(undefined);
      setAddLenderScanError(scanner.errorMessage || t("floorplan:reader.extractionFailedError"));
      setShowAddChoiceDialog(true);
      scanner.clearReopenRequest();
    } else if (scanner.status === "completed" || scanner.status === "scanning" || scanner.status === "failed") {
      setScannerOpen(true);
    }
  }, [scanner.reopenRequested, scanner.status, scanner.originFlow, scanner.result, scanner.errorMessage]);

  // Scroll to anchor hash on load (e.g. /floorplan#scenario-compare from dashboard)
  useEffect(() => {
    const hash = window.location.hash?.slice(1);
    if (!hash) return;
    const attempt = (tries: number) => {
      const el = document.getElementById(hash);
      if (el) { el.scrollIntoView({ behavior: "smooth", block: "start" }); return; }
      if (tries > 0) setTimeout(() => attempt(tries - 1), 300);
    };
    attempt(10);
  }, []);

  const { data: lenders = [], isLoading: loadingLenders } = useQuery<LenderWithTiers[]>({ queryKey: ["/api/lenders"] });
  const { data: allStatements = [] } = useQuery<any[]>({ queryKey: ["/api/parsed-statements"] });

  // Debounce scenario.daysOnFloor so comparison-stats isn't fetched on every keystroke
  const [debouncedScenarioDays, setDebouncedScenarioDays] = useState(scenario.daysOnFloor);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedScenarioDays(scenario.daysOnFloor), 300);
    return () => clearTimeout(t);
  }, [scenario.daysOnFloor]);

  // Fetch comparison-stats for the Scenario Comparison section so LenderCard and rankedLenders
  // use the same server-computed APR/fee source as the Lender Overviews table.
  const { data: scenarioCompData } = useQuery<{ lenders: ComparisonStat[] }>({
    queryKey: [`/api/lenders/comparison-stats?turnDays=${debouncedScenarioDays}`],
    enabled: lenders.length > 0,
    staleTime: 30_000,
  });
  const scenarioCompStats = scenarioCompData?.lenders ?? [];

  // Only statements linked to active lenders — excludes orphaned uploads
  const linkedStatements = useMemo(
    () => filterStatementsToLenders(allStatements, lenders),
    [allStatements, lenders]
  );

  // Live portfolio stats from per-unit receivables — reacts to any add/delete/replace
  const autoStats = useMemo(() => computeFloorplanStats(linkedStatements), [linkedStatements]);

  const deleteLenderMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/lenders/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/lenders"] });
      qc.invalidateQueries({ queryKey: ["/api/parsed-statements"] });
      qc.invalidateQueries({ queryKey: ["/api/floorplan-inventory"], refetchType: "all" });
      qc.invalidateQueries({ queryKey: ["/api/inventory/enriched"] });
      qc.invalidateQueries({
        predicate: (q) => typeof q.queryKey[0] === "string" && q.queryKey[0].startsWith("/api/lenders/comparison-stats"),
      });
      setDeleteLender(null);
      toast({ title: t("floorplan:toast.lenderDeleted") });
    },
    onError: (err: any) => toast({ title: t("floorplan:toast.failedDeleteLender"), description: err.message, variant: "destructive" }),
  });

  // Sorted lenders: ranked by estimated cost using comparison-stats APR/fees (primary) or client-side fallback
  const { rankedLenders, cheapestCost, secondCheapestCost } = useMemo(() => {
    const withTerms = lenders.filter(lenderHasTerms).map((l) => {
      const terms = dbLenderToTerms(l);
      const stmts = stmtsForLender(allStatements, l, lenders);
      const realApr = computeRealAprForLender(stmts, { manualFeePerUnit: manualFeePerUnitFromLender(l) });
      // APR: prefer comparison-stats (server-side) → client-side realApr → manual
      const compStat = scenarioCompStats.find((s) => s.id === l.id) ?? null;
      const effectiveRate = compStat?.realInterestRatePct ?? (realApr.interestOnlyApr != null ? realApr.interestOnlyApr : (terms.aprRate > 0 ? terms.aprRate : 0));
      const effectiveTerms = { ...terms, aprRate: effectiveRate };
      const rankScenario = { ...scenario, avgUnitsPerMonth: n(l.avgUnitsPerMonth, scenario.avgUnitsPerMonth) };
      const bdResult = computeCostBreakdownWithTiers(effectiveTerms, dbTiersToTierConfigs((l as any).tiers ?? []), rankScenario);
      const bd = bdResult.breakdown;

      // Fee: prefer comparison-stats avgFeePerUnit (server-side, only when unitCount > 0)
      //      → receivables-derived → manual
      const rankRecvFee = deriveReceivablesFeeForScenario(stmts, l, scenario.daysOnFloor);
      const hasCompStatFeeRank = (compStat?.unitCount ?? 0) > 0 && compStat?.avgFeePerUnit != null;
      const rankFee = hasCompStatFeeRank ? compStat!.avgFeePerUnit! : (rankRecvFee !== null ? rankRecvFee : bd.feesTotal);

      return { lender: l, cost: bd.interestTotal + rankFee };
    }).sort((a, b) => a.cost - b.cost);

    const withoutTerms = lenders.filter((l) => !lenderHasTerms(l));
    const cheapest = withTerms.length > 0 ? withTerms[0].cost : null;
    const secondCheapest = withTerms.length > 1 ? withTerms[1].cost : null;
    return {
      rankedLenders: [...withTerms.map((x) => x.lender), ...withoutTerms],
      cheapestCost: cheapest,
      secondCheapestCost: secondCheapest,
    };
  }, [lenders, allStatements, scenario, scenarioCompStats]);

  const openEdit = (lender: Lender) => {
    setPrefilledTerms(undefined);
    setLenderDialogInitTab("terms");
    setEditLender(lender);
    setShowLenderDialog(true);
  };

  const openUpload = (lender: Lender) => {
    setLenderDialogInitTab("upload");
    setEditLender(lender);
    setShowLenderDialog(true);
  };

  const openAddLender = () => {
    setPrefilledTerms(undefined);
    setEditLender(null);
    setShowAddChoiceDialog(true);
  };

  const handleAnalyzeAndSave = (prefilled: Partial<TermsFormState>, _lenderName: string, statementId: string | null) => {
    setPrefilledTerms(prefilled);
    setPendingStatementId(statementId);
    setLenderDialogInitTab("terms");
    setEditLender(null);
    setShowLenderDialog(true);
  };

  if (authUser?.memberPermissions && !authUser.memberPermissions.lenderAccess) {
    return <AccessRestricted feature={t("floorplan:accessFeature")} />;
  }

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-screen-xl">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t("floorplan:page.title")}</h1>
          <p className="text-muted-foreground text-sm mt-0.5">{t("floorplan:page.subtitle")}</p>
        </div>
        <Button onClick={openAddLender} data-testid="button-add-lender">
          <Plus className="w-4 h-4 mr-2" />{t("floorplan:page.addLender")}
        </Button>
      </div>

      {/* Main content */}
      <div className="space-y-4">
        {loadingLenders ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-48 w-full rounded-xl" />)}
          </div>
        ) : lenders.length === 0 ? (
          <div className="py-16 text-center">
            <Building2 className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
            <p className="text-base font-medium text-muted-foreground">{t("floorplan:page.noLendersTitle")}</p>
            <p className="text-sm text-muted-foreground/70 mt-1">{t("floorplan:page.noLendersDesc")}</p>
            <Button className="mt-4" onClick={openAddLender} data-testid="button-add-first-lender">
              <Plus className="w-4 h-4 mr-2" />{t("floorplan:page.addFirstLender")}
            </Button>
          </div>
        ) : (
          <>
            {/* ── Live portfolio summary — persists across all tabs ── */}
            {autoStats.portfolioUnits > 0 && (
              <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-2 px-4 py-3 rounded-lg bg-muted/40 border text-sm" data-testid="portfolio-summary-strip">
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-muted-foreground uppercase tracking-wide font-semibold">{t("floorplan:page.portfolio")}</span>
                </div>
                <div>
                  <span className="text-muted-foreground text-xs">{t("floorplan:page.unitsOnFloor")}</span>
                  <p className="font-semibold leading-tight" data-testid="stat-portfolio-units">{autoStats.portfolioUnits}</p>
                </div>
                {autoStats.avgVehicleValue != null && (
                  <div>
                    <span className="text-muted-foreground text-xs">{t("floorplan:page.avgVehicleValue")}</span>
                    <p className="font-semibold leading-tight" data-testid="stat-avg-vehicle-value">
                      {autoStats.avgVehicleValue.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })}
                    </p>
                  </div>
                )}
                {autoStats.avgTurnDays != null && (
                  <div>
                    <span className="text-muted-foreground text-xs">{t("floorplan:page.avgDaysOnFloor")}</span>
                    <p className="font-semibold leading-tight" data-testid="stat-avg-turn-days">{autoStats.avgTurnDays}d</p>
                  </div>
                )}
              </div>
            )}

            {/* ── Top-level analytics tabs ── */}
            <Tabs defaultValue="floorplans" data-testid="floorplan-analytics-tabs">
              <TabsList className="mb-4" data-testid="tabs-floorplan-list">
                <TabsTrigger value="floorplans" data-testid="tab-trigger-floorplans">{t("floorplan:page.tabs.floorplans")}</TabsTrigger>
                <TabsTrigger value="comparisons" data-testid="tab-trigger-comparisons">{t("floorplan:page.tabs.comparisons")}</TabsTrigger>
                <TabsTrigger value="pricing-change" data-testid="tab-trigger-pricing-change">{t("floorplan:page.tabs.pricingChange")}</TabsTrigger>
              </TabsList>

              {/* ── Floorplans Tab ── */}
              <TabsContent value="floorplans" className="space-y-4">
                {/* Section header */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <CheckCircle2 className="w-4 h-4 text-chart-3" />
                    <h2 className="text-sm font-semibold">{t("floorplan:page.realAnalysisTitle")}</h2>
                    <span className="text-xs text-muted-foreground">{t("floorplan:page.realAnalysisSuffix")}</span>
                  </div>
                  <p className="text-xs text-muted-foreground pl-6">
                    {t("floorplan:page.realAnalysisHint")}
                  </p>
                </div>

                <RealAprSection
                  lenders={lenders}
                  scenario={scenario}
                  onUpload={openUpload}
                  onEdit={openEdit}
                  onDelete={(l) => setDeleteLender(l)}
                  onClickAnalytics={(l) => { setAnalyticsMode("real"); setAnalyticsLender(l); }}
                />

                <Separator />

                {/* ── Lender Comparison Table (cheapest to most expensive) ── */}
                {lenders.filter(lenderHasTerms).length >= 1 && (
                  <LenderComparisonTable lenders={lenders} scenario={scenario} />
                )}
              </TabsContent>

              {/* ── Comparisons Tab ── */}
              <TabsContent value="comparisons" className="space-y-4">
                <Tabs defaultValue="scenario-comparison">
                  <TabsList className="mb-4" data-testid="comparisons-subtabs-list">
                    <TabsTrigger value="scenario-comparison" data-testid="subtab-trigger-scenario-comparison">{t("floorplan:page.subtabs.scenarioComparison")}</TabsTrigger>
                    <TabsTrigger value="head-to-head" data-testid="subtab-trigger-head-to-head">{t("floorplan:page.subtabs.headToHead")}</TabsTrigger>
                  </TabsList>

                  {/* Sub-tab: Scenario Comparison */}
                  <TabsContent value="scenario-comparison">
                    <div id="scenario-compare">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="flex items-center justify-center w-7 h-7 rounded-md bg-primary/10">
                          <Calculator className="w-4 h-4 text-primary" />
                        </div>
                        <div>
                          <h2 className="text-base font-semibold leading-tight">{t("floorplan:page.scenarioComparisonTitle")}</h2>
                          <p className="text-xs text-muted-foreground">{t("floorplan:page.scenarioComparisonDesc")}</p>
                        </div>
                      </div>

                      {/* Scenario inputs */}
                      <div className="flex items-center gap-5 flex-wrap p-3 rounded-lg bg-muted/40 border">
                        <div className="flex items-center gap-2">
                          <Calculator className="w-3.5 h-3.5 text-primary shrink-0" />
                          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t("floorplan:page.inputs")}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Label className="text-xs whitespace-nowrap">{t("floorplan:page.vehicleValue")}</Label>
                          <div className="relative">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">$</span>
                            <Input
                              type="text"
                              inputMode="numeric"
                              value={vehicleValueStr}
                              onChange={(e) => {
                                const raw = e.target.value.replace(/[^0-9]/g, "");
                                setVehicleValueStr(raw === "" ? "" : parseInt(raw, 10).toLocaleString("en-US"));
                                const num = raw === "" ? 0 : parseInt(raw, 10);
                                if (!isNaN(num)) setScenario((s) => ({ ...s, vehicleValue: num }));
                              }}
                              onBlur={() => {
                                if (vehicleValueStr === "") setVehicleValueStr("0");
                              }}
                              className="pl-5 h-7 w-28 text-xs"
                              data-testid="input-scenario-value"
                            />
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Label className="text-xs whitespace-nowrap">{t("floorplan:page.daysOnFloor")}</Label>
                          <div className="relative">
                            <Input
                              type="text"
                              inputMode="numeric"
                              value={daysOnFloorStr}
                              onChange={(e) => {
                                const raw = e.target.value.replace(/[^0-9.]/g, "");
                                setDaysOnFloorStr(raw);
                                const num = raw === "" ? 0 : parseFloat(raw);
                                if (!isNaN(num)) setScenario((s) => ({ ...s, daysOnFloor: num }));
                              }}
                              onBlur={() => {
                                if (daysOnFloorStr === "") setDaysOnFloorStr("0");
                              }}
                              className="pr-8 h-7 w-24 text-xs"
                              data-testid="input-scenario-days"
                            />
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">{t("floorplan:page.days")}</span>
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground ml-auto hidden md:block">
                          {autoStats.method === "receivables"
                            ? t("floorplan:page.defaultsReceivables")
                            : t("floorplan:page.defaultsProjected")}
                        </p>
                      </div>

                      {/* Lender projection cards */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                        {rankedLenders.map((lender, i) => (
                          <LenderCard
                            key={lender.id}
                            lender={lender}
                            scenario={scenario}
                            rank={i + 1}
                            isFirst={i === 0 && lenderHasTerms(lender)}
                            isLast={i === rankedLenders.filter(lenderHasTerms).length - 1 && lenderHasTerms(lender) && rankedLenders.filter(lenderHasTerms).length > 1}
                            onEdit={() => openEdit(lender)}
                            onDelete={() => setDeleteLender(lender)}
                            onClickAnalytics={() => { setAnalyticsMode("scenario"); setAnalyticsLender(lender); }}
                            allStatements={allStatements}
                            cheapestCost={cheapestCost}
                            secondCheapestCost={secondCheapestCost}
                            allLenders={lenders}
                            comparisonStats={scenarioCompStats}
                          />
                        ))}

                        {/* Add scenario lender card */}
                        <button
                          onClick={openAddLender}
                          className="border-2 border-dashed border-border rounded-xl p-4 text-center hover:border-primary/40 hover:bg-muted/20 transition-all group"
                          data-testid="button-add-lender-card"
                        >
                          <Plus className="w-4 h-4 text-muted-foreground/40 group-hover:text-primary mx-auto mb-1.5 transition-colors" />
                          <p className="text-xs text-muted-foreground group-hover:text-foreground transition-colors font-medium">{t("floorplan:page.addScenarioLender")}</p>
                        </button>
                      </div>
                    </div>
                  </TabsContent>

                  {/* Sub-tab: Head to Head Comparison */}
                  <TabsContent value="head-to-head">
                    <div id="head-to-head-comparison">
                      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center justify-center w-7 h-7 rounded-md bg-primary/10">
                            <ArrowLeftRight className="w-4 h-4 text-primary" />
                          </div>
                          <div>
                            <h2 className="text-base font-semibold leading-tight">{t("floorplan:page.headToHeadTitle")}</h2>
                            <p className="text-xs text-muted-foreground">{t("floorplan:page.headToHeadDesc")}</p>
                          </div>
                        </div>
                      </div>
                      <LenderCostComparison lenders={lenders} allStatements={allStatements} onAddLender={openAddLender} />
                    </div>
                  </TabsContent>
                </Tabs>
              </TabsContent>

              {/* ── Pricing Change Tab ── */}
              <TabsContent value="pricing-change" className="space-y-4">
                <div id="what-if-pricing">
                  <WhatIfPricingSection lenders={lenders} allStatements={allStatements} scenario={scenario} />
                </div>
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>

      {/* Dialogs */}
      <AddLenderChoiceDialog
        open={showAddChoiceDialog}
        onOpenChange={(v) => {
          setShowAddChoiceDialog(v);
          if (!v) {
            setAddLenderInitialResult(undefined);
            setAddLenderScanError(null);
            // Only clear scanner state on terminal outcomes (completed re-open or
            // failed). When the dialog closes during an active scan (minimize),
            // the scanner must keep running so the floating pill stays visible.
            if (scanner.status === "completed" || scanner.status === "failed") {
              scanner.clear();
            }
          }
        }}
        onChooseManual={() => {
          setShowAddChoiceDialog(false);
          setPrefilledTerms(undefined);
          setEditLender(null);
          setShowLenderDialog(true);
        }}
        onAnalyzeAndSave={handleAnalyzeAndSave}
        initialResult={addLenderInitialResult}
        initialError={addLenderScanError}
      />

      <LenderDialog
        open={showLenderDialog}
        onOpenChange={(v) => {
          setShowLenderDialog(v);
          if (!v) {
            setEditLender(null);
            setPrefilledTerms(undefined);
            // pendingStatementLinkedRef is set synchronously by onStatementLinked
            // before this callback runs, so it reliably distinguishes save vs cancel.
            if (pendingStatementId && !pendingStatementLinkedRef.current) {
              // Cancel path: statement was uploaded but lender was never saved —
              // delete the orphaned record so it doesn't accumulate in the database.
              apiRequest("DELETE", `/api/parsed-statements/${pendingStatementId}`).catch(() => {});
            }
            pendingStatementLinkedRef.current = false;
            setPendingStatementId(null);
          }
        }}
        editLender={editLender}
        prefilledTerms={prefilledTerms}
        pendingStatementId={pendingStatementId}
        allStatements={allStatements}
        defaultTab={lenderDialogInitTab}
        onStatementLinked={() => {
          // Mark linked synchronously via ref so the onOpenChange closure sees it
          // immediately — React state updates are async and would arrive too late.
          pendingStatementLinkedRef.current = true;
          setPendingStatementId(null);
        }}
      />

      <DeleteLenderDialog
        lender={deleteLender}
        onClose={() => setDeleteLender(null)}
        onConfirm={() => deleteLender && deleteLenderMutation.mutate(deleteLender.id)}
        isPending={deleteLenderMutation.isPending}
      />

      <LenderAnalyticsDialog
        lender={analyticsLender}
        scenario={scenario}
        mode={analyticsMode}
        onClose={() => setAnalyticsLender(null)}
        onEdit={() => {
          const l = analyticsLender!;
          setAnalyticsLender(null);
          openEdit(l);
        }}
        onUpload={() => {
          const l = analyticsLender!;
          setAnalyticsLender(null);
          openUpload(l);
        }}
      />

      <StatementReader
        open={scannerOpen}
        onOpenChange={setScannerOpen}
        onMinimize={() => setScannerOpen(false)}
      />
    </div>
  );
}
